'use strict';

// external imports
import React, {Component} from 'react';
import {
    bindActionCreators
} from 'redux';
import {connect} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import _, {
    DebouncedFunc
} from 'lodash';
import {AxiosResponse} from 'axios';
import parse from 'html-react-parser';
import omniclone from 'omniclone';

// prime react imports
import {Card} from 'primereact/card';
import {ConfirmDialog} from 'primereact/confirmdialog';
import {
    ConfirmPopup,
    confirmPopup
} from 'primereact/confirmpopup';
import {
    TabPanel,
    TabView,
    TabViewTabChangeEvent
} from 'primereact/tabview';

// shared imports
import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ItemType from '~/shared/src/mm/ItemType';
import PropertySet, {
    isPropertyValueType,
    PropertyValueType
} from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import Item, {
    ItemAttributesForCreation,
    ItemAttributesSettable,
    ItemOnlyAttributesSettable,
    itemOnlyAttributesSettable
} from '~/shared/src/ci/Item';
import Property, {
    propertyAttributesSettable
} from '~/shared/src/ci/Property';
import EnumProperty from '~/shared/src/ci/EnumProperty';
import Reference, {
    isReferenceAttribute,
    ReferenceAttributesForCreation,
    ReferenceAttributesSettable,
    referenceOnlyAttributesSettable
} from '~/shared/src/ci/Reference';
import ReferenceProperty, {
    referencePropertyAttributesSettable
} from '~/shared/src/ci/ReferenceProperty';
import EnumReferenceProperty from '~/shared/src/ci/EnumReferenceProperty';
import LocalizableError from '~/shared/src/base/LocalizableError';
import StackError from '~/shared/src/base/StackError';
import User from '~/shared/src/model/User';
import {
    DataObjects,
    getResolvedConfigs
} from '~/shared/src/utilities/action';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult,
    CheckReferenceInsertableParameters,
    CheckReferenceInsertableResult,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataDescriptors,
    DataObjectProperties,
    DataStructure,
    extractDataObjectsFromDataStructure,
    getDataObjectByOrigId,
    getDataStructure,
    getParentDataObject,
    ObjectDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import dataUtils, {
    EnumUpdateValue,
    EnumUpdateValueAttributes,
    EnumValue,
    ItemData,
    ReferenceData,
    ReferenceValue,
    ObjectData,
    ResolvedDataObjectChain
} from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    CommonProperties,
    ContainerCommonInheritedProperties,
    ContainerLayoutStructure,
    ContainerProperties,
    ContainerToContainerInheritedProperties,
    FormFieldData,
    FormFieldInheritedProperties,
    FormFieldLayoutStructureAndDataBinding,
    FormFieldProperties,
    FormInheritedProperties,
    FormLayoutStructure,
    FormProperties,
    LayoutElements,
    LoadConfigResult,
    LoadContainerData,
    LoadDataResult,
    LoadFormData,
    LoadReferenceFieldItemsParameters,
    LoadTabContainerData,
    ReferenceFieldProperties,
    RequestConfig,
    StaticContentFieldProperties,
    TabContainerLayoutStructure,
    TabContainerProperties,
    VariableFieldProperties,
    WidthUnit,
    containerInheritedProperties,
    fieldTypeToItemTypeMapping,
    formFieldInheritedProperties,
    formInheritedProperties,
    useHookForm,
    widthUnitMapping
} from '~/shared/src/utilities/modules/form';
import {getIconCssClassName} from '~/shared/src/utilities/getCssClassName';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import PropertyResolver from '~/shared/src/utilities/PropertyResolver';
import ReferencePropertyResolver from '~/shared/src/utilities/ReferencePropertyResolver';
import messageUtils from '~/shared/src/utilities/messageUtils';
import {Action} from '~/shared/src/utilities/action';
import {Message} from '~/shared/src/utilities/message';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';
import {formatValue} from '~/shared/src/utilities/valueFormaters';
import {validateValueForType} from '~/shared/src/utilities/valueValidators';

// frontend imports
import BooleanField from '~/frontend/src/components/base/shared/field/valueTypeFields/BooleanField';
import ButtonField from '~/frontend/src/components/base/shared/field/ButtonField';
import CalendarView, {
    ReferenceType as CalendarViewReferenceType
} from '~/frontend/src/components/base/shared/view/CalendarView';
import ChartView from '~/frontend/src/components/base/shared/view/ChartView';
import ColorField from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';
import CoordinateField from '~/frontend/src/components/base/shared/field/valueTypeFields/CoordinateField';
import CurrencyField from '~/frontend/src/components/base/shared/field/valueTypeFields/CurrencyField';
import DateField from '~/frontend/src/components/base/shared/field/valueTypeFields/DateField';
import DoubleField from '~/frontend/src/components/base/shared/field/valueTypeFields/DoubleField';
import DurationField from '~/frontend/src/components/base/shared/field/valueTypeFields/DurationField';
import EmailField from '~/frontend/src/components/base/shared/field/valueTypeFields/EmailField';
import GraphView from '~/frontend/src/components/base/shared/view/GraphView';
import IbanField from '~/frontend/src/components/base/shared/field/valueTypeFields/IbanField';
import ImageDataField from '~/frontend/src/components/base/shared/field/valueTypeFields/ImageDataField';
import IntegerField from '~/frontend/src/components/base/shared/field/valueTypeFields/IntegerField';
import JsonField from '~/frontend/src/components/base/shared/field/valueTypeFields/JsonField';
import LinkField from '~/frontend/src/components/base/shared/field/valueTypeFields/LinkField';
import MapView from '~/frontend/src/components/base/shared/view/MapView';
import OverviewView, {
    ReferenceType as OverviewViewReferenceType
} from '~/frontend/src/components/base/shared/view/OverviewView';
import PasswordField from '~/frontend/src/components/base/shared/field/PasswordField';
import PhoneField from '~/frontend/src/components/base/shared/field/valueTypeFields/PhoneField';
import StaticContentField, {
    ComponentProps as StaticContentFieldComponentProps,
    GetDataConfig as StaticContentFieldGetDataConfig
} from '~/frontend/src/components/base/shared/field/StaticContentField';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import TextField from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import TimeField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimeField';
import TimestampField from '~/frontend/src/components/base/shared/field/valueTypeFields/TimestampField';
import ValueSetField, {
    componentProps as valueSetFieldComponentProps,
    ComponentProps as ValueSetFieldComponentProps
} from '~/frontend/src/components/base/shared/field/ValueSetField';
import EnumField, {
    componentProps as enumFieldComponentProps,
    ComponentProps as EnumFieldComponentProps,
    EnumValueErrors
} from '~/frontend/src/components/base/shared/field/EnumField';
import ReferenceField, {
    componentProps as referenceFieldComponentProps,
    ComponentProps as ReferenceFieldComponentProps,
    GetDataConfig as ReferenceFieldGetDataConfig
} from '~/frontend/src/components/base/shared/field/ReferenceField';
import FileField, {
    componentProps as fileFieldComponentProps,
    ComponentProps as FileFieldComponentProps
} from '~/frontend/src/components/base/shared/field/FileField';

import {
    store,
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    getPanelElusiveData,
    getPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    getItemKeyDisplayName,
    getItemNameDisplayName,
    getItemTypeDisplayName,
    getPropertyKeyDisplayName,
    getPropertySetDisplayName,
    getPropertyValueDisplayName,
    getReferencePropertyKeyDisplayName,
    getReferencePropertySetDisplayName,
    getReferencePropertyValueDisplayName,
    getStatusColorStyleObject,
    nl2br
} from '~/frontend/src/utilities/renderHelpers';
import {
    formatRenderer,
    isInvalidHTML,
    renderValue,
    resolveStyle,
    resolveStyleToClassNames
} from '~/frontend/src/utilities/valueRenderers';
import {fieldUtils} from '~/frontend/src/utilities/fieldUtils';
import {
    checkCardinalityFeOnly,
    checkKeyUniqueFeOnly,
    getModifiedData,
    getModifiedDataRecursive,
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import ActionMixin, {
    ActionMixinInterface,
    ClonedObjectCache,
    ClonedObjectsIdMappings,
    MappedResolvedDataObjects,
    ResolvedDataObject,
    ResolvedDataObjectState,
    ResolvedDataObjects,
    ResolvedLayoutElements
} from '~/frontend/src/utilities/ActionMixin';

// action imports
import openPanel, {OpenPanel} from '~/frontend/src/actions/panelManager/openPanel';
import openPanelForItemType, {OpenPanelForItemType} from '~/frontend/src/actions/panelManager/openPanelForItemType';
import setParamOrig, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import setParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import dataLoadData from '~/frontend/src/actions/data/loadData';
import {LoadData as DataLoadData} from '~/frontend/src/actions/data/loadData';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import deleteItem, {DeleteItem} from '~/frontend/src/actions/data/deleteItem';
import resetItem, {ResetItem} from '~/frontend/src/actions/data/resetItem';
import updateItem, {UpdateItem} from '~/frontend/src/actions/data/updateItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import deleteReference, {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import updateReference, {UpdateReference} from '~/frontend/src/actions/data/updateReference';
import loadData, {LoadData} from '~/frontend/src/actions/panel/form/loadData';
import saveData, {SaveData} from '~/frontend/src/actions/panel/form/saveData';
import {
    SetElusiveDataAction,
    UnsetElusiveDataAction,
    setElusiveData,
    unsetElusiveData
} from '~/frontend/src/actions/elusiveData';
import {
    SetMessage,
    UnsetMessage,
    setMessage,
    unsetMessage,
    MsgParams
} from '~/frontend/src/actions/intercom';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import startRequest, {StartRequest} from '~/frontend/src/actions/ajax/startRequest';
import endRequest, {EndRequest} from '~/frontend/src/actions/ajax/endRequest';


const debugColorScheme = {
    error                             : 'F44336',
    shouldComponentUpdate             : 'AB47BC',
    shouldComponentUpdateAsync        : '6A1B9A',
    shouldComponentUpdateDeferred     : '757575',
    shouldComponentUpdateBuildFunction: '607D8B',
    init                              : '0097A7',
    loadData                          : '009688',
    onIntermediateValueChange         : 'FFA726',
    onValueChange                     : 'FF9800',
    onIntermediateValueInvalid        : 'FF7043',
    onValueInvalid                    : 'FF5722',
    onIntermediateValueValid          : '9CCC65',
    onValueValid                      : '8BC34A',
    onTriggerAction                   : '4CAF50',
    onReferenceAction                 : '4CAF50',
    onGetDataAction                   : '4CAF50',
    onScheduledAction                 : '4CAF50',
    setValueForLayoutElement          : 'F57C00',
    render                            : '1565C0',
    validator                         : '8BC34A'
};


type Values = {
    [ objectId : number ] : AttributeValue
};
type ItemValues = {
    [ itemId : number ] : AttributeValue
};
type PropertyObject = {
    [ itemId : number ] : number[];
};
type PropertyValue = {
    [ propertyTypeId : number ] : AttributeValue | undefined;
};
type PropertyValues = {
    [ itemId : number ] : PropertyValue;
};
type EnumPropertyPropertyObject = {
    [ propertyTypeId : number ] : AttributeValue[];
};
type EnumPropertyObject = {
    [ itemId : number ] : EnumPropertyPropertyObject
};
type EnumPropertyValue = {
    [ propertyTypeId : number ] : EnumValue | undefined
};
type EnumPropertyValues = {
    [ itemId : number ] : EnumPropertyValue
};
type ReferenceValues = {
    [ referenceId : number ] : ReferenceValue | ReferenceValue[] | undefined
};
type ReferencePropertyObject = {
    [ referenceId : number ] : number[];
};
type ReferencePropertyValue = {
    [ propertyTypeId : number ] : AttributeValue | undefined;
};
type ReferencePropertyValues = {
    [ referenceId : number ] : ReferencePropertyValue;
};
type EnumReferencePropertyPropertyObject = {
    [ propertyTypeId : number ] : AttributeValue[];
};
type EnumReferencePropertyObject = {
    [ referenceId : number ] : EnumReferencePropertyPropertyObject;
};
type EnumReferencePropertyValue = {
    [ propertyTypeId : number ] : EnumValue | undefined
};
type EnumReferencePropertyValues = {
    [ referenceId : number ] : EnumReferencePropertyValue
};

type ItemDataAndMetaModelMapping = {
    type : 'item',
    metaModels : number[];
    objects? : number[];
    values? : ItemValues;
    originalValues? : ItemValues;
    isDefault : never;
    isSettable : boolean;
    hasNoParent : never;
};
type PropertyDataAndMetaModelMapping = {
    type : 'property',
    metaModels : number[];
    objects? : PropertyObject;
    values? : PropertyValues;
    originalValues? : PropertyValues;
    isDefault : boolean;
    isSettable : boolean;
    hasNoParent : boolean;
};
type EnumPropertyDataAndMetaModelMapping = {
    type : 'enumProperty',
    metaModels : number[];
    objects? : EnumPropertyObject;
    values? : EnumPropertyValues;
    originalValues? : EnumPropertyValues;
    isDefault : never;
    isSettable : boolean;
    hasNoParent : boolean;
};
type ReferenceDataAndMetaModelMapping = {
    type : 'reference',
    metaModels : number[];
    objects? : number[];
    values? : ReferenceValues;
    originalValues? : ReferenceValues;
    isDefault : never;
    isSettable : boolean;
    hasNoParent : never;
};
type ReferencePropertyDataAndMetaModelMapping = {
    type : 'referenceProperty',
    metaModels : number[];
    objects? : ReferencePropertyObject;
    values? : ReferencePropertyValues;
    originalValues? : ReferencePropertyValues;
    isDefault : boolean;
    isSettable : boolean;
    hasNoParent : boolean;
};
type ReferenceEnumPropertyDataAndMetaModelMapping = {
    type : 'enumReferenceProperty',
    metaModels : number[];
    objects? : EnumReferencePropertyObject;
    values? : EnumReferencePropertyValues;
    originalValues? : EnumReferencePropertyValues;
    isDefault : never;
    isSettable : boolean;
    hasNoParent : boolean;
};
type StaticDataAndMetaModelMapping = {
    type : 'static',
    metaModels : number[];
    objects? : never;
    values? : {
        [ objectId : number ] : any
    };
    originalValues? : never;
    isDefault : never;
    isSettable : false;
    hasNoParent : never;
};
type ObjectDataAndMetaModelMapping =
    ItemDataAndMetaModelMapping
    | PropertyDataAndMetaModelMapping
    | EnumPropertyDataAndMetaModelMapping
    | ReferenceDataAndMetaModelMapping
    | ReferencePropertyDataAndMetaModelMapping
    | ReferenceEnumPropertyDataAndMetaModelMapping;
type DataAndMetaModelMapping = ObjectDataAndMetaModelMapping | StaticDataAndMetaModelMapping;

type DataDescriptorToDataAndMetaModelMapping = {
    [ dataDescriptorAlias : string ] : DataAndMetaModelMapping;
};
type DataDescriptorToValueMapping = {
    [ dataDescriptorAlias : string ] : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined;
};
type DataDescriptorItemKeyCheckData = {
    itemId : number;
    itemTypeId : number;
    originalKey : string;
    lastCheckedKey : string;
    isKeyUnique? : boolean;
};
type DataDescriptorToItemKeyCheckMapping = {
    [ dataDescriptorAlias : string ] : DataDescriptorItemKeyCheckData;
};
type DataDescriptorToReferenceInsertableCheckData = CheckReferenceInsertableParameters
    & {
    isReferenceInsertable? : boolean;
    errorMessage? : string;
    errorPlaceholders? : Placeholders;
};
type DataDescriptorToReferenceInsertableCheckMapping = {
    [ dataDescriptorAlias : string ] : DataDescriptorToReferenceInsertableCheckData;
};
type EditValue = AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined;
type DataDescriptorMappings = {
    dataAndMetaModel? : DataDescriptorToDataAndMetaModelMapping;
    value : DataDescriptorToValueMapping;
    originalValue : DataDescriptorToValueMapping;
    editValue? : {
        [ dataDescriptorAlias : string ] : EditValue;
    };
    itemKeyCheck? : DataDescriptorToItemKeyCheckMapping;
    referenceInsertableCheck? : DataDescriptorToReferenceInsertableCheckMapping;
};

type LayoutElementToErrorsMapping = {
    [ layoutElementId : number | string ] : string[];
};
type LayoutElementToCacheMapping = {
    [ layoutElementId : number | string ] : any;
};
type LayoutElementToPropsByFunctionsMapping = {
    [ layoutElementId : number ] : Omit<ContainerProperties, keyof CommonProperties> | Omit<FormFieldProperties, keyof CommonProperties>;
};
type LayoutElementMappings = {
    setValueErrors? : LayoutElementToErrorsMapping;
    errors? : LayoutElementToErrorsMapping;
    cache? : LayoutElementToCacheMapping;
    propsByFunctions? : LayoutElementToPropsByFunctionsMapping;
};

type FocusedElement = {
    id : string;
    path : string[];
    selectionStart? : number;
    selectionEnd? : number;
};

export type Variables = {
    [ variable : string ] : any;
};

export type FunctionParameters = {
    [ name : string ] : any;
};

type Parent = {
    id : number;
    type : 'form' | 'layoutWrapper' | 'card' | 'container' | 'tabContainer';
    gridSize : number;
    dataDescriptorAlias? : string;
    referenceId? : number;
    itemId? : number;
    parentItemId? : number;
    isRepeating? : boolean;
    index? : number;
    hasNoParent? : boolean;
    idSuffix? : string;
};

type ReferenceSortMapping = {
    reference : Reference;
    item : Item;
    sortLabel : string;
};

type OrderBy = {
    key : string;
    direction : 'asc' | 'desc';
};

type GetContainerContent = {
    isDirty : boolean;
    isInvalid : boolean;
    content : React.ReactNode[];
    tabIndexMapping? : {
        [ referenceId : number ] : number;
    };
};

type AdditionalParams = {
    [ parameterKey : string ] : any;
};
type Params = {
    [ parameterKey : string ] : number | string;
};
type RequestParams = BaseRequestParameters & {
    documentKey? : string;
    mailKey? : string;
    rootItemId? : number;
    rootItemKey? : string;
    rootItemType? : string;
    parameters? : Params
};
const requestParamKeys = keys<RequestParams>() as string[];

type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    dataDescriptorMappings? : DataDescriptorMappings;
    layoutElementMappings? : LayoutElementMappings;
    variables? : Variables;
    variablesEditValues? : Variables;
    treeVariables? : Variables;
    lastSaveTimestamp? : number;
    lastSaveRequestParameters? : string;
    viewDataContextsToSave? : string[];
};

type SetParam = <K extends keyof ( ComponentProps & {
    rootItemId? : number;
    rootItemKey? : string;
    rootItemType? : string;
} )>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<( Omit<ComponentProps, 'rootItemId' | 'rootItemKey' | 'rootItemType'> & {
    rootItemId? : number;
    rootItemKey? : string;
    rootItemType? : string;
} )> ) => SetParamsAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;
type UnsetElusiveData = ( panelKey : string, keys : ( keyof PanelElusiveData )[] ) => UnsetElusiveDataAction;

const setParam = setParamOrig as SetParam;

export type DefaultProps = BaseRequestParameters & {

    /**
     * The structure of the form data.
     */
    dataStructure? : DataStructure;

    /**
     * The unique dataDescriptors used within the form.
     */
    dataDescriptors? : DataDescriptors;

    /**
     * The structure of the form layout data.
     */
    layoutStructure? : FormLayoutStructure;

    /**
     * The unique layoutelements used within the form.
     */
    layoutElements? : LayoutElements;

    /**
     * Is this form standalone.
     */
    mode? : 'embedded' | 'popup' | 'standalone' | 'standalonePopup';

    scrollTop? : number;
    scrollLeft? : number;

    activeTabContainerIndexes? : {
        [ layoutElementId : number | string ] : number;
    };

    tabContainerScrollPositions? : {
        [ layoutElementId : number | string ] : number;
    };

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    /**
     * Additional context to be checked against (key unique, reference cardinality)
     */
    additionalCheckDataContexts? : string[];

    tabIndexOffset? : number;

    readOnly? : boolean;

    saveConfig? : RequestConfig;
};
export const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    dataStructure                : {} as DataStructure,
    dataDescriptors              : {},
    layoutStructure              : {} as FormLayoutStructure,
    layoutElements               : {},
    mode                         : 'standalone',
    scrollTop                    : 0,
    scrollLeft                   : 0,
    activeTabContainerIndexes    : {},
    tabContainerScrollPositions  : {},
    additionalElusiveDataContexts: [],
    additionalCheckDataContexts  : [],
    tabIndexOffset               : 1000,
    readOnly                     : false,
    saveConfig                   : {
        requestCommand: 'save',
        requestModule : 'data'
    } as RequestConfig
};

type CommonProps = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * identifier to access global.data with.
     */
    dataContext : string;

    /**
     * The key of the overview config.
     */
    viewKey : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    /**
     * The itemId of the parent form.
     */
    formParentItemId? : number;

    /**
     * The itemId of the parent in a tree.
     * Only valid if running in a tree context.
     */
    treeParentItemId? : number;

    /**
     * The itemId of the root of a tree.
     * Only valid if running in a tree context.
     */
    treeRootItemId? : number;

    /**
     * Reload function for the tree
     */
    treeReloadData? : () => void;

    /**
     * The tree variables
     */
    treeVariables? : Variables;

    /**
     * Getter for the tree variables
     */
    treeSetVariables? : ( variables : Variables ) => void;

    /**
     * Save data function for the tree.
     */
    treeDoSaveData? : () => Promise<SaveDataResult | false>;

    exportConfig? : RequestConfig;

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadConfigResult} config
     */
    onConfigLoad? : ( config : LoadConfigResult, reloadConfig : boolean ) => void;

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadDataResult} config
     */
    onDataLoad? : ( config : LoadDataResult ) => void;

    /**
     * Function invoked if form should render a new item (rootItemID < 0) and no global.data therefor existing.
     */
    onInvalidData? : () => void;

    /**
     * Function invoked if form errorData changed.
     */
    onErrorDataChanged? : ( uid : string, errorData : ErrorData | undefined ) => void;

    /**
     * Function invoked if form isDirty changed.
     */
    onDirtyChanged? : ( uid : string, dirty : boolean ) => void;

    focusedElement? : FocusedElement;

    persistentSubPanelProps? : {
        [ subPanelKey : string ] : any;
    };

    /**
     * The transformer for the module (component) state.
     * NOTE: Have to be set (in opposition to be a default) to be included in the state anyway!
     */
    moduleTransformer? : string;
};
export type Props = CommonProps & (
    {

        /**
         * The id of the root item. No further root item information required.
         */
        rootItemId : number;

        rootItemKey? : never;
        rootItemType? : never;
    } |
    {

        /**
         * The key of the root item. If set also rootItemType is required.
         */
        rootItemKey : string;

        /**
         * The ItemType of the root item. If set also rootItemKey is required.
         */
        rootItemType : string;

        rootItemId? : never;
    }
    );
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    messageCatalogTimestamp? : number;

    metaModelTimestamp? : number;

    debug : boolean;
};

type ActionPropsRaw = {

    // panel manager actions
    // DO NOT REMOVE - not necessarily used in this file BUT needed by ActionMixin (ActionMixinInterface)
    openPanel : OpenPanel;
    openPanelForItemType : OpenPanelForItemType;

    // common panel actions
    setParam : SetParam;
    setParams : SetParams;

    // data actions
    dataLoadData : DataLoadData;
    addItem : AddItem;
    deleteItem : DeleteItem;
    resetItem : ResetItem;
    updateItem : UpdateItem;
    addReference : AddReference;
    updateReference : UpdateReference;
    deleteReference : DeleteReference;

    // form actions
    loadData : LoadData;
    saveData : SaveData;

    // elusiveData actions
    setElusiveData : SetElusiveData;
    unsetElusiveData : UnsetElusiveData;

    // intercom actions
    setMessage : SetMessage;
    unsetMessage : UnsetMessage;

    // xhr action for masking
    startRequest : StartRequest;
    endRequest : EndRequest;
};
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

const Actions = Object.assign( {}, { openPanel, openPanelForItemType, setParam, setParams, dataLoadData, addItem, deleteItem, resetItem, updateItem, addReference, updateReference, deleteReference, loadData, saveData, setElusiveData, unsetElusiveData, setMessage, unsetMessage, endRequest, startRequest } ) as any;

export type AllProps = Required<DefaultProps> & Omit<Props, 'rootItemId' | 'rootItemKey' | 'rootItemType'> & {
    rootItemId? : number;
    rootItemKey? : string;
    rootItemType? : string;
} & StateProps & ActionProps;


type State = {
    deferredRenderEndTrigger : number;
    dynamicFunctionErrors? : string[];
    isViewDirty : boolean;
    justLoaded : boolean;
    formGridUnitWidth : number;
    remWidth : number;
};


// define interface for Form component to apply methods from mixin
export interface Form extends Component<AllProps, State>, ActionMixinInterface {
}


/**
 * Form
 */
export class Form extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    static async doSaveData( uid : string, dataContext : string, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              elusiveData                                        = getPanelElusiveData( uid ),
              {
                  schema,
                  rootItemId,
                  saveConfig,
                  activeTabContainerIndexes
              }                                                  = propsWithDefaults,
              newActiveTabContainerIndexes                       = { ...activeTabContainerIndexes },
              processedRequestParams : Params                    = Form.resolveRequestParameters( saveConfig.requestParams, schema, dataContext, rootItemId!, elusiveData?.variables );

        const result = await ( store.dispatch as AppDispatch )( saveData(
            uid,
            dataContext,
            schema,
            rootItemId!,
            saveConfig.requestModule ?? defaultProps.saveConfig.requestModule,
            saveConfig.requestCommand ?? defaultProps.saveConfig.requestCommand,
            processedRequestParams,
            elusiveData?.viewDataContextsToSave,
            successMsgParams
        ) );

        // remap activeTabContainerIndexes
        if ( result && result.success && result.data.referenceMetaDataMapping && Object.keys( result.data.referenceMetaDataMapping ).length ) {
            let remapped : boolean = false;

            for ( const realTabContainerLayoutElementId of Object.keys( newActiveTabContainerIndexes ) ) {
                if ( newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] < 0 && result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ] ) {
                    remapped = true;
                    newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] = result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ].id;
                }
            }

            if ( remapped ) {
                ( store.dispatch as AppDispatch )( setParam(
                    uid,
                    'activeTabContainerIndexes',
                    newActiveTabContainerIndexes
                ) );
            }
        }

        return result;
    }

    /**
     * Resolves and processes request parameters based on the provided schema, data context, and other optional parameters.
     *
     * @param {Object<string, string> | undefined} requestParameters - An object representing the request parameters, where keys are parameter names and values are corresponding values.
     * @param {Schema} schema - The schema used for resolving parameters.
     * @param {string} dataContext - The data context used in the resolution process.
     * @param {number} rootItemId - The root item ID to be considered during resolution.
     * @param {Variables} [variables] - Optional variables that may assist in parameter resolution.
     * @param {Object<string, any>} [additionalResolverParameters] - Additional parameters to provide context during the resolution process.
     * @return {Object<string, string>} An object containing the resolved request parameters, where keys are parameter names and values are the processed values.
     */
    static resolveRequestParameters( requestParameters : Params | undefined, schema : Schema, dataContext : string, rootItemId : number, variables? : Variables, additionalResolverParameters? : AdditionalParams ) : Params {
        const retVal : Params = {};

        if ( requestParameters ) {
            const requestParamsKeys             = Object.keys( requestParameters ?? {} ),
                  messageTemplateStringResolver = Form.getMessageTemplateStringResolver(
                      dataContext,
                      rootItemId,
                      variables ?? {},
                      {
                          isDataModified: isDataModifiedRecursive( dataContext, schema ?? defaultBaseRequestParameters.schema, rootItemId ),
                          ...additionalResolverParameters,
                          requestParameters: {
                              ...global.landingPageRequestParameters
                          }
                      }
                  );

            for ( const key of requestParamsKeys ) {
                const requestParam          = requestParameters[ key ],
                      processedRequestParam = typeof requestParam === 'string'
                          ? messageTemplateStringResolver( requestParam )
                          : requestParam;

                if ( processedRequestParam !== 'undefined' ) {
                    retVal[ key ] = processedRequestParam;
                }
            }
        }

        return retVal;
    }

    /**
     * Creates a message template string resolver function based on the specified data context, root item, and variables.
     *
     * @param {string} dataContext - The context name for the data source.
     * @param {number} rootItemId - The identifier of the root item in the data source.
     * @param {Variables} variables - The variables to be used within the template resolution process.
     * @param {{[name: string]: any}} [additionalResolverParameters] - Optional additional parameters for the resolver.
     * @return {(templateString: string, rawMode?: boolean) => string} A function that resolves the provided template string into a processed string based on the specified data context, root item, and variables.
     */
    static getMessageTemplateStringResolver( dataContext : string, rootItemId : number, variables : Variables, additionalResolverParameters? : {
        [ name : string ] : any
    } ) : ( templateString : string, rawMode? : boolean ) => string {
        const item = global.data[ dataContext ]?.items[ rootItemId ];

        // TODO: add default for treeParentObject and treeRootObject same as rootObject
        // TODO: add resolver for all dataObject items and references if needed (see getResolvedDataObjects - key => label)

        let retVal : ( templateString : string, rawMode? : boolean ) => string;

        if ( item ) {
            retVal = messageUtils.getMessageTemplateStringResolverForResolvedDataObjects(
                item,
                undefined,
                {
                    rootObject: {
                        item
                    }
                },
                {
                    variables: {
                        ...variables,
                        [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                            if ( hint === 'string' ) {
                                return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                            }
                            else {
                                return null;
                            }
                        }
                    },
                    ...additionalResolverParameters
                }
            );
        }
        else {
            retVal = messageUtils.getMessageTemplateStringResolverForData(
                {},
                {
                    variables: {
                        ...variables,
                        [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                            if ( hint === 'string' ) {
                                return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                            }
                            else {
                                return null;
                            }
                        }
                    },
                    ...additionalResolverParameters
                }
            );
        }

        return retVal;
    }

    /* properties *************************************************************************************************************************/

    debug : boolean = false;
    renderStartTime : number = 0;

    /**
     * Represents an optional field configuration for auto-focusing within a form or layout.
     *
     * @property {number} tabIndex - The tab index of the field, determining its position in the focus order.
     * @property {string} layoutElementName - The name of the layout element associated with the field.
     * @property {boolean} hasError - Indicates whether the field currently contains an error.
     */
    autoFocusField? : {
        tabIndex : number;
        layoutElementName : string;
        hasError : boolean;
    };
    /**
     * A debounced function to retrieve the current scroll position.
     *
     * This function is used to improve performance and reduce the frequency
     * of scroll-related operations by executing them after a specified delay
     * rather than on every scroll event.
     *
     * @function debouncedGetScrollPosition
     * @returns {void} This function does not return any value.
     */
    debouncedGetScrollPosition : DebouncedFunc<() => void>;
    /**
     * A boolean variable that determines whether the scroll position should be set.
     *
     * - When `true`, the functionality to set or adjust the scroll position is enabled.
     * - When `false`, the scroll position remains unchanged.
     *
     * Default value is `false`.
     */
    doSetScrollPosition : boolean = false;
    /**
     * Represents an optional array of strings that defines the keys
     * for global parameters used within dynamic functions.
     *
     * This variable can be used to store names of global parameters dynamically
     * accessed or utilized by functions where the keys are required
     * to manage global-level configuration or behavior.
     *
     * @type {string[]|undefined}
     */
    dynamicFunctionGlobalParameterKeys? : string[];
    /**
     * Represents an optional array of global parameter values used for dynamic functions.
     * This variable allows the configuration of global parameters to be passed dynamically
     * to the functions where required, enabling flexible and reusable functionality.
     *
     * @type {any[] | undefined}
     */
    dynamicFunctionGlobalParameterValues? : any[];
    /**
     * Represents optional global parameters for a dynamic function.
     * The `dynamicFunctionGlobalParameters` variable encapsulates settings
     * or configurations to be utilized globally across a dynamic function.
     * This property is optional and will contain an object of type `FunctionParameters`
     * if defined.
     */
    dynamicFunctionGlobalParameters? : FunctionParameters;
    /**
     * Represents the maximum length of the error context string.
     * This value is used to limit the number of characters in
     * the contextual information presented alongside an error.
     * Ensures that the error context does not exceed the specified
     * character length.
     */
    readonly errorContextMaxLength : number = 80;
    /**
     * A boolean variable that indicates whether the initial focus has been set.
     * Used to determine if a particular focus operation has already been performed.
     * Defaults to false.
     */
    firstFocusSet : boolean = false;
    /**
     * A variable that optionally specifies the message template string resolver.
     * This resolver is responsible for handling dynamic replacement or formatting of message strings
     * in templates, enabling custom logic for resolving placeholders or variables in messages.
     * It can be particularly useful for internationalization, localization, or dynamic content insertion
     * within message templates.
     *
     * The resolver should conform to the `MessageResolver` structure or interface, which determines
     * how message templates are processed and resolved.
     *
     * If not defined or set to `undefined`, no message resolution will take place.
     */
    messageTemplateStringResolver? : ( templateString : string, rawMode? : boolean ) => string;
    views : { [ key : string ] : CalendarViewReferenceType | OverviewViewReferenceType | null } = {};
    tabIndex : number = 1;
    maxTabIndex : number = 1;
    readonly tabIndexStep : number = 1000;
    variables? : Variables;
    /**
     * A mapping object that represents remapped enum value keys. This structure allows
     * the association of a set of old enum keys to new keys under various data descriptor aliases.
     *
     * @typedef {Object} remappedEnumValueKeys
     * @property {Object.<string, Object.<string, string>>} [dataDescriptorAlias] - An optional mapping where each key represents a data descriptor alias.
     *                                                                                 Each data descriptor alias contains a mapping of old keys to their corresponding new keys.
     */
    remappedEnumValueKeys? : {
        [ dataDescriptorAlias : string ] : {
            [ oldKey : string ] : string;
        };
    };
    /**
     * A mapping object that represents remapped reference enum value keys. This structure allows
     * the association of a set of old reference enum keys to new keys under various data descriptor aliases.
     *
     * @typedef {Object} remappedReferenceEnumValueKeys
     * @property {Object.<string, Object.<string, string>>} [dataDescriptorAlias] - An optional mapping where each key represents a data descriptor alias.
     *                                                                                 Each data descriptor alias contains a mapping of old keys to their corresponding new keys.
     */
    remappedReferenceEnumValueKeys? : {
        [ dataDescriptorAlias : string ] : {
            [ oldKey : string ] : string;
        };
    };
    isActionRunning : boolean = false;
    actionRunTimeoutId? : NodeJS.Timeout;

    /**
     * A boolean flag that indicates whether rendering should be deferred.
     *
     * When set to `true`, rendering operations will be delayed until a specific condition
     * or event to improve performance or batch updates. If `false`, rendering occurs immediately.
     *
     * Default value is `false`.
     */
    deferredRender : boolean = false;
    /**
     * Represents an array of property keys that have changed and are flagged for deferred rendering.
     * The properties listed within this array are intended to be rendered at a later update cycle,
     * optimizing the rendering performance by batching multiple changes.
     *
     * If undefined, it indicates either no changes have been flagged for deferred rendering or
     * the feature for tracking deferred render properties is not in use.
     */
    deferredRenderChangedProps : string[] | undefined;
    /**
     * Indicates whether an asynchronous `shouldComponentUpdate` operation is currently running.
     *
     * This variable tracks the execution state of a potentially asynchronous update check,
     * preventing conflicts, redundant executions, or unexpected behaviors when the process
     * is in progress. While `true`, it signifies that the asynchronous operation is active.
     * When `false`, it indicates that no such operation is currently underway.
     */
    asyncShouldComponentUpdateRunning : boolean = false;
    /**
     * Indicates whether the asynchronous operation triggered within the `shouldComponentUpdate` lifecycle method
     * should be canceled.
     *
     * When set to `true`, it prevents any ongoing asynchronous behavior in the `shouldComponentUpdate` method
     * from completing, typically used to optimize or reset the update process in React components.
     *
     * Default value is `false`.
     */
    cancelAsyncShouldComponentUpdate : boolean = false;

    /**
     * An array that holds numerical identifiers for scheduled action interval timers.
     * These IDs are typically returned by functions like `setInterval` and can be
     * used to clear the intervals using `clearInterval`.
     */
    scheduledActionIntervalIds : number[] = [];

    /**
     * A mapping object that tracks the visibility state of toast notifications.
     *
     * The keys in the object represent unique identifiers for toast parameters,
     * while the values are booleans indicating whether the corresponding toast
     * has been displayed or not.
     *
     * This variable is useful for ensuring that certain toast notifications
     * are only shown once or as per specific business logic.
     */
    shownToasts : {
        [ toastParams : string ] : boolean;
    } = {};

    uniqueCount : number = 0;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            deferredRenderEndTrigger: 0,
            justLoaded              : false,
            formGridUnitWidth       : 1400 / 96,
            remWidth                : 12,
            isViewDirty             : false
        };

        // bind
        this.orderLayoutStructure = this.orderLayoutStructure.bind( this );
        this.onFormClick = this.onFormClick.bind( this );
        this.onFormKeyUp = this.onFormKeyUp.bind( this );

        this.messageTemplateStringResolver = Form.getMessageTemplateStringResolver( props.dataContext, props.rootItemId ?? 0, props.elusiveData?.variables ?? {} );

        this.debouncedGetScrollPosition = _.debounce( this.getScrollPosition.bind( this ), 250 );

        this.debug = props.debug;

        if ( this.debug ) {
            this.renderStartTime = performance.now();
        }
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.init();

        this.setTabContainerScrollPositions();
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        this.cleanup();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps ) {
        this.checkForLoad();

        this.afterRender();

        this.setTabContainerScrollPositions();
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const startTime            = this.debug ? performance.now() : 0,
              changedProps         = objectUtils.getDiffKeys(
                  this.props,
                  nextProps,
                  [
                      'activeTabContainerIndexes',
                      'dataDescriptors',
                      'dataStructure',
                      'elusiveData',
                      'elusiveData.data',
                      'elusiveData.dataDescriptorMappings',
                      'elusiveData.dataDescriptorMappings.dataAndMetaModel',
                      'elusiveData.dataDescriptorMappings.editValue',
                      'elusiveData.dataDescriptorMappings.itemKeyCheck',
                      'elusiveData.dataDescriptorMappings.originalValue',
                      'elusiveData.dataDescriptorMappings.referenceInsertableCheck',
                      'elusiveData.dataDescriptorMappings.value',
                      'elusiveData.errorData',
                      'elusiveData.lastParams',
                      'elusiveData.layoutElementMappings',
                      'elusiveData.layoutElementMappings.cache',
                      'elusiveData.layoutElementMappings.errors',
                      'elusiveData.layoutElementMappings.setValueErrors',
                      'elusiveData.metaData',
                      'intercomMessages',
                      'layoutElements',
                      'layoutStructure',
                      'activeTabContainerIndexes',
                      'user',
                      'xhrRequestsForComponent'
                  ],
                  [
                      'elusiveData',
                      'elusiveData.dataDescriptorMappings',
                      'elusiveData.layoutElementMappings'
                  ],
                  [
                      'debug',
                      'elusiveData.dataVersions',
                      'focusedElement',
                      'scrollLeft',
                      'scrollTop'
                  ]
              ),
              changedState         = objectUtils.getDiffKeys(
                  this.state,
                  nextState
              ),
              treeVariablesChanged = objectUtils.getDiffKeys(
                  nextProps.treeVariables ?? {},
                  nextProps.elusiveData?.treeVariables ?? {}
              ),
              currentCount         = this.uniqueCount++,
              isViewDirty          = !!Object.values( this.views ).find( v => v?.dirty() );

        let retVal : boolean = changedProps.length > 1 || ( changedProps.length > 0 && !changedProps.includes( 'xhrRequestsForComponent' ) ),
            justLoaded       = this.state.justLoaded || nextState.justLoaded;

        this.debug = nextProps.debug;

        // treeVariables changed
        if ( treeVariablesChanged.length ) {
            retVal = false;
            this.props.setElusiveData( this.props.uid, { treeVariables: nextProps.treeVariables } );

            if ( !this.deferredRender ) {
                this.deferredRender = true;
                this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
            }
        }

        // isViewDirty changed
        if ( isViewDirty !== nextState.isViewDirty ) {
            retVal = false;
            this.setState( { isViewDirty } );

            if ( !this.deferredRender ) {
                this.deferredRender = true;
                this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
            }
        }

        // store finished (re)load
        if (
            changedProps.includes( 'xhrRequestsForComponent' )
            && this.props.xhrRequestsForComponent?.loadData
            && !nextProps.xhrRequestsForComponent?.loadData
            && !this.state.justLoaded
        ) {
            justLoaded = true;
            this.setState( { justLoaded: true } );
        }

        // form config changed -> show toast
        if (
            (
                changedProps.includes( 'dataStructure' )
                || changedProps.includes( 'dataDescriptors' )
                || changedProps.includes( 'layoutStructure' )
                || changedProps.includes( 'layoutElements' )
            )
            && Object.keys( this.props.dataStructure ).length
        ) {
            retVal = false;

            this.props.setMessage?.(
                this.props.uid,
                'toast',
                'configChanged',
                { id: 'showWarn' }
            );
        }

        // defer render if rootItem not exists
        if ( nextProps.rootItemId && !global.data[ nextProps.dataContext ]?.items[ nextProps.rootItemId ] ) {
            retVal = false;

            if ( !this.deferredRender ) {
                this.deferredRender = true;
                this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
            }
        }

        // collect all changedProps (including deferred ones)
        if ( this.deferredRenderChangedProps ) {
            for ( const deferredProp of this.deferredRenderChangedProps ) {
                if ( !changedProps.includes( deferredProp ) ) {
                    changedProps.push( deferredProp );
                }
            }
        }

        // check saveSuccess notification for rootItemId change
        if ( changedProps.includes( 'intercomMessages' ) && nextProps.intercomMessages?.notification ) {
            for ( const notification of Object.keys( nextProps.intercomMessages.notification ) ) {
                if ( notification === 'saveSuccess' && nextProps.rootItemId ) {
                    const newIdMapping = nextProps.intercomMessages.notification[ notification ],
                          rootItemId   = newIdMapping.itemMetaDataMapping?.[ nextProps.rootItemId ]?.id;

                    if ( rootItemId && rootItemId !== nextProps.rootItemId ) {
                        this.props.setParam( nextProps.uid, 'rootItemId', rootItemId );

                        this.props.unsetMessage( nextProps.uid, 'notification', notification as string );

                        this.deferredRender = true;
                        if ( changedState.includes( 'deferredRenderEndTrigger' ) ) {
                            changedState.splice( changedState.findIndex( cS => cS === 'deferredRenderEndTrigger' ), 1 );
                        }
                        this.setState( { deferredRenderEndTrigger: nextState.deferredRenderEndTrigger + 1 } );

                        break;
                    }
                }
            }
        }

        // handle deferred render
        if ( this.deferredRender ) {
            retVal = false;

            for ( const changedProp of changedProps ) {
                if ( !this.deferredRenderChangedProps ) {
                    this.deferredRenderChangedProps = [];
                }
                if ( !this.deferredRenderChangedProps.includes( changedProp ) ) {
                    this.deferredRenderChangedProps.push( changedProp );
                }
            }

            // end up deferred render
            if ( changedState.includes( 'deferredRenderEndTrigger' ) ) {
                this.deferredRender = false;
            }
            else if ( this.debug ) {
                console.log( `%c${`Form - shouldComponentUpdate (${currentCount}) - DEFERRED`.asLogLine( 100 )}`, `color: #${debugColorScheme.shouldComponentUpdateDeferred};` );

                if ( this.renderStartTime === 0 ) {
                    this.renderStartTime = startTime;
                }
            }
        }

        // do nothing if still deferred
        if ( !this.deferredRender ) {
            const currentDeferredRenderChangedProps = this.deferredRenderChangedProps ?? [];

            if ( this.debug ) {
                console.groupCollapsed( `%c${`%cForm - %cshouldComponentUpdate (${currentCount})%c`.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.shouldComponentUpdate};`, 'font-weight: normal;' );
                console.log( '    changedProps', changedProps );
                console.log( '    changedState', changedState );
            }

            delete this.deferredRenderChangedProps;

            if (
                changedProps.length
                || changedState.includes( 'deferredRenderEndTrigger' )
                || changedState.includes( 'isViewDirty' )
            ) {
                // init messageTemplateStringResolver
                if (
                    changedProps.includes( 'elusiveData.data' )
                    || changedProps.includes( 'elusiveData.variables' )
                    || changedProps.includes( 'elusiveData.treeVariables' )
                    || changedProps.includes( 'rootItemId' )
                    || changedProps.includes( 'user' )
                    || changedProps.includes( 'messageCatalogTimestamp' )
                    || changedProps.includes( 'metaModelTimestamp' )
                ) {
                    if ( this.debug ) {
                        console.log( `buildMessageTemplateStringResolver (${currentCount})`.asLogLine( 100 ) );
                    }

                    this.messageTemplateStringResolver = Form.getMessageTemplateStringResolver(
                        nextProps.dataContext,
                        nextProps.rootItemId ?? 0,
                        nextProps.elusiveData?.variables ?? {},
                        {
                            treeVariables: {
                                ...nextProps.elusiveData?.treeVariables,
                                [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                                    // toString
                                    if ( hint === 'string' ) {
                                        return nl2br( JSON.stringify( nextProps.elusiveData?.treeVariables ?? {}, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                                    }
                                    // all other
                                    else {
                                        return null;
                                    }
                                }
                            }
                        }
                    );
                }

                // form config or data changed
                if (
                    changedProps.includes( 'dataStructure' )
                    || changedProps.includes( 'dataDescriptors' )
                    || changedProps.includes( 'layoutStructure' )
                    || changedProps.includes( 'layoutElements' )
                    || changedProps.includes( 'activeTabContainerIndexes' )
                    || changedProps.includes( 'elusiveData.data' )
                    || changedProps.includes( 'elusiveData.dataDescriptorMappings.editValue' )
                    || changedProps.includes( 'elusiveData.dataDescriptorMappings.itemKeyCheck' )
                    || changedProps.includes( 'elusiveData.dataDescriptorMappings.referenceInsertableCheck' )
                    || changedProps.includes( 'elusiveData.layoutElementMappings.setValueErrors' )
                    || changedProps.includes( 'elusiveData.variables' )
                    || changedProps.includes( 'elusiveData.lastSaveTimestamp' )
                    || changedProps.includes( 'elusiveData.lastSaveRequestParameters' )
                    || changedProps.includes( 'elusiveData.variablesEditValues' )
                    || changedProps.includes( 'elusiveData.treeVariables' )
                    || changedProps.includes( 'rootItemId' )
                    || changedProps.includes( 'user' )
                    || changedProps.includes( 'messageCatalogTimestamp' )
                    || changedProps.includes( 'metaModelTimestamp' )
                    || changedProps.includes( 'configHash' )
                    || !nextProps.elusiveData?.dataDescriptorMappings
                    || changedState.includes( 'isViewDirty' )
                ) {
                    const {
                              uid,
                              dataContext,
                              schema,
                              rootItemId,
                              rootItemKey,
                              rootItemType,
                              dataStructure,
                              dataDescriptors,
                              layoutElements,
                              elusiveData,
                              user
                          } = nextProps;

                    if ( dataStructure ) {
                        const asyncBuild = async () => {
                            const asyncStartTime         = this.debug ? performance.now() : 0,
                                  rootDataId             = rootItemId ?? dataUtils.findItem( dataContext, rootItemType ?? '', rootItemKey ?? '' )?.id ?? 0,
                                  dataDescriptorMappings = elusiveData?.dataDescriptorMappings ? { ...elusiveData.dataDescriptorMappings } : {} as DataDescriptorMappings,
                                  layoutElementMappings  = elusiveData?.layoutElementMappings ? { ...elusiveData.layoutElementMappings } : {};

                            let newElusiveData                    = elusiveData,
                                errorData                         = elusiveData?.errorData,
                                isDirty                           = elusiveData?.isDirty,
                                viewDataContextsToSave : string[] = [];

                            this.asyncShouldComponentUpdateRunning = true;
                            this.deferredRender = true;

                            await Promise.resolve();

                            if ( this.debug ) {
                                console.groupCollapsed( `%c${`%cForm - %cshouldComponentUpdate (${currentCount}) - async part%c`.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.shouldComponentUpdateAsync};`, 'font-weight: normal;' );
                            }

                            try {
                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // get dataDescriptor to dataAndMetaModel (metaModel part) mapping
                                if (
                                    !Object.keys( dataDescriptorMappings.dataAndMetaModel ?? {} ).length
                                    || changedProps.includes( 'dataDescriptors' )
                                    || changedProps.includes( 'rootItemId' )
                                    || changedProps.includes( 'metaModelTimestamp' )
                                ) {
                                    dataDescriptorMappings.dataAndMetaModel = this.buildDataDescriptorToMetaModelMapping( dataDescriptors, dataContext, schema, dataStructure, rootDataId, user );
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // get dataDescriptor to dataAndMetaModel (data part) and dataDescriptor to value mapping
                                if (
                                    dataDescriptorMappings.dataAndMetaModel !== elusiveData?.dataDescriptorMappings?.dataAndMetaModel
                                    || changedProps.includes( 'dataDescriptors' )
                                    || changedProps.includes( 'rootItemId' )
                                    || changedProps.includes( 'elusiveData.data' )
                                ) {
                                    dataDescriptorMappings.dataAndMetaModel = this.buildDataDescriptorToDataMapping( dataDescriptors, dataDescriptorMappings, dataContext, schema, dataStructure, rootDataId );
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // get dataDescriptor to value mapping
                                if (
                                    !dataDescriptorMappings.value
                                    || dataDescriptorMappings.dataAndMetaModel !== elusiveData?.dataDescriptorMappings?.dataAndMetaModel
                                    || changedProps.includes( 'elusiveData.dataDescriptorMappings.editValue' )
                                ) {
                                    const dataDescriptorMappingsValue = this.buildDataDescriptorToValueMapping( dataDescriptors, dataDescriptorMappings );
                                    if ( JSON.stringify( dataDescriptorMappingsValue ) !== JSON.stringify( dataDescriptorMappings.value ) ) {
                                        dataDescriptorMappings.value = dataDescriptorMappingsValue;
                                    }
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // get dataDescriptor to originalValue mapping
                                if (
                                    !dataDescriptorMappings.originalValue
                                    || dataDescriptorMappings.dataAndMetaModel !== elusiveData?.dataDescriptorMappings?.dataAndMetaModel
                                ) {
                                    const dataDescriptorMappingsOriginalValue = this.buildDataDescriptorToOriginalValueMapping( dataDescriptors, dataDescriptorMappings );
                                    if ( JSON.stringify( dataDescriptorMappingsOriginalValue ) !== JSON.stringify( dataDescriptorMappings.originalValue ) ) {
                                        dataDescriptorMappings.originalValue = dataDescriptorMappingsOriginalValue;
                                    }
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // get dataDescriptor to itemKeyCheck mapping
                                if (
                                    !dataDescriptorMappings.itemKeyCheck
                                    || changedProps.includes( 'dataDescriptors' )
                                    || changedProps.includes( 'rootItemId' )
                                ) {
                                    dataDescriptorMappings.itemKeyCheck = this.buildDataDescriptorsToItemKeyCheckMapping( dataDescriptors, dataDescriptorMappings );
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // get dataDescriptor to referenceInsertable mapping
                                if (
                                    !dataDescriptorMappings.referenceInsertableCheck
                                    || changedProps.includes( 'dataDescriptors' )
                                    || changedProps.includes( 'rootItemId' )
                                ) {
                                    dataDescriptorMappings.referenceInsertableCheck = this.buildDataDescriptorsToReferenceInsertableCheckMapping( dataDescriptors, dataDescriptorMappings );
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // get layoutElement to cache mapping (functions and inheritance applied)
                                if (
                                    !layoutElementMappings.cache
                                    || dataDescriptorMappings.dataAndMetaModel !== elusiveData?.dataDescriptorMappings?.dataAndMetaModel
                                    || changedProps.includes( 'layoutElements' )
                                    || changedProps.includes( 'activeTabContainerIndexes' )
                                    || changedProps.includes( 'elusiveData.variables' )
                                    || changedProps.includes( 'elusiveData.treeVariables' )
                                    || changedProps.includes( 'user' )
                                    || changedProps.includes( 'messageCatalogTimestamp' )
                                    || changedProps.includes( 'metaModelTimestamp' )
                                ) {
                                    const layoutElementMappingsCache = await this.buildLayoutElementToCacheMapping( dataDescriptorMappings, nextProps, justLoaded, elusiveData?.layoutElementMappings );
                                    if ( JSON.stringify( layoutElementMappingsCache ) !== JSON.stringify( elusiveData?.layoutElementMappings?.cache ) ) {
                                        layoutElementMappings.cache = layoutElementMappingsCache;
                                    }
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // update layoutElement to errors mapping (validation)
                                if (
                                    !layoutElementMappings.errors
                                    || layoutElementMappings !== elusiveData?.layoutElementMappings
                                    || dataDescriptorMappings.value !== elusiveData?.dataDescriptorMappings?.value
                                    || dataDescriptorMappings.itemKeyCheck !== elusiveData?.dataDescriptorMappings?.itemKeyCheck
                                    || changedProps.includes( 'elusiveData.dataDescriptorMappings.itemKeyCheck' )
                                    || dataDescriptorMappings.referenceInsertableCheck !== elusiveData?.dataDescriptorMappings?.referenceInsertableCheck
                                    || changedProps.includes( 'elusiveData.dataDescriptorMappings.referenceInsertableCheck' )
                                    || changedProps.includes( 'layoutElements' )
                                    || changedProps.includes( 'elusiveData.variables' )
                                    || changedProps.includes( 'elusiveData.variablesEditValues' )
                                    || changedProps.includes( 'elusiveData.treeVariables' )
                                    || changedProps.includes( 'user' )
                                    || changedProps.includes( 'messageCatalogTimestamp' )
                                ) {
                                    const layoutElementMappingsErrors = await this.buildLayoutElementToErrorsMapping( layoutElements, layoutElementMappings, dataDescriptorMappings, dataDescriptors, elusiveData?.variables, elusiveData?.variablesEditValues, user, ( nextProps.rootItemId ?? 0 ) < 0 );

                                    if ( JSON.stringify( layoutElementMappingsErrors ) !== JSON.stringify( elusiveData?.layoutElementMappings?.errors ) ) {
                                        layoutElementMappings.errors = layoutElementMappingsErrors;
                                    }
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // update errorData
                                if (
                                    layoutElementMappings.errors !== elusiveData?.layoutElementMappings?.errors
                                    || changedProps.includes( 'elusiveData.layoutElementMappings.setValueErrors' )
                                    || changedProps.includes( 'user' )
                                    || changedProps.includes( 'messageCatalogTimestamp' )
                                ) {
                                    const elusiveDataErrorData = this.buildErrorData( layoutElements, dataDescriptors, dataStructure, dataDescriptorMappings, layoutElementMappings, schema, rootItemId );
                                    if ( JSON.stringify( elusiveDataErrorData ) !== JSON.stringify( elusiveData?.errorData ) ) {
                                        errorData = elusiveDataErrorData;
                                    }
                                    // clear new errors if just loaded (no in form marking)
                                    /*if ( justLoaded ) {
                                        layoutElementMappings.errors = elusiveData?.layoutElementMappings?.errors;
                                    }*/
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // update dirty
                                if (
                                    /*!justLoaded
                                    &&*/
                                    elusiveData?.data?.items
                                    && (
                                        changedProps.includes( 'elusiveData.data' )
                                        || changedProps.includes( 'elusiveData.variables' )
                                        || changedProps.includes( 'elusiveData.treeVariables' )
                                        || changedProps.includes( 'elusiveData.lastSaveRequestParameters' )
                                        || changedState.includes( 'isViewDirty' )
                                    )
                                ) {

                                    // set last save request params (temporary)
                                    if ( !elusiveData.lastSaveRequestParameters && nextProps.saveConfig.requestParams ) {
                                        elusiveData.lastSaveRequestParameters = JSON.stringify( Form.resolveRequestParameters( nextProps.saveConfig.requestParams, schema, dataContext, nextProps.rootItemId ?? 0, elusiveData?.variables ) );
                                    }

                                    isDirty = this.buildDirty( nextProps, elusiveData );
                                    viewDataContextsToSave = Object.values( this.views ).reduce(
                                        ( innerRetVal, v ) => {
                                            if ( v?.dirty() ) {
                                                for ( const dC of v.dirtyDataContexts() ) {
                                                    if ( !innerRetVal.includes( dC ) ) {
                                                        innerRetVal.push( dC );
                                                    }
                                                }
                                            }

                                            return innerRetVal;
                                        },
                                        [] as string[] );
                                }

                                if ( this.cancelAsyncShouldComponentUpdate ) {
                                    // eslint-disable-next-line no-throw-literal
                                    throw ( 'canceled' );
                                }

                                // update elusiveData
                                if (
                                    JSON.stringify( dataDescriptorMappings.dataAndMetaModel ) !== JSON.stringify( elusiveData?.dataDescriptorMappings?.dataAndMetaModel ?? {} )
                                    || dataDescriptorMappings.value !== elusiveData?.dataDescriptorMappings?.value
                                    || dataDescriptorMappings.originalValue !== elusiveData?.dataDescriptorMappings?.originalValue
                                    || dataDescriptorMappings.itemKeyCheck !== elusiveData?.dataDescriptorMappings?.itemKeyCheck
                                    || layoutElementMappings.cache !== elusiveData?.layoutElementMappings?.cache
                                    || layoutElementMappings.errors !== elusiveData?.layoutElementMappings?.errors
                                    || layoutElementMappings.propsByFunctions !== elusiveData?.layoutElementMappings?.propsByFunctions
                                    || errorData !== elusiveData?.errorData
                                    || isDirty !== ( elusiveData?.isDirty ?? false )
                                    || JSON.stringify( viewDataContextsToSave ) !== JSON.stringify( elusiveData?.viewDataContextsToSave ?? [] )
                                ) {
                                    newElusiveData = {
                                        dataDescriptorMappings,
                                        layoutElementMappings,
                                        errorData,
                                        isDirty,
                                        viewDataContextsToSave
                                    };

                                    this.props.setElusiveData( uid, newElusiveData );

                                    if ( errorData !== elusiveData?.errorData && this.props.onErrorDataChanged ) {
                                        this.props.onErrorDataChanged( this.props.uid, errorData );
                                    }

                                    if ( isDirty !== undefined && isDirty !== ( elusiveData?.isDirty ?? false ) && this.props.onDirtyChanged ) {
                                        this.props.onDirtyChanged( this.props.uid, isDirty );
                                    }
                                }
                                else if ( this.debug ) {
                                    console.log( `    elusiveData NOT changed (${currentCount})` );
                                }

                                // handle FormField defaultValue
                                if ( this.state.justLoaded || nextState.justLoaded ) {
                                    this.setState( { justLoaded: false } );

                                    await this.handleDefaultValues( { ...nextProps, elusiveData: newElusiveData } );
                                }
                                else {
                                    this.autoFocusField = undefined;
                                }
                            }
                            catch ( e ) {
                                if ( e === 'canceled' ) {
                                    if ( this.debug ) {
                                        console.log( `    CANCELED (${currentCount})` );
                                    }

                                    // add current currentDeferredRenderChangedProps
                                    for ( const changedProp of currentDeferredRenderChangedProps ) {
                                        if ( !this.deferredRenderChangedProps ) {
                                            this.deferredRenderChangedProps = [];
                                        }
                                        if ( !this.deferredRenderChangedProps.includes( changedProp ) ) {
                                            this.deferredRenderChangedProps.push( changedProp );
                                        }
                                    }

                                    this.cancelAsyncShouldComponentUpdate = false;
                                }
                            }
                            finally {
                                this.asyncShouldComponentUpdateRunning = false;

                                if ( !( changedProps.length === 1 && changedProps.includes( 'deferredRenderEndTrigger' ) ) || newElusiveData !== elusiveData ) {
                                    this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                                }

                                if ( this.debug ) {
                                    console.log( `%c(${currentCount}) - ${Math.round( 1000 * ( performance.now() - asyncStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.shouldComponentUpdateAsync}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
                                    console.groupEnd();
                                }
                            }
                        };

                        retVal = false;

                        if ( this.debug && this.renderStartTime === 0 ) {
                            this.renderStartTime = startTime;
                        }

                        void asyncBuild();
                    }
                }

                // if deferredRender end up without any processing let it render
                else if ( !retVal && changedState.includes( 'deferredRenderEndTrigger' ) ) {
                    retVal = true;
                }
            }

            // handle scheduled actions
            if ( this.state.justLoaded && !nextState.justLoaded ) {
                this.startScheduledActions( nextProps );
            }

            // clear masking
            if ( retVal && nextProps.xhrRequestsForComponent?.processing ) {
                this.props.endRequest( () => {
                }, this.props.uid, 'processing' );
            }

            retVal = retVal || changedState.includes( 'dynamicFunctionErrors' );

            if ( this.debug ) {
                console.log( '    retVal', retVal );
                console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.shouldComponentUpdate}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
                console.groupEnd();
            }
        }

        if ( this.debug && retVal && this.renderStartTime === 0 ) {
            this.renderStartTime = startTime;
        }

        const dataVersions = JSON.stringify( { items: nextProps.elusiveData?.data?.items, references: nextProps.elusiveData?.data?.references } );
        if ( dataVersions !== nextProps.elusiveData?.dataVersions ) {
            this.props.setElusiveData( this.props.uid, { dataVersions } );
        }

        return retVal;
    }


    /* event handlers *********************************************************************************************************************/

    onFormClick() {
        const { uid } = this.props;

        if ( document.activeElement && document.getElementById( `${uid}_form` )?.contains( document.activeElement ) ) {
            const path : string[] = [];

            let currentElement : Element | null = document.activeElement,
                parentElement : Element | null  = null;

            while ( currentElement && !currentElement.id ) {
                parentElement = currentElement.parentElement;

                if ( parentElement ) {
                    const children = parentElement.children;

                    for ( let i = 0; i < children.length; i++ ) {
                        if ( children[ i ] === currentElement ) {
                            path.unshift( `${currentElement.tagName.toLowerCase()}:nth-child( ${i + 1} )` );
                            break;
                        }
                    }
                }

                currentElement = parentElement;
            }

            if ( currentElement?.id && !currentElement.id.match( /_reference[A-Z]./ ) ) {
                this.props.setParam( uid, 'focusedElement', {
                    id: currentElement.id,
                    path,

                    // could be an input or textarea so selectionStart could be valid
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    selectionStart: document.activeElement.selectionStart,
                    // could be an input or textarea so selectionEnd could be valid
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    selectionEnd: document.activeElement.selectionEnd
                } );
            }
        }
    }

    onFormKeyUp( event : React.KeyboardEvent<HTMLDivElement> ) {
        if ( event.key === 'Tab' ) {
            this.onFormClick();
        }
    }

    onVariableChange( realLayoutElementId : number | string, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | {
        start : string;
        end : string;
    } | undefined, originalEvent : any, props : AllProps = this.props ) {
        void this.onVariableChangeAsync( realLayoutElementId, value, originalEvent, props );
    }

    async onVariableChangeAsync( realLayoutElementId : number | string, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | {
        start : string;
        end : string;
    } | undefined, originalEvent : any, props : AllProps = this.props ) {
        const startTime         = this.debug ? performance.now() : 0,
              {
                  uid,
                  dataDescriptors,
                  layoutElements,
                  elusiveData
              }                 = props,
              {
                  layoutElementId,
                  parentId
              }                 = this.getLayoutElementIdAndParentIdFromRealLayoutElementId( realLayoutElementId ),
              layoutElement     = layoutElements[ layoutElementId ] as FormFieldData,
              layoutElementData = layoutElement.data as VariableFieldProperties,
              isDeferred        = this.deferredRender;

        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cForm - %conVariableChange%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onValueChange};`, 'font-weight: normal;' );
        }

        if ( layoutElement.data.type === 'CALENDAR' ) {
            const variables = { ...elusiveData?.variables };

            variables[ layoutElement.data.key ] = value;

            props.setElusiveData( uid, {
                variables
            } );
        }

        else if ( layoutElementData.variableName && elusiveData?.dataDescriptorMappings && elusiveData?.layoutElementMappings ) {

            // prevent render
            if ( !isDeferred ) {
                this.deferRender();
            }

            // update focusedElement
            this.updateFocusedElement();

            const dataDescriptorMappings = { ...elusiveData.dataDescriptorMappings },
                  layoutElementMappings  = { ...elusiveData.layoutElementMappings },
                  variables              = { ...elusiveData?.variables },
                  variablesEditValues    = { ...elusiveData?.variablesEditValues },
                  validationErrors       = await this.validateValueForLayoutElement( layoutElement, undefined, elusiveData.layoutElementMappings, value, dataDescriptorMappings, dataDescriptors, false, originalEvent, elusiveData?.variables );

            delete variablesEditValues[ layoutElementData.variableName ];
            if ( parentId && variablesEditValues.repeated?.[ layoutElementData.variableName ]?.[ parentId ] ) {
                delete variablesEditValues.repeated[ layoutElementData.variableName ][ parentId ];
            }

            // set value
            if ( !validationErrors ) {
                variables[ layoutElementData.variableName ] = value;
                if ( parentId ) {
                    variables.repeated = {
                        ...variables.repeated,
                        [ layoutElementData.variableName ]: {
                            ...variables.repeated?.[ layoutElementData.variableName ],
                            [ parentId ]: value
                        }
                    };
                }

                props.setElusiveData( uid, {
                    variables,
                    variablesEditValues
                } );

                await this.onTriggerAction(
                    realLayoutElementId,
                    originalEvent,
                    {
                        ...props,
                        elusiveData: {
                            ...props.elusiveData,
                            dataDescriptorMappings,
                            layoutElementMappings,
                            variables
                        }
                    }
                );
            }
            else {
                variablesEditValues[ layoutElementData.variableName ] = value;
                if ( parentId ) {
                    variablesEditValues.repeated = {
                        ...variablesEditValues.repeated,
                        [ layoutElementData.variableName ]: {
                            ...variablesEditValues.repeated?.[ layoutElementData.variableName ],
                            [ parentId ]: value
                        }
                    };
                }

                props.setElusiveData( uid, {
                    variablesEditValues
                } );
            }

            // force render if deferred
            if ( !isDeferred && this.deferredRender ) {
                setTimeout( () => {
                    this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                }, 5 );
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onValueChange}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();

            if ( this.renderStartTime === 0 ) {
                this.renderStartTime = startTime;
            }
        }
    }

    onIntermediateVariableChange( realLayoutElementId : number | string, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | {
        start : string;
        end : string;
    } | undefined, originalEvent : any, props : AllProps = this.props ) {
        const startTime = this.debug ? performance.now() : 0;

        // update focusedElement
        this.updateFocusedElement();

        if ( this.debug ) {
            console.log( `%cForm - %conIntermediateVariableChange%c - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 108 ), `font-weight: bold;`, `color: #${debugColorScheme.onIntermediateValueChange}; font-weight: bold;`, 'color: unset; font-weight: bold;', 'font-weight: unset;' );
        }
    }

    onValueChange( realLayoutElementId : number | string, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any, props : AllProps = this.props ) {
        void this.onValueChangeAsync( realLayoutElementId, value, originalEvent, props );
    }

    async onValueChangeAsync( realLayoutElementId : number | string, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any, props : AllProps = this.props ) {
        const startTime  = this.debug ? performance.now() : 0,
              {
                  uid,
                  dataDescriptors,
                  layoutElements,
                  elusiveData
              }          = props,
              isDeferred = this.deferredRender,
              {
                  layoutElementId,
                  parentId
              }          = this.getLayoutElementIdAndParentIdFromRealLayoutElementId( realLayoutElementId );

        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cForm - %conValueChange%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onValueChange};`, 'font-weight: normal;' );
        }

        if ( elusiveData?.dataDescriptorMappings && elusiveData?.layoutElementMappings ) {

            // prevent render
            if ( !isDeferred ) {
                this.deferRender();
            }

            // update focusedElement
            this.updateFocusedElement();

            const layoutElement                        = layoutElements[ layoutElementId ] as FormFieldData,
                  layoutElementDataDescriptorAlias     = layoutElement.dataDescriptorAlias ?? '',
                  realLayoutElementDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( layoutElementDataDescriptorAlias, parentId ),
                  dataDescriptorMappings               = { ...elusiveData.dataDescriptorMappings },
                  layoutElementMappings                = { ...elusiveData.layoutElementMappings },
                  validationErrors                     = await this.validateValueForLayoutElement( layoutElement, parentId, elusiveData.layoutElementMappings, value, dataDescriptorMappings, dataDescriptors, true, originalEvent, elusiveData?.variables );

            let originalValue : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined;

            dataDescriptorMappings.editValue = { ...dataDescriptorMappings.editValue };
            layoutElementMappings.setValueErrors = { ...layoutElementMappings.setValueErrors };
            delete layoutElementMappings.setValueErrors[ realLayoutElementId ];

            // set value
            if ( !validationErrors ) {
                try {
                    originalValue = JSON.parse( JSON.stringify( value ) );
                }
                catch ( e ) {
                    // do nothing
                }

                const setValueErrors = await this.setValueForLayoutElement( layoutElement, parentId, value, props, originalEvent );

                if ( setValueErrors ) {
                    layoutElementMappings.setValueErrors[ realLayoutElementId ] = setValueErrors;
                }
            }

            // update or delete edit value
            // for enum(Reference)Properties
            if (
                dataDescriptorMappings.dataAndMetaModel?.[ layoutElementDataDescriptorAlias ]?.type === 'enumProperty'
                || dataDescriptorMappings.dataAndMetaModel?.[ layoutElementDataDescriptorAlias ]?.type === 'enumReferenceProperty'
            ) {
                originalValue = originalValue as EnumUpdateValue;
                value = value as EnumUpdateValue;

                let setValueErrors : EnumValueErrors | undefined;

                try {
                    setValueErrors = JSON.parse( layoutElementMappings.setValueErrors[ realLayoutElementId ]?.[ 0 ] ?? '{}' );
                }
                catch ( e ) {
                    // do nothing
                }

                // clear editValue structure for removed keys
                for ( const stringKey of Object.keys( originalValue ) ) {
                    if ( !Object.keys( value ).includes( stringKey ) ) {
                        delete ( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue )?.[ stringKey ];
                    }
                }

                for ( const stringKey of Object.keys( value ) ) {

                    // errors -> update edit value
                    if ( validationErrors ?? setValueErrors?.[ stringKey ] ) {
                        const attributes = Object.keys( value[ stringKey ] ) as ( keyof EnumUpdateValueAttributes )[];

                        for ( const attribute of attributes ) {
                            ( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue ) = {
                                ...( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue ),
                                [ stringKey ]: {
                                    ...( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue )?.[ stringKey ],
                                    [ attribute ]: value[ stringKey ][ attribute ]
                                }
                            };
                        }

                        // cleanup editValue structure
                        if ( !Object.keys( ( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue )?.[ stringKey ] ?? {} ).length ) {
                            delete ( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue )?.[ stringKey ];
                        }
                    }

                    // no errors -> delete edit value
                    else {
                        delete ( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue )?.[ stringKey ];
                    }
                }

                // cleanup editValue structure
                if ( !Object.keys( ( dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] as EnumUpdateValue ) ?? {} ).length ) {
                    delete dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ];
                }
            }

            // errors -> update edit value
            else if (
                validationErrors
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                || layoutElementMappings.setValueErrors[ layoutElementId ]
            ) {
                dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ] = value ?? null;
            }

            // no errors - no parentId -> delete edit value
            else {
                delete dataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ];
            }

            props.setElusiveData( uid, {
                dataDescriptorMappings,
                layoutElementMappings
            } );

            // no errors -> trigger actions
            if ( !validationErrors && !layoutElementMappings.setValueErrors[ realLayoutElementId ] ) {
                await this.onTriggerAction(
                    realLayoutElementId,
                    originalEvent,
                    {
                        ...props,
                        elusiveData: {
                            ...props.elusiveData,
                            dataDescriptorMappings,
                            layoutElementMappings
                        }
                    }
                );
            }

            // force render if deferred
            if ( !isDeferred && this.deferredRender ) {
                setTimeout( () => {
                    this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                }, 5 );
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onValueChange}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();

            if ( this.renderStartTime === 0 ) {
                this.renderStartTime = startTime;
            }
        }
    }

    onIntermediateValueChange( realLayoutElementId : number | string, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any, props : AllProps = this.props ) {
        const startTime = this.debug ? performance.now() : 0,
              {
                  layoutElements,
                  elusiveData
              }         = props,
              {
                  layoutElementId,
                  parentId
              }         = this.getLayoutElementIdAndParentIdFromRealLayoutElementId( realLayoutElementId );

        // update focusedElement
        this.updateFocusedElement();

        if ( elusiveData?.dataDescriptorMappings ) {
            const layoutElement                    = layoutElements[ layoutElementId ] as FormFieldData,
                  layoutElementsData               = elusiveData?.layoutElementMappings?.cache ?? {},
                  layoutElementData                = layoutElementsData[ layoutElementId ],
                  {
                      readOnly,
                      visibility
                  }                                = layoutElementData,
                  layoutElementDataDescriptorAlias = layoutElement.dataDescriptorAlias ?? '',
                  dataDescriptorMappings           = { ...elusiveData.dataDescriptorMappings };

            // abort running key check
            if (
                !readOnly
                && visibility === 'visible'
                && dataDescriptorMappings?.itemKeyCheck?.[ layoutElementDataDescriptorAlias ]
                && (
                    !parentId
                    || ( dataDescriptorMappings?.itemKeyCheck[ layoutElementDataDescriptorAlias ] as {
                        [ parentId : number ] : DataDescriptorItemKeyCheckData
                    } )?.[ parentId ]
                )
            ) {
                const componentUid = this.getItemKeyCheckComponentId(),
                      requestUid   = this.getItemKeyCheckRequestId( layoutElementDataDescriptorAlias, parentId );

                xhr.abort( componentUid, requestUid );
            }
        }

        if ( this.debug ) {
            console.log( `%cForm - %conIntermediateValueChange%c - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 108 ), `font-weight: bold;`, `color: #${debugColorScheme.onIntermediateValueChange}; font-weight: bold;`, 'color: unset; font-weight: bold;', 'font-weight: unset;' );
        }
    }

    onValueInvalid( realLayoutElementId : number | string, errors : string[], props : AllProps = this.props ) {
        const startTime  = this.debug ? performance.now() : 0,
              {
                  uid,
                  elusiveData
              }          = props,
              isDeferred = this.deferredRender;

        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cForm - %conValueInvalid%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onValueInvalid};`, 'font-weight: normal;' );
        }

        if ( elusiveData?.layoutElementMappings ) {

            // prevent render
            if ( !isDeferred ) {
                this.deferRender();
            }

            // update focusedElement
            this.updateFocusedElement();

            const layoutElementMappings = { ...elusiveData.layoutElementMappings };

            layoutElementMappings.setValueErrors = { ...layoutElementMappings.setValueErrors };
            layoutElementMappings.setValueErrors[ realLayoutElementId ] = errors;

            props.setElusiveData( uid, {
                layoutElementMappings
            } );

            // force render if deferred
            if ( !isDeferred && this.deferredRender ) {
                setTimeout( () => {
                    this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                }, 5 );
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onValueInvalid}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();

            if ( this.renderStartTime === 0 ) {
                this.renderStartTime = startTime;
            }
        }
    }

    onIntermediateValueInvalid( realLayoutElementId : number | string, errors : string[], props : AllProps = this.props ) {
        const startTime = this.debug ? performance.now() : 0;

        // update focusedElement
        this.updateFocusedElement();

        if ( this.debug ) {
            console.log( `%cForm - %conIntermediateValueInvalid%c - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 108 ), `font-weight: bold;`, `color: #${debugColorScheme.onIntermediateValueInvalid}; font-weight: bold;`, 'color: unset; font-weight: bold;', 'font-weight: unset;' );
        }
    }

    onValueValid( realLayoutElementId : number | string, props : AllProps = this.props ) {
        const startTime  = this.debug ? performance.now() : 0,
              {
                  uid,
                  elusiveData
              }          = props,
              isDeferred = this.deferredRender;

        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cForm - %conValueValid%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onValueValid};`, 'font-weight: normal;' );
        }

        if ( elusiveData?.layoutElementMappings ) {

            // prevent render
            if ( !isDeferred ) {
                this.deferRender();
            }

            // update focusedElement
            this.updateFocusedElement();

            const layoutElementMappings = { ...elusiveData.layoutElementMappings };

            layoutElementMappings.setValueErrors = { ...layoutElementMappings.setValueErrors };
            delete layoutElementMappings.setValueErrors[ realLayoutElementId ];

            props.setElusiveData( uid, {
                layoutElementMappings
            } );

            // force render if deferred
            if ( !isDeferred && this.deferredRender ) {
                setTimeout( () => {
                    this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                }, 5 );
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onValueValid}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();

            if ( this.renderStartTime === 0 ) {
                this.renderStartTime = startTime;
            }
        }
    }

    onIntermediateValueValid( realLayoutElementId : number | string, props : AllProps = this.props ) {
        const startTime = this.debug ? performance.now() : 0;

        // update focusedElement
        this.updateFocusedElement();

        if ( this.debug ) {
            console.log( `%cForm - %conIntermediateValueValid%c - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 108 ), `font-weight: bold;`, `color: #${debugColorScheme.onIntermediateValueValid}; font-weight: bold;`, 'color: unset; font-weight: bold;', 'font-weight: unset;' );
        }
    }

    async onTriggerAction( realLayoutElementId : number | string, originalEvent : any, props : AllProps = this.props ) {
        const startTime                       = this.debug ? performance.now() : 0,
              {
                  uid,
                  schema,
                  dataContext,
                  dataStructure,
                  rootItemId,
                  dataDescriptors,
                  layoutElements,
                  mode,
                  activeTabContainerIndexes,
                  elusiveData
              }                               = props,
              {
                  layoutElementId,
                  parentId
              }                               = this.getLayoutElementIdAndParentIdFromRealLayoutElementId( realLayoutElementId ),
              layoutElementsData              = elusiveData?.layoutElementMappings?.cache ?? {},
              layoutElement                   = layoutElements[ layoutElementId ] as FormFieldData | undefined,
              dataDescriptor : DataDescriptor = dataDescriptors?.[ layoutElement?.dataDescriptorAlias ?? '' ],
              layoutElementData               = layoutElementsData[ layoutElementId ] as FormFieldProperties | VariableFieldProperties,
              itemIdProps                     = _.pick( props, [ 'rootItemId', 'formParentItemId', 'treeParentItemId', 'treeRootItemId' ] );

        if ( this.debug ) {
            if ( this.renderStartTime === 0 ) {
                this.renderStartTime = startTime;
            }

            console.groupCollapsed( `%c${`%cForm - %conTriggerAction - ${layoutElementData.type === 'BUTTON' ? 'click' : 'change'}%c`.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onTriggerAction};`, 'font-weight: normal;' );
        }

        let aborted : boolean = false;

        if ( layoutElementData?.actions?.length ) {
            const actionsStartTime                                  = this.debug ? performance.now() : 0,
                  actionDataContext                                 = `changeData_actions_${layoutElementId}`,
                  clonedObjectCache : ClonedObjectCache             = { items: {}, references: {}, originalStates: { items: {}, references: {} } },
                  clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} },
                  treeVariables                                     = { ...this.props.treeVariables };

            if ( this.debug ) {
                console.groupCollapsed( `%c${'%cexecute actions%c'.asLogLine( 104 )}`, `font-weight: normal;`, `font-weight: bold;`, 'font-weight: normal;' );
            }

            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            if ( rootItemId || layoutElement?.type === 'OVERVIEW' ) {
                delete this.dynamicFunctionGlobalParameters;

                let allResolvedDataObjects : ResolvedDataObjects = {},
                    changeActionExecuted : boolean               = false;

                for ( const action of layoutElementData.actions ) {
                    if ( action.type === 'changeData' && action.function ) {
                        const actionStartTime           = this.debug ? performance.now() : 0,
                              dataObjects : DataObjects = {
                                  ...action.dataObjects
                              };

                        if ( this.debug ) {
                            console.groupCollapsed( `%c${`execute changeData action %c${action.key}%c`.asLogLine( 104 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                        }

                        let resolvedDataObjects : ResolvedDataObjects = {};

                        // resolve triggerObject by default
                        if ( dataDescriptor && dataDescriptor.type !== 'expression' ) {
                            dataObjects.triggerObject = {
                                ...dataDescriptor.dataObject,
                                dataSelectionMode: 'single',
                                parentId
                            };
                        }

                        try {
                            resolvedDataObjects = this.getResolvedDataObjects( dataObjects, actionDataContext, clonedObjectCache, clonedObjectsIdMappings, props );

                            if ( !( resolvedDataObjects.rootObject as ResolvedDataObject )?.item ) {
                                console.log( '%cexecution aborted due to missing rootObject', `color: #${debugColorScheme.error}; font-weight: bold;`, [ action, rootItemId, rootItemId ? global.data[ dataContext ].items[ rootItemId ]?.toJSON() : undefined, Object.keys( resolvedDataObjects ) ] );

                                aborted = true;
                                break;
                            }

                            let triggerValue : AttributeValue | {
                                [ parentId : number ] : number
                            } | undefined;

                            if ( ( layoutElement! as unknown as LoadTabContainerData ).type === 'TABCONTAINER' ) {
                                triggerValue = activeTabContainerIndexes[ realLayoutElementId ] ?? ( layoutElementData as unknown as TabContainerProperties ).activeTabIndex ?? 0;
                            }
                            else if ( layoutElementData.type === 'VARIABLE' ) {
                                triggerValue = elusiveData?.variables?.[ layoutElementData.variableName ];
                            }
                            else if ( resolvedDataObjects.triggerObject && !Array.isArray( resolvedDataObjects.triggerObject ) && dataDescriptor.type !== 'expression' && dataDescriptor.type !== 'static' ) {
                                let data : ObjectData | undefined = undefined;

                                if ( parentId ) {
                                    const parentObject = getParentDataObject( dataDescriptor.dataObject.id, dataStructure );

                                    if ( parentObject ) {
                                        const subDataStructure = getDataStructure( parentObject.id, dataStructure );

                                        if ( subDataStructure ) {
                                            data = dataUtils.getData(
                                                dataDescriptor,
                                                subDataStructure,
                                                dataContext,
                                                parentId
                                            )
                                        }
                                    }
                                }
                                else {
                                    data = dataUtils.getData(
                                        dataDescriptor,
                                        dataStructure,
                                        dataContext,
                                        rootItemId
                                    );
                                }

                                if ( data?.[ 0 ] && data[ 0 ].type !== 'propertySet' && data[ 0 ].type !== 'referencePropertySet' ) {
                                    triggerValue = dataUtils.getValueFromData( dataDescriptor, data[ 0 ] ) as AttributeValue | undefined;
                                }
                            }

                            // merge in newly added items and references
                            for ( const previouslyResolvedDataObjectKey of Object.keys( allResolvedDataObjects ) ) {
                                if ( resolvedDataObjects[ previouslyResolvedDataObjectKey ] ) {
                                    resolvedDataObjects[ previouslyResolvedDataObjectKey ] = allResolvedDataObjects[ previouslyResolvedDataObjectKey ];
                                }
                            }

                            await this.buildDynamicFunction(
                                {
                                    ...this.getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects ),
                                    ...getResolvedConfigs( action.configs ?? {} ),
                                    trigger: {
                                        id       : layoutElementData.id,
                                        key      : layoutElementData.key,
                                        name     : layoutElementData.name,
                                        type     : layoutElementData.type ?? layoutElement?.type,
                                        eventType: layoutElementData.type === 'BUTTON' ? 'click' : 'change',
                                        value    : triggerValue,
                                        event    : originalEvent,
                                        parentId
                                    },
                                    treeVariables
                                },
                                action.function,
                                resolvedDataObjects,
                                props,
                                originalEvent
                            )();

                            changeActionExecuted = true;
                        }
                        catch ( e ) {
                            console.error( e, {
                                function           : action.function,
                                resolvedDataObjects: Object.keys( resolvedDataObjects )
                            } );

                            const errorTitle  = `<span class="yn-error yn-confirmpopup-title">${__(
                                      'YN.base.form.executeAction.error.title',
                                      {
                                          id  : String( action.id ) ?? '',
                                          key : action.key ?? `order: ${action.order}`,
                                          name: action.name ?? ''
                                      }
                                  )}</span>`,
                                  errorDetail = this.getDynamicFunctionErrorDetail( e, action.function );

                            // wait on error
                            await new Promise( ( resolve ) => {
                                confirmPopup( {
                                    target         : originalEvent.currentTarget,
                                    content        : '',
                                    message        : parse( `${errorTitle}<br /><br />${errorDetail}` ),
                                    icon           : 'pi pi-exclamation-triangle yn-error',
                                    acceptLabel    : __( 'YN.base.common.ok' ),
                                    acceptClassName: 'p-button p-button-outlined',
                                    rejectClassName: 'hidden',
                                    accept         : () => {
                                        resolve( true );
                                    }
                                } );
                            } );

                            aborted = true;

                            if ( this.debug ) {
                                console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                            }

                            break;
                        }

                        allResolvedDataObjects = {
                            ...allResolvedDataObjects,
                            ...resolvedDataObjects
                        };

                        if ( this.debug ) {
                            console.log( `%c${Math.round( 1000 * ( performance.now() - actionStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `font-weight: bold;`, 'font-weight: unset;' );
                            console.groupEnd();
                        }
                    }
                    else if ( [ 'backendRequest', 'fileDownload', 'sendMail' ].includes( action.type ) ) {
                        const actionStartTime = this.debug ? performance.now() : 0;

                        if ( this.debug ) {
                            console.groupCollapsed( `%c${`execute ${action.type} action %c${action.key}%c`.asLogLine( 104 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                        }

                        try {
                            const requestParams : RequestParams = {};
                            let res : AxiosResponse | null;

                            switch ( action.type ) {

                                // TODO: map as async HelperFunction (backendRequest)
                                case 'backendRequest':
                                case 'fileDownload':
                                case 'sendMail':
                                    if ( action.requestParams ) {
                                        const requestParamsKeys = Object.keys( action.requestParams ) as string[];

                                        // TODO: add variables

                                        let resolvedDataObjects : ResolvedDataObjects = {};

                                        try {
                                            resolvedDataObjects = this.getResolvedDataObjects( action.dataObjects, `fileDownload_action_${layoutElementId}`, undefined, undefined, { ...props, ...itemIdProps } );
                                        }
                                        catch ( e ) {
                                            console.error( e );
                                        }

                                        if ( ( resolvedDataObjects.rootObject as ResolvedDataObject )?.item ) {
                                            for ( const key of requestParamsKeys ) {
                                                const value = action.requestParams[ key ];

                                                let resolvedParameterValue;

                                                try {
                                                    resolvedParameterValue = ( await this.buildDynamicFunction(
                                                        resolvedDataObjects,
                                                        `return ${value};`,
                                                        resolvedDataObjects
                                                    )() );
                                                }
                                                catch ( e ) {
                                                    console.log( `%cWarning: request parameter '${key}' couldn't be resolved dynamically, set to raw value ('${value}') instead`, `color: #${debugColorScheme.error}; font-weight: bold;` );
                                                    resolvedParameterValue = value;
                                                }

                                                if ( requestParamKeys.includes( key ) && key !== 'parameters' ) {
                                                    requestParams[ key as keyof RequestParams ] = resolvedParameterValue;
                                                }
                                                else {
                                                    if ( !requestParams.parameters ) {
                                                        requestParams.parameters = {};
                                                    }
                                                    requestParams.parameters[ key ] = resolvedParameterValue;
                                                }
                                            }
                                        }
                                    }

                                    res = await xhr.post(
                                        `${BASE_URL}/${action.requestModule !== 'logout' ? 'api/' : ''}${action.requestModule}${action.requestCommand ? '/' + action.requestCommand : ''}`,
                                        requestParams ?? {},
                                        action.type === 'fileDownload'
                                            ? {
                                                responseType   : 'blob',
                                                withCredentials: true
                                            }
                                            : undefined
                                    );

                                    if ( res ) {
                                        if ( action.type === 'fileDownload' ) {
                                            // TODO: map as HelperFunction
                                            fileDownload( res.data, requestParams.parameters?.fileName as string ?? 'document', undefined, res.headers[ 'content-type' ], !requestParams.parameters?.fileName );
                                        }
                                        else {
                                            // TODO: implement response handling (handover response to next action? Shouldn't we be able to run code before and after running the request?)
                                            if ( this.debug ) {
                                                console.log( '%cWarning: unhandled backend request response:', `color: #${debugColorScheme.error}; font-weight: bold;`, [ res ] );
                                            }
                                        }
                                    }

                                    break;
                            }
                        }
                        catch ( e ) {
                            console.error( e );

                            // wait on error
                            await new Promise( ( resolve ) => {
                                confirmPopup( {
                                    target         : originalEvent.currentTarget,
                                    content        : '',
                                    message        : parse( `<span class="yn-error yn-confirmpopup-title">${__(
                                        'YN.base.form.executeActionError.title',
                                        {
                                            id  : String( action.id ) ?? '',
                                            key : action.key ?? `order: ${action.order}`,
                                            name: action.name ?? ''
                                        }
                                    )}</span><br /><br />${e instanceof Error ? e.toHTML() : String( e )}` ),
                                    icon           : 'pi pi-exclamation-triangle yn-error',
                                    acceptLabel    : __( 'YN.base.common.ok' ),
                                    acceptClassName: 'p-button p-button-outlined',
                                    rejectClassName: 'hidden',
                                    accept         : () => {
                                        resolve( true );
                                    }
                                } );
                            } );

                            aborted = true;

                            if ( this.debug ) {
                                console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                            }

                            break;
                        }

                        if ( this.debug ) {
                            console.log( `%c${Math.round( 1000 * ( performance.now() - actionStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `font-weight: bold;`, 'font-weight: unset;' );
                            console.groupEnd();
                        }
                    }
                }

                // apply modifications
                if ( !aborted && changeActionExecuted ) {
                    const isDeferred = this.deferredRender;

                    if ( !isDeferred ) {
                        this.deferRender();
                    }

                    try {
                        if ( treeVariables ) {
                            this.props.treeSetVariables?.( treeVariables );
                        }
                        this.applyDynamicFunctionVariablesChanges( props );
                        this.applyDynamicFunctionDataChanges( actionDataContext, allResolvedDataObjects, clonedObjectsIdMappings, props );
                    }
                    catch ( e ) {
                        console.error( e );
                    }

                    // force render
                    if ( !isDeferred && this.deferredRender ) {
                        setTimeout( () => {
                            this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                        }, 5 );
                    }
                }

                // cleanup
                delete global.data[ actionDataContext ];

                // execute reset and save actions
                if ( !aborted ) {
                    for ( const action of layoutElementData.actions! ) {
                        if ( [ 'reset', 'save' ].includes( action.type ) ) {
                            const actionStartTime = this.debug ? performance.now() : 0;

                            if ( this.debug ) {
                                console.groupCollapsed( `%c${`execute ${action.type} action %c${action.key}%c`.asLogLine( 104 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                            }

                            try {
                                switch ( action.type ) {
                                    // TODO: map as async HelperFunction
                                    case 'reset':
                                        // data modified?
                                        if ( rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema ) ) {
                                            await new Promise( ( resolve ) => {
                                                confirmPopup( {
                                                    target         : originalEvent.currentTarget,
                                                    content        : '',
                                                    message        : parse( `<span class="yn-warning yn-confirmpopup-title">${__( 'YN.base.form.confirmReload.title' )}</span><br /><br />${__( 'YN.base.form.confirmReload.text' )}` ),
                                                    icon           : 'pi pi-exclamation-triangle yn-warning',
                                                    acceptLabel    : __( 'YN.base.common.yes' ),
                                                    acceptClassName: 'p-button p-button-outlined',
                                                    rejectLabel    : __( 'YN.base.common.no' ),
                                                    rejectClassName: 'p-button-secondary p-button-outlined mr-3',
                                                    accept         : () => {
                                                        if ( mode === 'standalone' || mode === 'standalonePopup' ) {
                                                            void this.loadData().then( () => {
                                                                resolve( true );
                                                            } );
                                                        }
                                                        else {
                                                            this.props.treeReloadData?.();
                                                            resolve( true );
                                                        }
                                                    }
                                                } );
                                            } );
                                        }
                                        else if ( mode === 'standalone' || mode === 'standalonePopup' ) {
                                            await this.loadData();
                                        }
                                        else {
                                            this.props.treeReloadData?.();
                                        }

                                        break;

                                    // TODO: map as async HelperFunction
                                    case 'save':
                                        if ( rootItemId ) {
                                            if ( elusiveData?.errorData?.count ) {
                                                await new Promise( ( resolve ) => {
                                                    confirmPopup( {
                                                        target         : originalEvent.currentTarget,
                                                        content        : '',
                                                        message        : parse( `<span class="yn-error yn-confirmpopup-title">${__( 'YN.base.form.couldNotSaveWhileErrors.title' )}</span><br /><br />${__( 'YN.base.form.couldNotSaveWhileErrors.text' )}` ),
                                                        icon           : 'pi pi-exclamation-triangle yn-error',
                                                        acceptLabel    : __( 'YN.base.common.ok' ),
                                                        acceptClassName: 'p-button p-button-outlined',
                                                        rejectClassName: 'hidden',
                                                        accept         : () => {
                                                            resolve( true );
                                                        }
                                                    } );
                                                } );
                                            }
                                            else {
                                                let response;

                                                if ( mode === 'standalone' || mode === 'standalonePopup' ) {
                                                    response = await Form.doSaveData( uid, dataContext );
                                                }
                                                else {
                                                    // use trees save method if form runs in tree-context
                                                    response = await this.props.treeDoSaveData?.();
                                                }

                                                // grab new ids to be able to use the current value in following actions
                                                if ( response !== false && response?.success ) {
                                                    let key : keyof typeof itemIdProps;

                                                    for ( key in itemIdProps ) {
                                                        if ( response.data.itemMetaDataMapping[ itemIdProps[ key ]! ] ) {
                                                            itemIdProps[ key ] = response.data.itemMetaDataMapping[ itemIdProps[ key ]! ].id;
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        break;
                                }
                            }
                            catch ( e ) {
                                console.error( e );

                                // wait on error
                                await new Promise( ( resolve ) => {
                                    confirmPopup( {
                                        target         : originalEvent.currentTarget,
                                        content        : '',
                                        message        : parse( `<span class="yn-error yn-confirmpopup-title">${__(
                                            'YN.base.form.executeActionError.title',
                                            {
                                                id  : String( action.id ) ?? '',
                                                key : action.key ?? `order: ${action.order}`,
                                                name: action.name ?? ''
                                            }
                                        )}</span><br /><br />${e instanceof Error ? e.toHTML() : String( e )}` ),
                                        icon           : 'pi pi-exclamation-triangle yn-error',
                                        acceptLabel    : __( 'YN.base.common.ok' ),
                                        acceptClassName: 'p-button p-button-outlined',
                                        rejectClassName: 'hidden',
                                        accept         : () => {
                                            resolve( true );
                                        }
                                    } );
                                } );

                                aborted = true;

                                if ( this.debug ) {
                                    console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                                }

                                break;
                            }

                            if ( this.debug ) {
                                console.log( `%c${Math.round( 1000 * ( performance.now() - actionStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `font-weight: bold;`, 'font-weight: unset;' );
                                console.groupEnd();
                            }
                        }
                    }
                }
            }

            if ( this.debug ) {
                console.log( `%c${Math.round( 1000 * ( performance.now() - actionsStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `font-weight: bold;`, 'font-weight: unset;' );
                console.groupEnd();
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onTriggerAction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }
    }

    async onReferenceAction( layoutElementId : number, type : 'newReferencedItem' | 'addReference' | 'deleteReference', item : Item, reference : Reference | undefined = undefined, originalEvent : any, props : AllProps = this.props ) {
        const startTime          = this.debug ? performance.now() : 0,
              {
                  dataContext,
                  rootItemId,
                  elusiveData
              }                  = props,
              layoutElementsData = elusiveData?.layoutElementMappings?.cache ?? {},
              layoutElementData  = layoutElementsData[ layoutElementId ];

        if ( this.debug ) {
            console.groupCollapsed( `%c${`%cForm - %conReferenceAction - ${layoutElementData.type}%c`.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onReferenceAction};`, 'font-weight: normal;' );
        }

        let aborted : boolean = false;

        if ( layoutElementData?.actions.length ) {
            const actionsStartTime                                  = this.debug ? performance.now() : 0,
                  actionDataContext                                 = `changeReferenceData_actions_${layoutElementId}`,
                  clonedObjectCache : ClonedObjectCache             = { items: {}, references: {}, originalStates: { items: {}, references: {} } },
                  clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} },
                  treeVariables                                     = { ...this.props.treeVariables };

            if ( this.debug ) {
                console.groupCollapsed( `%c${'%cexecute actions%c'.asLogLine( 104 )}`, `font-weight: normal;`, `font-weight: bold;`, 'font-weight: normal;' );
            }

            // first execute only dataChange actions
            if ( rootItemId ) {
                delete this.dynamicFunctionGlobalParameters;

                for ( const action of layoutElementData.actions! ) {
                    if ( action.type === 'changeReferenceData' && action.function ) {
                        const actionStartTime           = this.debug ? performance.now() : 0,
                              dataObjects : DataObjects = {
                                  ...action.dataObjects
                              };

                        if ( this.debug ) {
                            console.groupCollapsed( `%c${`execute changeReferenceData action %c${action.key}%c`.asLogLine( 104 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                        }

                        let resolvedDataObjects : ResolvedDataObjects = {};

                        try {
                            resolvedDataObjects = this.getResolvedDataObjects( dataObjects, actionDataContext, clonedObjectCache, clonedObjectsIdMappings, props );

                            if ( !( resolvedDataObjects.rootObject as ResolvedDataObject )?.item ) {
                                console.log( '%cexecution aborted due to missing rootObject', `color: #${debugColorScheme.error}; font-weight: bold;`, [ action, rootItemId, rootItemId ? global.data[ dataContext ].items[ rootItemId ]?.toJSON() : undefined, Object.keys( resolvedDataObjects ) ] );

                                aborted = true;
                                break;
                            }

                            // add new item
                            resolvedDataObjects.triggerObject = {
                                item,
                                property         : new PropertyResolver( item ),
                                reference,
                                referenceProperty: reference ? new ReferencePropertyResolver( reference ) : undefined
                            };

                            // TODO: add triggerObjectInfo

                            await this.buildDynamicFunction(
                                {
                                    ...this.getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects ),
                                    ...getResolvedConfigs( action.configs ?? {} ),
                                    trigger: {
                                        id       : layoutElementData.id,
                                        key      : layoutElementData.key,
                                        name     : layoutElementData.name,
                                        type     : layoutElementData.type,
                                        eventType: type
                                    },
                                    treeVariables
                                },
                                action.function,
                                resolvedDataObjects
                            )();
                        }
                        catch ( e ) {
                            console.error( e, {
                                function           : action.function,
                                resolvedDataObjects: Object.keys( resolvedDataObjects )
                            } );

                            const errorTitle  = `<span class="yn-error yn-confirmpopup-title">${__(
                                      'YN.base.form.executeAction.error.title',
                                      {
                                          id  : String( action.id ) ?? '',
                                          key : action.key ?? `order: ${action.order}`,
                                          name: action.name ?? ''
                                      }
                                  )}</span>`,
                                  errorDetail = this.getDynamicFunctionErrorDetail( e, action.function );

                            // wait on error
                            await new Promise( ( resolve ) => {
                                confirmPopup( {
                                    target         : originalEvent.currentTarget,
                                    content        : '',
                                    message        : parse( `${errorTitle}<br /><br />${errorDetail}` ),
                                    icon           : 'pi pi-exclamation-triangle yn-error',
                                    acceptLabel    : __( 'YN.base.common.ok' ),
                                    acceptClassName: 'p-button p-button-outlined',
                                    rejectClassName: 'hidden',
                                    accept         : () => {
                                        resolve( true );
                                    }
                                } );
                            } );

                            aborted = true;

                            if ( this.debug ) {
                                console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                            }

                            break;
                        }

                        if ( this.debug ) {
                            console.log( `%c${Math.round( 1000 * ( performance.now() - actionStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `font-weight: bold;`, 'font-weight: unset;' );
                            console.groupEnd();
                        }
                    }
                }

                // apply modifications
                if ( !aborted ) {
                    try {
                        if ( treeVariables ) {
                            this.props.treeSetVariables?.( treeVariables );
                        }
                        this.applyDynamicFunctionVariablesChanges( props );
                    }
                    catch ( e ) {
                        console.error( e );
                    }
                }

                // cleanup
                delete global.data[ actionDataContext ];
            }

            if ( this.debug ) {
                console.log( `%c${Math.round( 1000 * ( performance.now() - actionsStartTime ) ) / 1000} ms%c`.asLogLine( 104 ), `font-weight: bold;`, 'font-weight: unset;' );
                console.groupEnd();
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onReferenceAction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }
    }

    async onGetDataAction( realLayoutElementId : number | string, getDataConfig : {
        [ parameter : string ] : any
    }, props : AllProps = this.props ) {
        const startTime          = this.debug ? performance.now() : 0,
              {
                  dataContext,
                  rootItemId,
                  elusiveData
              }                  = props,
              {
                  layoutElementId
              }                  = this.getLayoutElementIdAndParentIdFromRealLayoutElementId( realLayoutElementId ),
              layoutElementsData = elusiveData?.layoutElementMappings?.cache ?? {},
              layoutElementData  = layoutElementsData[ layoutElementId ] as ReferenceFieldProperties | StaticContentFieldProperties;

        if ( this.debug ) {
            console.groupCollapsed( `%c${`%cForm - %conGetDataAction - ${layoutElementData.type}%c`.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onGetDataAction};`, 'font-weight: normal;' );
        }

        let retVal : any | undefined,
            aborted : boolean = false;

        if ( layoutElementData?.getDataAction ) {
            const action                                            = layoutElementData.getDataAction,
                  actionDataContext                                 = `getData_action_${layoutElementId}`,
                  dataObjects : DataObjects                         = {
                      ...action.dataObjects
                  },
                  clonedObjectCache : ClonedObjectCache             = { items: {}, references: {}, originalStates: { items: {}, references: {} } },
                  clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} },
                  treeVariables                                     = { ...this.props.treeVariables };

            let resolvedDataObjects : ResolvedDataObjects = {};

            // first execute only dataChange actions
            if ( rootItemId && action.type === 'changeData' && action.function ) {
                delete this.dynamicFunctionGlobalParameters;

                try {
                    resolvedDataObjects = this.getResolvedDataObjects( dataObjects, actionDataContext, clonedObjectCache, clonedObjectsIdMappings, props );

                    if ( !( resolvedDataObjects.rootObject as ResolvedDataObject )?.item ) {
                        console.log( '%cexecution aborted due to missing rootObject', `color: #${debugColorScheme.error}; font-weight: bold;`, [ action, rootItemId, rootItemId ? global.data[ dataContext ].items[ rootItemId ]?.toJSON() : undefined, Object.keys( resolvedDataObjects ) ] );

                        aborted = true;
                    }
                    else {
                        retVal = await this.buildDynamicFunction(
                            {
                                ...this.getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects ),
                                ...getResolvedConfigs( action.configs ?? {} ),
                                trigger: {
                                    id       : layoutElementData.id,
                                    key      : layoutElementData.key,
                                    name     : layoutElementData.name,
                                    type     : layoutElementData.type,
                                    eventType: 'getData'
                                },
                                treeVariables,
                                getDataConfig
                            },
                            action.function,
                            resolvedDataObjects
                        )();
                    }
                }
                catch ( e ) {
                    console.error( e, {
                        function           : action.function,
                        resolvedDataObjects: Object.keys( resolvedDataObjects )
                    } );

                    const errorTitle  = `<span class="yn-error yn-confirmpopup-title">${__(
                              'YN.base.form.executeAction.error.title',
                              {
                                  id  : String( action.id ) ?? '',
                                  key : action.key ?? `order: ${action.order}`,
                                  name: action.name ?? ''
                              }
                          )}</span>`,
                          errorDetail = this.getDynamicFunctionErrorDetail( e, action.function );

                    // wait on error
                    await new Promise( ( resolve ) => {
                        confirmPopup( {
                            content        : '',
                            message        : parse( `${errorTitle}<br /><br />${errorDetail}` ),
                            icon           : 'pi pi-exclamation-triangle yn-error',
                            acceptLabel    : __( 'YN.base.common.ok' ),
                            acceptClassName: 'p-button p-button-outlined',
                            rejectClassName: 'hidden',
                            accept         : () => {
                                resolve( true );
                            }
                        } );
                    } );

                    aborted = true;

                    if ( this.debug ) {
                        console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                    }
                }


                // apply modifications
                if ( !aborted ) {
                    try {
                        if ( treeVariables ) {
                            this.props.treeSetVariables?.( treeVariables );
                        }
                        this.applyDynamicFunctionVariablesChanges( props );
                    }
                    catch ( e ) {
                        console.error( e );
                    }
                }

                // cleanup
                delete global.data[ actionDataContext ];
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onGetDataAction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    onScheduledAction( action : Action ) {
        void this.onScheduledActionAsync( action );
    }

    async onScheduledActionAsync( action : Action ) {
        const startTime                                         = this.debug ? performance.now() : 0,
              {
                  dataContext,
                  rootItemId,
                  layoutStructure,
                  elusiveData
              }                                                 = this.props,
              layoutElementData                                 = elusiveData?.layoutElementMappings?.cache?.[ layoutStructure.layoutElementId ],
              actionDataContext                                 = `scheduled_action_${action.id}`,
              dataObjects : DataObjects                         = {
                  ...action.dataObjects
              },
              clonedObjectCache : ClonedObjectCache             = { items: {}, references: {}, originalStates: { items: {}, references: {} } },
              clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} },
              treeVariables                                     = { ...this.props.treeVariables };

        if ( this.debug ) {
            console.groupCollapsed( `%c${`%cForm - %conScheduledAction - ${action.key}%c`.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.onScheduledAction};`, 'font-weight: normal;' );
        }

        let retVal : any | undefined,
            aborted : boolean                         = false,
            resolvedDataObjects : ResolvedDataObjects = {};

        // first execute only dataChange actions
        if ( rootItemId && action.type === 'changeData' && action.function ) {
            delete this.dynamicFunctionGlobalParameters;

            try {
                resolvedDataObjects = this.getResolvedDataObjects( dataObjects, actionDataContext, clonedObjectCache, clonedObjectsIdMappings );

                if ( !( resolvedDataObjects.rootObject as ResolvedDataObject )?.item ) {
                    console.log( '%cexecution aborted due to missing rootObject', `color: #${debugColorScheme.error}; font-weight: bold;`, [ action, rootItemId, rootItemId ? global.data[ dataContext ].items[ rootItemId ]?.toJSON() : undefined, Object.keys( resolvedDataObjects ) ] );

                    aborted = true;
                }
                else {
                    retVal = await this.buildDynamicFunction(
                        {
                            ...this.getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects ),
                            ...getResolvedConfigs( action.configs ?? {} ),
                            trigger: {
                                id       : layoutElementData.id,
                                key      : layoutElementData.key,
                                name     : layoutElementData.name,
                                type     : 'FORM',
                                eventType: 'scheduled'
                            },
                            treeVariables
                        },
                        action.function,
                        resolvedDataObjects
                    )();
                }
            }
            catch ( e ) {
                console.error( e, {
                    function           : action.function,
                    resolvedDataObjects: Object.keys( resolvedDataObjects )
                } );

                const errorTitle  = `<span class="yn-error yn-confirmpopup-title">${__(
                          'YN.base.form.executeAction.error.title',
                          {
                              id  : String( action.id ) ?? '',
                              key : action.key ?? `order: ${action.order}`,
                              name: action.name ?? ''
                          }
                      )}</span>`,
                      errorDetail = this.getDynamicFunctionErrorDetail( e, action.function );

                // wait on error
                await new Promise( ( resolve ) => {
                    confirmPopup( {
                        content        : '',
                        message        : parse( `${errorTitle}<br /><br />${errorDetail}` ),
                        icon           : 'pi pi-exclamation-triangle yn-error',
                        acceptLabel    : __( 'YN.base.common.ok' ),
                        acceptClassName: 'p-button p-button-outlined',
                        rejectClassName: 'hidden',
                        accept         : () => {
                            resolve( true );
                        }
                    } );
                } );

                aborted = true;

                if ( this.debug ) {
                    console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                }
            }


            // apply modifications
            if ( !aborted ) {
                try {
                    if ( treeVariables ) {
                        this.props.treeSetVariables?.( treeVariables );
                    }
                    this.applyDynamicFunctionVariablesChanges();
                }
                catch ( e ) {
                    console.error( e );
                }
            }

            // cleanup
            delete global.data[ actionDataContext ];
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.onScheduledAction}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    onTabContainerTabChange( realLayoutElementId : number | string, referenceToTabIndexMapping : {
        [ referenceId : number ] : number;
    } | undefined, event : TabViewTabChangeEvent ) {
        const doFnc = async () => {
            const {
                      uid,
                      activeTabContainerIndexes
                  } = this.props;

            let tabIndex = event.index;

            // map the real index given by event to referenceId if
            // (so visible tab stays on the reference even information changing the tab sort is changed)
            if ( referenceToTabIndexMapping ) {
                for ( const index of Object.keys( referenceToTabIndexMapping ) ) {
                    if ( referenceToTabIndexMapping[ index ] === event.index ) {
                        tabIndex = index;
                        break;
                    }
                }
            }

            // use the real index as given by event for action
            await this.onTriggerAction(
                realLayoutElementId,
                event,
                {
                    ...this.props,
                    activeTabContainerIndexes: {
                        ...activeTabContainerIndexes,
                        [ realLayoutElementId ]: event.index
                    }
                }
            );

            // use the mapped index for set
            this.props.setParam(
                uid,
                'activeTabContainerIndexes',
                {
                    ...activeTabContainerIndexes,
                    [ realLayoutElementId ]: tabIndex
                }
            );
        };

        void doFnc();
    }

    onTabContainerScroll( realLayoutElementId : number | string, event : React.UIEvent<HTMLDivElement> ) {
        const {
                  uid,
                  tabContainerScrollPositions
              } = this.props;

        this.props.setParam(
            uid,
            'tabContainerScrollPositions',
            {
                ...tabContainerScrollPositions,
                [ realLayoutElementId ]: ( event.target as HTMLElement ).scrollLeft
            }
        );
    }

    /* private ****************************************************************************************************************************/

    init() {
        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cForm - %cinit%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.init};`, 'color: unset; font-weight: normal;' );
        }

        const {
                  uid,
                  treeVariables,
                  elusiveData
              }                    = this.props,
              dataVersions         = JSON.stringify( { items: elusiveData?.data?.items, references: elusiveData?.data?.references } ),
              treeVariablesChanged = objectUtils.getDiffKeys(
                  treeVariables ?? {},
                  elusiveData?.treeVariables ?? {}
              );

        let doAfterRender = true;

        document.addEventListener( 'scroll', this.debouncedGetScrollPosition, true );

        this.doSetScrollPosition = true;

        this.props.setParam( uid, 'moduleTransformer', 'form' );

        // load data from backend if...
        // - dataStructure is not present
        // - root item doesn't exist in global data context
        // - dataDescriptorMappings is missing in elusiveData (this occurs in Tree context)
        if ( this.debug ) {
            console.log( '    -> check load data' );
        }

        this.checkForLoad();

        // data versions changed
        if ( this.debug ) {
            console.log( '    -> check data versions changed' );
        }

        if ( elusiveData?.dataVersions ) {
            if ( dataVersions !== elusiveData?.dataVersions ) {
                if ( this.debug ) {
                    console.log( '        -> data versions changed' );
                }

                doAfterRender = false;

                this.props.setParam( uid, 'dataStructure', undefined );
            }
        }
        else {
            this.props.setElusiveData( uid, { dataVersions } );
        }

        // treeVariables changed
        if ( this.debug ) {
            console.log( '    -> check treeVariables changed' );
        }

        if ( treeVariablesChanged.length ) {
            if ( this.debug ) {
                console.log( '        -> treeVariables changed' );
            }

            doAfterRender = false;

            this.props.setElusiveData( uid, { treeVariables } );
        }

        if ( doAfterRender ) {
            this.afterRender();
        }

        this.startScheduledActions();

        if ( this.debug ) {
            console.groupEnd();
        }
    }

    checkForLoad() {
        const {
                  dataContext,
                  dataStructure,
                  elusiveData
              }          = this.props,
              rootItemId = this.props.rootItemId ?? 0;

        if ( !Object.keys( dataStructure ).length || !global.data[ dataContext ]?.items?.[ rootItemId ] || !elusiveData ) {
            if ( rootItemId < 0 && !global.data[ dataContext ]?.items[ rootItemId ] ) {
                if ( this.props.onInvalidData ) {
                    if ( this.debug ) {
                        console.log( '        -> data invalid' );
                    }

                    this.props.onInvalidData();
                }
            }
            else {
                if ( this.debug ) {
                    console.log( '        -> load data' );
                }

                void this.loadData();
            }
        }
        else if ( !elusiveData?.dataDescriptorMappings || !elusiveData?.layoutElementMappings ) {
            this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
        }
    }

    deferRender() {
        this.deferredRender = true;

        if ( this.asyncShouldComponentUpdateRunning ) {
            this.cancelAsyncShouldComponentUpdate = true;
        }
    }

    cleanup() {
        document.removeEventListener( 'scroll', this.debouncedGetScrollPosition, true );

        this.debouncedGetScrollPosition.cancel();

        this.stopScheduledActions();

        this.getScrollPosition();
    }

    setScrollPosition() {
        if ( this.doSetScrollPosition ) {
            const { uid }       = this.props,
                  scrollElement = document.getElementById( `${uid}_form` )?.parentElement;

            if ( scrollElement ) {
                scrollElement.scrollTop = this.props.scrollTop;
                scrollElement.scrollLeft = this.props.scrollLeft;

                if ( ( this.props.scrollTop === 0 || scrollElement.scrollTop > 0 ) && ( this.props.scrollLeft === 0 || scrollElement.scrollLeft > 0 ) ) {
                    this.doSetScrollPosition = false;
                }
            }
        }
    }

    getScrollPosition() {
        if ( !this.doSetScrollPosition ) {
            const { uid }       = this.props,
                  scrollElement = document.getElementById( `${uid}_form` )?.parentElement;

            if ( scrollElement ) {
                this.props.setParam( uid, 'scrollTop', scrollElement.scrollTop ?? 0 );
                this.props.setParam( uid, 'scrollLeft', scrollElement.scrollLeft ?? 0 );
            }
        }
    }

    /**
     * Starts the execution of scheduled actions defined within the layout element data.
     * If any scheduled action has a defined trigger interval, it sets up an interval for execution.
     * Automatically clears any previously registered scheduled actions before setting new ones.
     *
     * @param {AllProps} [props=this.props] The properties containing layout structure and elusive data to retrieve and initiate actions from.
     * @return {void} Does not return any value.
     */
    startScheduledActions( props : AllProps = this.props ) {
        const {
                  layoutStructure,
                  elusiveData
              }                 = this.props,
              layoutElementData = elusiveData?.layoutElementMappings?.cache?.[ layoutStructure.layoutElementId ];

        this.stopScheduledActions();

        for ( const action of ( layoutElementData?.actions ?? [] ) as Action[] ) {
            if ( action.configs?.[ '::referenceProperties' ]?.triggerInterval ) {
                this.scheduledActionIntervalIds.push(
                    window.setInterval(
                        this.onScheduledAction.bind( this, action ),
                        action.configs[ '::referenceProperties' ].triggerInterval
                    )
                );
            }
            else {
                console.log( '%cstart of scheduled action failed due to missing triggerInterval', `color: #${debugColorScheme.error}; font-weight: bold;`, [ action ] );
            }
        }
    }

    /**
     * Stops all currently scheduled actions by clearing their associated intervals
     * and resetting the `scheduledActionIntervalIds` array.
     *
     * @return {void} This method does not return any value.
     */
    stopScheduledActions() {
        for ( const intervalId of this.scheduledActionIntervalIds ) {
            window.clearInterval( intervalId );
        }

        this.scheduledActionIntervalIds = [];
    }

    afterRender() {
        this.handleFieldFocus();

        this.setScrollPosition();

        this.computeGridUnitWidthAndRemWidth();

        this.checkForIntercomMessages( this.props.intercomMessages );
    }

    /**
     * Updates the scroll positions of tab container elements based on the provided scroll positions.
     * The method iterates through the keys of `tabContainerScrollPositions` and applies the corresponding
     * scroll position to the identified tab container elements in the DOM.
     *
     * @return {void} Does not return a value.
     */
    setTabContainerScrollPositions() {
        const {
                  tabContainerScrollPositions
              } = this.props;

        for ( const realLayoutElementId of ( Object.keys( tabContainerScrollPositions ) ) ) {
            const scrollElement = document.querySelector( `#leid_${realLayoutElementId}  >.p-tabview > .p-tabview-nav-container > .p-tabview-nav-content` );

            if ( scrollElement ) {
                scrollElement.scrollLeft = tabContainerScrollPositions[ realLayoutElementId ];
            }
        }
    }

    /**
     * Updates the focused element by retrieving its details and setting the related parameters.
     * If the element exists in the DOM and has a defined path, the method retrieves the element
     * within the specified path. Captures input or textarea selection details if applicable.
     *
     * @return {void} No return value.
     */
    updateFocusedElement() {
        if ( this.props.focusedElement ) {
            const {
                      uid,
                      focusedElement
                  } = this.props;

            let element = document.getElementById( focusedElement.id );

            if ( element && focusedElement.path.length ) {
                element = element.querySelector( focusedElement.path.join( ' ' ) );
            }

            if ( element ) {
                this.props.setParam( uid, 'focusedElement', {
                    ...focusedElement,

                    // could be an input or textarea so selectionStart could be valid
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    selectionStart: element.selectionStart,
                    // could be an input or textarea so selectionEnd could be valid
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    selectionEnd: element.selectionEnd
                } );
            }
        }
    }

    /**
     * Handles the focus behavior for a specific field within the component.
     * It determines the focused element, sets focus on the target element,
     * and adjusts the selection range if required. Additionally, it handles
     * autofocus for specific fields based on predefined configurations.
     *
     * @return {void} This method does not return any value.
     */
    handleFieldFocus() {
        if ( this.doSetScrollPosition ) {
            if ( this.props.focusedElement ) {
                const focusedElement = this.props.focusedElement;

                this.firstFocusSet = true;

                setTimeout(
                    () => {
                        const parentElement = document.getElementById( focusedElement.id );

                        let element : HTMLElement | null = parentElement;

                        if ( parentElement && focusedElement.path.length ) {
                            element = parentElement.querySelector( focusedElement.path.join( ' ' ) );
                        }

                        if ( element ) {
                            if ( parentElement?.classList.contains( 'p-multiselect' ) ) {
                                // open list - disable since sometimes list renders wrong (position, content)
                                window.setTimeout(
                                    () => {
                                        parentElement.click?.();
                                    },
                                    20
                                );
                                //element.focus?.( { preventScroll: true } );
                            }
                            else {
                                element.focus?.( { preventScroll: true } );
                            }

                            if ( focusedElement.selectionStart !== undefined ) {
                                // delay for masked fields that won't work otherwise
                                setTimeout( () => {
                                    try {
                                        // if selectionStart is set then setSelectionRange exists for the element (see onFormClick)
                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                        // @ts-ignore
                                        element.setSelectionRange( focusedElement.selectionStart, focusedElement.selectionEnd );
                                    }
                                    catch ( e ) {
                                        // not all elements support setSelectionRange feature so catch this up
                                    }
                                }, 100 );
                            }
                        }
                    },
                    1
                );
            }
            else if ( this.autoFocusField ) {
                const autoFocusField = this.autoFocusField;

                setTimeout(
                    () => {
                        if ( !this.firstFocusSet || document.activeElement === document.body ) {
                            const element = document.getElementsByName( autoFocusField.layoutElementName )?.[ 0 ];

                            this.firstFocusSet = true;

                            if ( element ) {
                                if ( Number( element.getAttribute( 'tabIndex' ) ?? 0 ) === autoFocusField.tabIndex ) {
                                    element.focus?.( { preventScroll: true } );
                                }
                                else {
                                    const query      = `*[tabIndex="${autoFocusField.tabIndex}"]`,
                                          tabElement = element.querySelector( query ) as HTMLElement | null;

                                    if ( tabElement ) {
                                        tabElement.focus?.( { preventScroll: true } );
                                    }
                                }
                            }
                        }
                    },
                    1
                );
            }
        }
    }

    /**
     * Computes and updates the grid unit width and rem width for a form element based on its computed styles.
     *
     * This method calculates:
     * 1. The grid unit width by dividing the form element's width by the grid size configured in the layoutStructure.
     * 2. The rem width using the form element's font size.
     *
     * Updates the state if computed values for grid unit width or rem width differ from current state values.
     *
     * @return {void} Does not return a value, but updates component state with computed grid unit width and rem width.
     */
    computeGridUnitWidthAndRemWidth() {
        const {
                  uid,
                  layoutStructure,
                  elusiveData
              }                  = this.props,
              {
                  formGridUnitWidth,
                  remWidth
              }                  = this.state,
              layoutElementsData = elusiveData?.layoutElementMappings?.cache ?? {},
              formElement        = document.getElementById( `${uid}_form` ),
              computedStyles     = formElement ? window.getComputedStyle( formElement ) : undefined;

        if ( computedStyles?.fontSize ) {
            const width                = Number( computedStyles.width.substring( 0, computedStyles.width.length - 2 ) ),
                  fontSize             = Number( computedStyles.fontSize.substring( 0, computedStyles.fontSize.length - 2 ) ),
                  gridSize             = layoutStructure ? layoutElementsData[ layoutStructure.layoutElementId ]?.gridSize ?? 96 : 96,
                  newFormGridUnitWidth = isNaN( width / gridSize ) ? 0 : width / gridSize;

            if ( newFormGridUnitWidth !== formGridUnitWidth ) {
                this.setState( { formGridUnitWidth: newFormGridUnitWidth } );
            }

            if ( fontSize !== remWidth ) {
                this.setState( { remWidth: fontSize } );
            }
        }
    }

    /**
     * Checks if there are intercom messages.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for notifications
        if ( intercomMessages?.notification ) {
            for ( const notification of Object.keys( intercomMessages.notification ) ) {
                // clear notification message
                this.props.unsetMessage( this.props.uid, 'notification', notification as string );
            }
        }
    }

    /**
     * Resolves a message string or message object, replacing template placeholders with
     * the appropriate values derived from resolved data objects and additional resolver parameters.
     *
     * @param {string|Message} message - The message to be resolved. Can be a string or a Message object containing a template and data object references.
     * @param {ResolvedDataObjectChain} [resolvedDataObjects={}] - An object chain containing resolved data objects used for template replacements in the message.
     * @param {{ [p: string]: any }} [additionalResolverParameters={}] - Additional parameters and data used for resolving template placeholders in the message.
     * @param {number} [parentItemId] - Optional ID of the parent item used for additional context during data resolution.
     * @return {string} The resolved message string, with all placeholders replaced by their corresponding values or an empty string if resolution fails.
     */
    resolveMessage( message : string | Message, resolvedDataObjects : ResolvedDataObjectChain = {}, additionalResolverParameters : {
        [ p : string ] : any
    }                                                                                         = {}, parentItemId? : number ) {
        const {
                  configHash,
                  dataContext,
                  dataStructure,
                  elusiveData,
                  rootItemId,
                  rootItemKey,
                  rootItemType,
                  treeVariables,
                  viewKey
              } = this.props;

        resolvedDataObjects = { ...resolvedDataObjects };
        additionalResolverParameters = { ...additionalResolverParameters };

        if ( !resolvedDataObjects.rootObject ) {
            const rootItem = global.data[ dataContext ].items[ rootItemId ?? 0 ];

            resolvedDataObjects.rootObject = {
                item: rootItem
            };
        }

        if (
            !additionalResolverParameters.variables
            || !additionalResolverParameters.treeVariables
            || !viewKey
            || !rootItemId
            || !rootItemKey
            || !rootItemType
            || !configHash
        ) {
            additionalResolverParameters = { ...additionalResolverParameters };

            if ( !additionalResolverParameters.variables ) {
                const variables = elusiveData?.variables ?? {};

                additionalResolverParameters.variables = {
                    ...variables,
                    [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                        // toString
                        if ( hint === 'string' ) {
                            return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                        }
                        // all other
                        else {
                            return null;
                        }
                    }
                };
            }

            if ( !additionalResolverParameters.treeVariables ) {
                additionalResolverParameters.treeVariables = {
                    ...treeVariables,
                    [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                        // toString
                        if ( hint === 'string' ) {
                            return nl2br( JSON.stringify( treeVariables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                        }
                        // all other
                        else {
                            return null;
                        }
                    }
                };
            }

            if (
                !viewKey
                || !rootItemId
                || !rootItemKey
                || !rootItemType
                || !configHash
            ) {
                additionalResolverParameters.viewKey = viewKey;
                additionalResolverParameters.rootItemId = rootItemId;
                additionalResolverParameters.rootItemKey = rootItemKey;
                additionalResolverParameters.rootItemType = rootItemType;
                additionalResolverParameters.configHash = configHash;
            }
        }

        let messageKey : string;

        if ( typeof message !== 'string' ) {
            messageKey = message.key;

            for ( const dataObjectLabel of Object.keys( message.dataObjects ) ) {
                const dataObject         = message.dataObjects[ dataObjectLabel ],
                      resolvedDataObject = dataUtils.getItemsAndReferencesForDataObject( dataObject, dataStructure, dataContext, rootItemId, parentItemId );

                if ( resolvedDataObject ) {
                    resolvedDataObjects[ dataObjectLabel ] = {
                        item     : resolvedDataObject?.items?.[ 0 ],
                        reference: resolvedDataObject?.references?.[ 0 ]
                    };
                }
                else {
                    additionalResolverParameters[ dataObjectLabel ] = {};
                }
            }
        }
        else {
            messageKey = message;
        }

        const messageTemplateStringResolver = messageUtils.getMessageTemplateStringResolverForResolvedDataObjects(
            resolvedDataObjects.rootObject.item,
            undefined,
            resolvedDataObjects,
            additionalResolverParameters
        );

        return messageTemplateStringResolver?.( __( messageKey ).replaceAll( /[\u200B-\u200E\uFEFF]/g, '' ) ) ?? messageKey;
    }

    /**
     * Loads the form data from the backend
     *
     * @private
     */
    async loadData( reloadConfig : boolean = false ) {
        const startTime  = this.debug ? performance.now() : 0,
              {
                  uid,
                  dataContext,
                  schema,
                  status,
                  viewKey,
                  mode,
                  configHash,
                  rootItemId,
                  rootItemKey,
                  rootItemType,
                  elusiveData
              }          = this.props,
              isDeferred = this.deferredRender;

        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cForm - %cloadData%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.loadData};`, 'font-weight: normal;' );
        }

        if ( !isDeferred ) {
            this.deferRender();
        }

        // masking
        this.props.startRequest( () => {
        }, uid, 'processing' );

        const processedRequestParams = Form.resolveRequestParameters(
            { verificationHash: '${requestParameters.hash}' },
            schema,
            dataContext,
            rootItemId!,
            elusiveData?.variables,
            {
                viewKey,
                rootItemId,
                rootItemKey,
                rootItemType,
                configHash
            }
        );

        try {
            await this.props.loadData(
                uid,
                dataContext,
                schema,
                status,
                viewKey,
                ( mode === 'standalone' || mode === 'standalonePopup' ) ? 'override' : 'keepExisting',
                !reloadConfig && configHash !== '' ? configHash : undefined,
                rootItemId,
                rootItemType,
                rootItemKey,
                'form',
                'loadData',
                processedRequestParams,
                ( result ) => {
                    let rootItem : Item | undefined;

                    if ( result.data.rootItemId ) {
                        rootItem = global.data[ dataContext ]?.items?.[ result.data.rootItemId ];
                    }
                    else if ( !rootItemId ) {
                        rootItem = dataUtils.findItem( dataContext, rootItemType ?? '', rootItemKey ?? '' );
                    }
                    else if ( rootItemId ) {
                        rootItem = global.data[ dataContext ]?.items?.[ rootItemId ];
                    }

                    if ( rootItem ) {
                        this.props.setParams( uid, {
                            rootItemId  : rootItem.id,
                            rootItemKey : rootItem.key,
                            rootItemType: rootItem.itemType.name
                        } );
                    }

                    if ( ( reloadConfig || configHash === undefined ) ) {
                        this.props.onConfigLoad?.( result as LoadConfigResult, reloadConfig );
                    }

                    this.props.onDataLoad?.( result );
                }
            );
        }
        catch ( e ) {
            console.error( e );
        }

        // force render if deferred
        if ( !isDeferred && this.deferredRender ) {
            setTimeout( () => {
                this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
            }, 5 );
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.loadData}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }
    }

    /**
     * Builds the dataDescriptor to metaModel mapping.
     *
     * memoizable
     *
     * @param dataDescriptors
     * @param dataContext
     * @param schema
     * @param dataStructure
     * @param rootItemId
     * @param user
     *
     * @private
     */
    buildDataDescriptorToMetaModelMapping( dataDescriptors : DataDescriptors, dataContext : string, schema : Schema, dataStructure : DataStructure, rootItemId : number, user? : User ) : DataDescriptorToDataAndMetaModelMapping | undefined {
        const startTime                                        = this.debug ? performance.now() : 0,
              retVal : DataDescriptorToDataAndMetaModelMapping = {},
              dataDescriptorKeys                               = Object.keys( dataDescriptors ) as string[];

        for ( const dataDescriptorAlias of dataDescriptorKeys ) {
            const dataDescriptor = dataDescriptors[ dataDescriptorAlias ];

            if ( dataDescriptor.type !== 'expression' && dataDescriptor.type !== 'static' && dataDescriptor.type !== 'items' && dataDescriptor.type !== 'properties' && dataDescriptor.type !== 'references' && dataDescriptor.type !== 'referenceProperties' ) {
                const metaModelData = dataUtils.getMetaModelData( dataDescriptor, dataStructure, schema, dataContext, rootItemId );

                if ( metaModelData?.length ) {
                    let type : DataAndMetaModelMapping[ 'type' ] = dataDescriptor.type,
                        isSettable : boolean                     = false;

                    if ( dataDescriptor.type === 'property' && ( metaModelData[ 0 ].object as PropertySet )?.isEnumerated ) {
                        type = 'enumProperty';
                    }
                    else if ( dataDescriptor.type === 'referenceProperty' && ( metaModelData[ 0 ].object as ReferencePropertySet )?.isEnumerated ) {
                        type = 'enumReferenceProperty';
                    }

                    if ( dataDescriptor?.type === 'item' ) {
                        metaModelData[ 0 ].object = metaModelData[ 0 ].object as ItemType;

                        isSettable = itemOnlyAttributesSettable.includes( dataDescriptor.attribute )
                            && !( dataDescriptor.attribute === 'key' && metaModelData[ 0 ].object.isKeyAutogenerated )
                            && !!user?.hasPermissionOnItemType( 'update', metaModelData[ 0 ].object, schema );
                    }
                    else if ( dataDescriptor?.type === 'property' ) {
                        metaModelData[ 0 ].object = metaModelData[ 0 ].object as PropertySet;

                        isSettable = propertyAttributesSettable.includes( dataDescriptor.attribute )
                            && !!user?.hasPermissionOnPropertySet( 'update', metaModelData[ 0 ].object, schema );
                    }
                    else if ( dataDescriptor?.type === 'reference' ) {
                        metaModelData[ 0 ].object = metaModelData[ 0 ].object as ReferenceSet;

                        isSettable = referenceOnlyAttributesSettable.includes( dataDescriptor.attribute )
                            && !!user?.hasPermissionOnReferenceSet( 'update', metaModelData[ 0 ].object, schema );
                    }
                    else if ( dataDescriptor?.type === 'referenceProperty' ) {
                        metaModelData[ 0 ].object = metaModelData[ 0 ].object as ReferencePropertySet;

                        isSettable = referencePropertyAttributesSettable.includes( dataDescriptor.attribute )
                            && !!user?.hasPermissionOnReferencePropertySet( 'update', metaModelData[ 0 ].object, schema );
                    }

                    // type static is always excluded by if condition
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    retVal[ dataDescriptorAlias ] = {
                        type,
                        metaModels: ( metaModelData ? metaModelData.map( m => m.object.id ) : [] ) as number[],
                        isDefault : false,
                        isSettable
                    };
                }
            }
        }

        if ( this.debug ) {
            console.log( `%cbuildDataDescriptorToMetaModelMapping - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
        }

        return Object.keys( retVal ).length
            ? retVal
            : undefined;
    }

    /**
     * Builds the dataDescriptor to data mapping.
     *
     * memoizable
     *
     * @param dataDescriptors
     * @param dataDescriptorMappings
     * @param dataContext
     * @param schema
     * @param dataStructure
     * @param rootItemId
     *
     * @private
     */
    buildDataDescriptorToDataMapping( dataDescriptors : DataDescriptors, dataDescriptorMappings : DataDescriptorMappings, dataContext : string, schema : Schema, dataStructure : DataStructure, rootItemId : number ) : DataDescriptorToDataAndMetaModelMapping {
        const startTime                                        = this.debug ? performance.now() : 0,
              retVal : DataDescriptorToDataAndMetaModelMapping = { ...dataDescriptorMappings.dataAndMetaModel },
              dataDescriptorKeys                               = Object.keys( dataDescriptors ) as string[];

        for ( const dataDescriptorAlias of dataDescriptorKeys ) {
            if ( dataDescriptors[ dataDescriptorAlias ].type !== 'expression' && dataDescriptors[ dataDescriptorAlias ].type !== 'static' ) {
                const dataDescriptor = dataDescriptors[ dataDescriptorAlias ] as ObjectDataDescriptor;

                if ( retVal[ dataDescriptorAlias ] && retVal[ dataDescriptorAlias ].type !== 'static' ) {
                    retVal[ dataDescriptorAlias ] = { ...retVal[ dataDescriptorAlias ] };

                    if ( retVal[ dataDescriptorAlias ].type === 'item' || retVal[ dataDescriptorAlias ].type === 'reference' ) {
                        retVal[ dataDescriptorAlias ].objects = [];
                    }
                    else {
                        retVal[ dataDescriptorAlias ].objects = {};
                    }
                    retVal[ dataDescriptorAlias ].values = {};
                    retVal[ dataDescriptorAlias ].originalValues = {};

                    const data = dataUtils.getData( dataDescriptor, dataStructure, dataContext, rootItemId );

                    // extract values and original values (no originalValues for references!)
                    if ( data?.length ) {
                        for ( const d of data ) {

                            if ( d.type === 'propertySet' ) {
                                const objects                 = retVal[ dataDescriptorAlias ].objects as PropertyObject,
                                      values                  = retVal[ dataDescriptorAlias ].values as PropertyValues,
                                      itemId                  = d.itemId,
                                      itemObject              = objects[ itemId ],
                                      itemValues              = values[ itemId ],
                                      propertyTypeId          = d.object.propertyTypeId,
                                      realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, itemId );

                                let value : AttributeValue | undefined;

                                retVal[ dataDescriptorAlias ].hasNoParent = false;

                                if ( d.object.valueDefault !== undefined && d.object.valueDefault !== '' ) {
                                    value = d.object.valueDefault;

                                    retVal[ dataDescriptorAlias ].isDefault = true;
                                }
                                else {
                                    retVal[ dataDescriptorAlias ].isDefault = false;
                                }

                                if ( itemObject ) {
                                    itemObject.push( propertyTypeId );
                                }
                                else {
                                    objects[ itemId ] = [ propertyTypeId ];
                                }

                                if ( value !== undefined ) {
                                    if ( itemValues ) {
                                        itemValues[ propertyTypeId ] = value;
                                    }
                                    else {
                                        values[ itemId ] = {};
                                        values[ itemId ][ propertyTypeId ] = value;
                                    }
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects       : {
                                        [ itemId ]: objects[ itemId ]
                                    },
                                    values        : retVal[ dataDescriptorAlias ].values![ itemId ]
                                        ? {
                                            [ itemId ]: retVal[ dataDescriptorAlias ].values![ itemId ]
                                        }
                                        : {},
                                    originalValues: retVal[ dataDescriptorAlias ].originalValues![ itemId ]
                                        ? {
                                            [ itemId ]: retVal[ dataDescriptorAlias ].originalValues![ itemId ]
                                        }
                                        : {}
                                } as PropertyDataAndMetaModelMapping;
                            }
                            else if ( d.type === 'property' ) {
                                const objects                 = retVal[ dataDescriptorAlias ].objects as PropertyObject,
                                      values                  = retVal[ dataDescriptorAlias ].values as PropertyValues,
                                      originalValues          = retVal[ dataDescriptorAlias ].originalValues as PropertyValues,
                                      itemId                  = ( d.object.parent as Item ).id,
                                      itemObject              = objects[ itemId ],
                                      itemValues              = values[ itemId ],
                                      itemOriginalValues      = originalValues[ itemId ],
                                      propertyTypeId          = d.object.propertySet.propertyTypeId,
                                      realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, itemId );

                                let value : AttributeValue | undefined,
                                    originalValue : AttributeValue | undefined;

                                retVal[ dataDescriptorAlias ].hasNoParent = false;

                                if ( d.object.isDeleted() && d.object.propertySet.valueDefault !== undefined && d.object.propertySet.valueDefault !== '' ) {
                                    value = d.object.propertySet.valueDefault;

                                    retVal[ dataDescriptorAlias ].isDefault = true;
                                }
                                else if ( d.object.isDeleted() ) {
                                    value = undefined;

                                    retVal[ dataDescriptorAlias ].isDefault = false;
                                }
                                else {
                                    value = dataUtils.getValueFromData( dataDescriptor, d ) as AttributeValue | undefined;

                                    retVal[ dataDescriptorAlias ].isDefault = false;
                                }

                                if (
                                    (
                                        !d.object.isDeleted()
                                        && !d.object.attributesModified.has( 'statusId' )
                                    )
                                    || (
                                        d.object.attributesModified.has( 'statusId' )
                                        && !metaModel[ schema ].getDeletedStatus().map( s => s.id ).includes( d.object.attributesModified.get( 'statusId' )! as number )
                                    )
                                ) {
                                    originalValue = dataUtils.getOriginalValueFromData( dataDescriptor, d ) as AttributeValue | undefined;
                                }

                                if ( itemObject ) {
                                    itemObject.push( propertyTypeId );
                                }
                                else {
                                    objects[ itemId ] = [ propertyTypeId ];
                                }

                                if ( value !== undefined ) {
                                    if ( itemValues ) {
                                        itemValues[ propertyTypeId ] = value;
                                    }
                                    else {
                                        values[ itemId ] = {};
                                        values[ itemId ][ propertyTypeId ] = value;
                                    }
                                }

                                if ( itemOriginalValues ) {
                                    itemOriginalValues[ propertyTypeId ] = originalValue;
                                }
                                else {
                                    originalValues[ itemId ] = {};
                                    originalValues[ itemId ][ propertyTypeId ] = originalValue;
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects       : {
                                        [ itemId ]: objects[ itemId ]
                                    },
                                    values        : retVal[ dataDescriptorAlias ].values![ itemId ]
                                        ? {
                                            [ itemId ]: retVal[ dataDescriptorAlias ].values![ itemId ]
                                        }
                                        : {},
                                    originalValues: retVal[ dataDescriptorAlias ].originalValues![ itemId ]
                                        ? {
                                            [ itemId ]: retVal[ dataDescriptorAlias ].originalValues![ itemId ]
                                        }
                                        : {}
                                } as PropertyDataAndMetaModelMapping;
                            }
                            else if ( d.type === 'enumProperty' ) {
                                const objects                         = retVal[ dataDescriptorAlias ].objects as EnumPropertyObject,
                                      values                          = retVal[ dataDescriptorAlias ].values as EnumPropertyValues,
                                      originalValues                  = retVal[ dataDescriptorAlias ].originalValues as EnumPropertyValues,
                                      itemId                          = ( d.object.parent as Item ).id,
                                      itemObject                      = objects[ itemId ],
                                      itemValues                      = values[ itemId ],
                                      itemOriginalValues              = originalValues[ itemId ],
                                      propertyTypeId                  = d.object.propertySet.propertyTypeId,
                                      propertyKeys : AttributeValue[] = [],
                                      value                           = dataUtils.getValueFromData( dataDescriptor, d ) as EnumValue | undefined,
                                      originalValue                   = dataUtils.getOriginalValueFromData( dataDescriptor, d ) as EnumValue | undefined,
                                      realDataDescriptorAlias         = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, itemId );

                                let enumPropertyObject         = itemObject?.[ propertyTypeId ],
                                    enumPropertyValues         = itemValues?.[ propertyTypeId ],
                                    enumPropertyOriginalValues = itemOriginalValues?.[ propertyTypeId ];

                                retVal[ dataDescriptorAlias ].hasNoParent = false;

                                // get only keys of non deleted properties
                                for ( const property of d.object.properties.values() ) {
                                    if ( !property.isDeleted() ) {
                                        propertyKeys.push( property.stringKey );
                                    }
                                }

                                // filter value by keys of non deleted properties
                                if ( value ) {
                                    for ( const key of Object.keys( value ) ) {
                                        if ( !propertyKeys.includes( key ) ) {
                                            delete value[ key ];
                                        }
                                    }
                                }

                                if ( enumPropertyObject ) {
                                    enumPropertyObject = propertyKeys;
                                }
                                else if ( itemObject ) {
                                    itemObject[ propertyTypeId ] = propertyKeys;
                                }
                                else {
                                    objects[ itemId ] = {};
                                    objects[ itemId ][ propertyTypeId ] = propertyKeys;
                                }

                                if ( enumPropertyValues ) {
                                    enumPropertyValues = value;
                                }
                                else if ( itemValues ) {
                                    itemValues[ propertyTypeId ] = value;
                                }
                                else {
                                    values[ itemId ] = {};
                                    values[ itemId ][ propertyTypeId ] = value;
                                }

                                if ( enumPropertyOriginalValues ) {
                                    enumPropertyOriginalValues = originalValue;
                                }
                                else if ( itemOriginalValues ) {
                                    itemOriginalValues[ propertyTypeId ] = originalValue;
                                }
                                else {
                                    originalValues[ itemId ] = {};
                                    originalValues[ itemId ][ propertyTypeId ] = originalValue;
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects       : {
                                        [ itemId ]: objects[ itemId ]
                                    },
                                    values        : retVal[ dataDescriptorAlias ].values![ itemId ]
                                        ? {
                                            [ itemId ]: retVal[ dataDescriptorAlias ].values![ itemId ]
                                        }
                                        : {},
                                    originalValues: retVal[ dataDescriptorAlias ].originalValues![ itemId ]
                                        ? {
                                            [ itemId ]: retVal[ dataDescriptorAlias ].originalValues![ itemId ]
                                        }
                                        : {}
                                } as EnumPropertyDataAndMetaModelMapping;
                            }
                            else if ( d.type === 'referencePropertySet' ) {
                                const objects                 = retVal[ dataDescriptorAlias ].objects as ReferencePropertyObject,
                                      values                  = retVal[ dataDescriptorAlias ].values as ReferencePropertyValues,
                                      referenceId             = d.referenceId,
                                      referenceObject         = objects[ referenceId ],
                                      referenceValues         = values[ referenceId ],
                                      propertyTypeId          = d.object.propertyTypeId,
                                      realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, referenceId );

                                let value : AttributeValue | undefined;

                                retVal[ dataDescriptorAlias ].hasNoParent = false;

                                if ( d.object.valueDefault !== undefined && d.object.valueDefault !== '' ) {
                                    value = d.object.valueDefault;

                                    retVal[ dataDescriptorAlias ].isDefault = true;
                                }
                                else {
                                    retVal[ dataDescriptorAlias ].isDefault = false;
                                }

                                if ( referenceObject ) {
                                    referenceObject.push( propertyTypeId );
                                }
                                else {
                                    objects[ referenceId ] = [ propertyTypeId ];
                                }

                                if ( value !== undefined ) {
                                    if ( referenceValues ) {
                                        referenceValues[ propertyTypeId ] = value;
                                    }
                                    else {
                                        values[ referenceId ] = {};
                                        values[ referenceId ][ propertyTypeId ] = value;
                                    }
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects       : {
                                        [ referenceId ]: objects[ referenceId ]
                                    },
                                    values        : retVal[ dataDescriptorAlias ].values![ referenceId ]
                                        ? {
                                            [ referenceId ]: retVal[ dataDescriptorAlias ].values![ referenceId ]
                                        }
                                        : {},
                                    originalValues: retVal[ dataDescriptorAlias ].originalValues![ referenceId ]
                                        ? {
                                            [ referenceId ]: retVal[ dataDescriptorAlias ].originalValues![ referenceId ]
                                        }
                                        : {}
                                } as ReferencePropertyDataAndMetaModelMapping;
                            }
                            else if ( d.type === 'referenceProperty' ) {
                                const objects                 = retVal[ dataDescriptorAlias ].objects as ReferencePropertyObject,
                                      values                  = retVal[ dataDescriptorAlias ].values as ReferencePropertyValues,
                                      originalValues          = retVal[ dataDescriptorAlias ].originalValues as ReferencePropertyValues,
                                      referenceId             = ( d.object.parent as Reference ).id,
                                      referenceObject         = objects[ referenceId ],
                                      referenceValues         = values[ referenceId ],
                                      referenceOriginalValues = originalValues[ referenceId ],
                                      propertyTypeId          = d.object.referencePropertySet.propertyTypeId,
                                      realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, referenceId );

                                let value : AttributeValue | undefined,
                                    originalValue : AttributeValue | undefined;

                                retVal[ dataDescriptorAlias ].hasNoParent = false;

                                if ( d.object.isDeleted() && d.object.referencePropertySet.valueDefault !== undefined && d.object.referencePropertySet.valueDefault !== '' ) {
                                    value = d.object.referencePropertySet.valueDefault;

                                    retVal[ dataDescriptorAlias ].isDefault = true;
                                }
                                else if ( d.object.isDeleted() ) {
                                    value = undefined;

                                    retVal[ dataDescriptorAlias ].isDefault = false;
                                }
                                else {
                                    value = dataUtils.getValueFromData( dataDescriptor, d ) as AttributeValue | undefined;

                                    retVal[ dataDescriptorAlias ].isDefault = false;
                                }

                                if (
                                    (
                                        !d.object.isDeleted()
                                        && !d.object.attributesModified.has( 'statusId' )
                                    )
                                    || (
                                        d.object.attributesModified.has( 'statusId' )
                                        && !metaModel[ schema ].getDeletedStatus().map( s => s.id ).includes( d.object.attributesModified.get( 'statusId' )! as number )
                                    )
                                ) {
                                    originalValue = dataUtils.getOriginalValueFromData( dataDescriptor, d ) as AttributeValue | undefined;
                                }

                                if ( referenceObject ) {
                                    referenceObject.push( propertyTypeId );
                                }
                                else {
                                    objects[ referenceId ] = [ propertyTypeId ];
                                }

                                if ( value !== undefined ) {
                                    if ( referenceValues ) {
                                        referenceValues[ propertyTypeId ] = value;
                                    }
                                    else {
                                        values[ referenceId ] = {};
                                        values[ referenceId ][ propertyTypeId ] = value;
                                    }
                                }

                                if ( referenceOriginalValues ) {
                                    referenceOriginalValues[ propertyTypeId ] = originalValue;
                                }
                                else {
                                    originalValues[ referenceId ] = {};
                                    originalValues[ referenceId ][ propertyTypeId ] = originalValue;
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects       : {
                                        [ referenceId ]: objects[ referenceId ]
                                    },
                                    values        : retVal[ dataDescriptorAlias ].values![ referenceId ]
                                        ? {
                                            [ referenceId ]: retVal[ dataDescriptorAlias ].values![ referenceId ]
                                        }
                                        : {},
                                    originalValues: retVal[ dataDescriptorAlias ].originalValues![ referenceId ]
                                        ? {
                                            [ referenceId ]: retVal[ dataDescriptorAlias ].originalValues![ referenceId ]
                                        }
                                        : {}
                                } as ReferencePropertyDataAndMetaModelMapping;
                            }
                            else if ( d.type === 'enumReferenceProperty' ) {
                                const objects                         = retVal[ dataDescriptorAlias ].objects as EnumReferencePropertyObject,
                                      values                          = retVal[ dataDescriptorAlias ].values as EnumReferencePropertyValues,
                                      originalValues                  = retVal[ dataDescriptorAlias ].originalValues as EnumReferencePropertyValues,
                                      referenceId                     = ( d.object.parent as Reference ).id,
                                      referenceObject                 = objects[ referenceId ],
                                      referenceValues                 = values[ referenceId ],
                                      referenceOriginalValues         = originalValues[ referenceId ],
                                      propertyTypeId                  = d.object.referencePropertySet.propertyTypeId,
                                      propertyKeys : AttributeValue[] = [],
                                      value                           = dataUtils.getValueFromData( dataDescriptor, d ) as EnumValue | undefined,
                                      originalValue                   = dataUtils.getOriginalValueFromData( dataDescriptor, d ) as EnumValue | undefined,
                                      realDataDescriptorAlias         = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, referenceId );

                                let enumReferencePropertyObject         = referenceObject?.[ propertyTypeId ],
                                    enumReferencePropertyValues         = referenceValues?.[ propertyTypeId ],
                                    enumReferencePropertyOriginalValues = referenceOriginalValues?.[ propertyTypeId ];

                                retVal[ dataDescriptorAlias ].hasNoParent = false;

                                // get only keys of non deleted properties
                                for ( const property of d.object.properties.values() ) {
                                    if ( !property.isDeleted() ) {
                                        propertyKeys.push( property.stringKey );
                                    }
                                }

                                // filter value by keys of non deleted properties
                                if ( value ) {
                                    for ( const key of Object.keys( value ) ) {
                                        if ( !propertyKeys.includes( key ) ) {
                                            delete value[ key ];
                                        }
                                    }
                                }

                                if ( enumReferencePropertyObject ) {
                                    enumReferencePropertyObject = propertyKeys;
                                }
                                else if ( referenceObject ) {
                                    referenceObject[ propertyTypeId ] = propertyKeys;
                                }
                                else {
                                    objects[ referenceId ] = {};
                                    objects[ referenceId ][ propertyTypeId ] = propertyKeys;
                                }

                                if ( enumReferencePropertyValues ) {
                                    enumReferencePropertyValues = value;
                                }
                                else if ( referenceValues ) {
                                    referenceValues[ propertyTypeId ] = value;
                                }
                                else {
                                    values[ referenceId ] = {};
                                    values[ referenceId ][ propertyTypeId ] = value;
                                }

                                if ( enumReferencePropertyOriginalValues ) {
                                    enumReferencePropertyOriginalValues = originalValue;
                                }
                                else if ( referenceOriginalValues ) {
                                    referenceOriginalValues[ propertyTypeId ] = originalValue;
                                }
                                else {
                                    originalValues[ referenceId ] = {};
                                    originalValues[ referenceId ][ propertyTypeId ] = originalValue;
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects       : {
                                        [ referenceId ]: objects[ referenceId ]
                                    },
                                    values        : retVal[ dataDescriptorAlias ].values![ referenceId ]
                                        ? {
                                            [ referenceId ]: retVal[ dataDescriptorAlias ].values![ referenceId ]
                                        }
                                        : {},
                                    originalValues: retVal[ dataDescriptorAlias ].originalValues![ referenceId ]
                                        ? {
                                            [ referenceId ]: retVal[ dataDescriptorAlias ].originalValues![ referenceId ]
                                        }
                                        : {}
                                } as ReferenceEnumPropertyDataAndMetaModelMapping;
                            }
                            else if ( d.type === 'reference' ) {
                                const objects                 = retVal[ dataDescriptorAlias ].objects as number[],
                                      values                  = retVal[ dataDescriptorAlias ].values as ReferenceValues,
                                      objectId                = d.object.id,
                                      value                   = dataUtils.getValueFromData( dataDescriptor, d ) as ReferenceValue | undefined,
                                      realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, objectId );

                                objects.push( objectId );

                                if ( value ) {
                                    if ( values[ objectId ] ) {
                                        if ( !Array.isArray( values[ objectId ] ) ) {
                                            values[ objectId ] = [ values[ objectId ] as ReferenceValue ];
                                        }
                                        ( values[ objectId ] as ReferenceValue[] ).concat( value );
                                    }
                                    else {
                                        values[ objectId ] = value;
                                    }
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects: [ objectId ],
                                    values : retVal[ dataDescriptorAlias ].values![ objectId ]
                                        ? {
                                            [ objectId ]: retVal[ dataDescriptorAlias ].values![ objectId ]
                                        }
                                        : {}
                                } as ReferenceDataAndMetaModelMapping;
                            }
                            // item
                            else {
                                const objects                 = retVal[ dataDescriptorAlias ].objects as number[],
                                      values                  = retVal[ dataDescriptorAlias ].values as Values,
                                      originalValues          = retVal[ dataDescriptorAlias ].originalValues as Values,
                                      objectId                = d.object.id,
                                      value                   = dataUtils.getValueFromData( dataDescriptor, d ) as AttributeValue | undefined,
                                      originalValue           = dataUtils.getOriginalValueFromData( dataDescriptor, d ) as AttributeValue | undefined,
                                      realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, objectId );

                                retVal[ dataDescriptorAlias ].hasNoParent = false;

                                objects.push( objectId );

                                if ( value ) {
                                    values[ objectId ] = value;
                                }

                                if ( originalValue ) {
                                    originalValues[ objectId ] = originalValue;
                                }

                                retVal[ realDataDescriptorAlias ] = {
                                    ...retVal[ dataDescriptorAlias ],
                                    objects       : [ objectId ],
                                    values        : retVal[ dataDescriptorAlias ].values![ objectId ]
                                        ? {
                                            [ objectId ]: retVal[ dataDescriptorAlias ].values![ objectId ]
                                        }
                                        : {},
                                    originalValues: retVal[ dataDescriptorAlias ].originalValues![ objectId ]
                                        ? {
                                            [ objectId ]: retVal[ dataDescriptorAlias ].originalValues![ objectId ]
                                        }
                                        : {}
                                } as ItemDataAndMetaModelMapping;
                            }
                        }
                    }

                    // no data - check if there is a parent
                    else {
                        if ( dataDescriptor.type === 'reference' && dataDescriptor.attribute === 'id' ) {
                            const parentDataObject = getParentDataObject( dataDescriptor.dataObject.id, dataStructure ),
                                  item             = parentDataObject
                                      ? dataUtils.getData(
                                          buildDataDescriptor( { attributePath: 'item.id' }, parentDataObject ) as ObjectDataDescriptor,
                                          dataStructure,
                                          dataContext,
                                          rootItemId
                                      )?.[ 0 ].object as Item
                                      : undefined;

                            retVal[ dataDescriptorAlias ].hasNoParent = !item;
                        }
                        else if ( retVal[ dataDescriptorAlias ].type === 'item' || retVal[ dataDescriptorAlias ].type === 'property' || retVal[ dataDescriptorAlias ].type === 'enumProperty' ) {
                            const item = dataUtils.getData(
                                buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                dataStructure,
                                dataContext,
                                rootItemId
                            )?.[ 0 ].object as Item;

                            retVal[ dataDescriptorAlias ].hasNoParent = !item;
                        }
                        else {
                            const reference = dataUtils.getData(
                                buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                dataStructure,
                                dataContext,
                                rootItemId
                            )?.[ 0 ].object as Reference;

                            retVal[ dataDescriptorAlias ].hasNoParent = !reference;
                        }
                    }

                    // get originalValues for references
                    if ( retVal[ dataDescriptorAlias ].type === 'reference' ) {
                        // resolve data with deleted items and references to resolve original paths (and references therefor)
                        const originalValueData = dataUtils.getData( dataDescriptor, dataStructure, dataContext, rootItemId, true );

                        if ( originalValueData?.length ) {
                            for ( const d of originalValueData ) {
                                if ( d.type === 'reference' ) {
                                    const originalValues          = retVal[ dataDescriptorAlias ].originalValues as ReferenceValues,
                                          objectId                = d.object.id,
                                          originalValue           = dataUtils.getOriginalValueFromData( dataDescriptor, d ) as ReferenceValue,
                                          realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, objectId );

                                    if ( retVal[ realDataDescriptorAlias ] ) {
                                        if ( originalValue?.id > 0 ) {
                                            if ( originalValues[ objectId ] ) {
                                                if ( !Array.isArray( originalValues[ objectId ] ) ) {
                                                    originalValues[ objectId ] = [ originalValues[ objectId ] as ReferenceValue ];
                                                }
                                                ( originalValues[ objectId ] as ReferenceValue[] ).concat( originalValue );
                                            }
                                            else {
                                                originalValues[ objectId ] = originalValue;
                                            }
                                        }

                                        retVal[ realDataDescriptorAlias ].originalValues = retVal[ dataDescriptorAlias ].originalValues![ objectId ]
                                            ? {
                                                [ objectId ]: retVal[ dataDescriptorAlias ].originalValues![ objectId ]
                                            } as ReferenceDataAndMetaModelMapping[ 'originalValues' ]
                                            : {};
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if ( this.debug ) {
            console.log( `%cbuildDataDescriptorToDataMapping - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 102 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
        }

        return retVal;
    }

    /**
     * Builds the dataDescriptor to value mapping.
     *
     * memoizable
     *
     * @param dataDescriptors
     * @param dataDescriptorMappings
     * @param parentId
     *
     * @private
     */
    buildDataDescriptorToValueMapping( dataDescriptors : DataDescriptors, dataDescriptorMappings : DataDescriptorMappings, parentId? : number ) : DataDescriptorToValueMapping {
        const startTime = this.debug ? performance.now() : 0;

        const retVal : DataDescriptorToValueMapping = {},
              dataDescriptorKeys                    = Object.keys( dataDescriptors ) as string[],
              dataDescriptorToDataAndMetaModel      = dataDescriptorMappings.dataAndMetaModel,
              dataDescriptorsToEditValue            = dataDescriptorMappings.editValue;

        // get values
        for ( const dataDescriptorAlias of dataDescriptorKeys ) {
            if ( dataDescriptorToDataAndMetaModel?.[ dataDescriptorAlias ] ) {
                const dataDescriptorType = dataDescriptorToDataAndMetaModel[ dataDescriptorAlias ].type;

                let parentIds : ( number | string | undefined )[] = [ undefined ];

                if ( parentId ) {
                    parentIds.push( parentId );
                }
                else if ( dataDescriptorType === 'item' || dataDescriptorType === 'reference' ) {
                    parentIds = parentIds.concat( dataDescriptorToDataAndMetaModel[ dataDescriptorAlias ].objects as number[] );
                }
                else {
                    parentIds = parentIds.concat( Object.keys( dataDescriptorToDataAndMetaModel[ dataDescriptorAlias ].objects as {
                        [ parentId : number ] : any
                    } ) );
                }

                for ( const objectId of parentIds ) {
                    const realDataDescriptorAlias = typeof objectId !== 'string'
                        ? this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, objectId )
                        : `${dataDescriptorAlias}_${objectId}`;

                    // edit value
                    if ( dataDescriptorsToEditValue?.[ realDataDescriptorAlias ] !== undefined ) {

                        // for enum properties merge edit value over value
                        if ( dataDescriptorType === 'enumProperty' || dataDescriptorType === 'enumReferenceProperty' ) {
                            const enumEditValue = dataDescriptorsToEditValue?.[ realDataDescriptorAlias ] as EnumValue;

                            let enumValue : EnumValue | undefined;

                            if ( dataDescriptorToDataAndMetaModel[ realDataDescriptorAlias ] ) {
                                enumValue = ( this.getValueFromDataAndMetaModelMapping( dataDescriptorToDataAndMetaModel[ realDataDescriptorAlias ] ) ) as EnumValue;
                            }

                            if ( enumValue ) {
                                enumValue = { ...enumValue };
                            }
                            else {
                                enumValue = {};
                            }

                            for ( const stringKey of Object.keys( enumEditValue ) ) {
                                enumValue[ stringKey ] = {
                                    ...( enumValue[ stringKey ] ?? {} ),
                                    ...enumEditValue[ stringKey ]
                                }
                            }

                            retVal[ realDataDescriptorAlias ] = enumValue;
                        }
                        else {
                            retVal[ realDataDescriptorAlias ] = dataDescriptorsToEditValue?.[ realDataDescriptorAlias ] as AttributeValue | undefined;
                        }
                    }
                    // value
                    else if ( dataDescriptorToDataAndMetaModel[ realDataDescriptorAlias ] ) {
                        retVal[ realDataDescriptorAlias ] = this.getValueFromDataAndMetaModelMapping( dataDescriptorToDataAndMetaModel[ realDataDescriptorAlias ] );
                    }
                }
            }
        }

        if ( this.debug ) {
            console.log( `%cbuildDataDescriptorToValueMapping - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 102 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
        }

        return retVal;
    }

    /**
     * Builds the dataDescriptor to originalValue mapping.
     *
     * memoizable
     *
     * @param dataDescriptors
     * @param dataDescriptorMappings
     * @param parentId
     *
     * @private
     */
    buildDataDescriptorToOriginalValueMapping( dataDescriptors : DataDescriptors, dataDescriptorMappings : DataDescriptorMappings, parentId? : number ) : DataDescriptorToValueMapping {
        const startTime = this.debug ? performance.now() : 0;

        const retVal : DataDescriptorToValueMapping = {},
              dataDescriptorKeys                    = Object.keys( dataDescriptors ) as string[],
              dataDescriptorToDataAndMetaModel      = dataDescriptorMappings.dataAndMetaModel;

        // get original values
        for ( const dataDescriptorAlias of dataDescriptorKeys ) {
            if ( dataDescriptorToDataAndMetaModel?.[ dataDescriptorAlias ] ) {
                const dataDescriptorType = dataDescriptorToDataAndMetaModel[ dataDescriptorAlias ].type;

                let parentIds : ( number | string | undefined )[] = [ undefined ];

                if ( parentId ) {
                    parentIds.push( parentId );
                }
                else if ( dataDescriptorType === 'item' || dataDescriptorType === 'reference' ) {
                    parentIds = parentIds.concat( dataDescriptorToDataAndMetaModel[ dataDescriptorAlias ].objects as number[] );
                }
                else {
                    parentIds = parentIds.concat( Object.keys( dataDescriptorToDataAndMetaModel[ dataDescriptorAlias ].objects as {
                        [ parentId : number ] : any
                    } ) );
                }

                for ( const objectId of parentIds ) {
                    const realDataDescriptorAlias = typeof objectId !== 'string'
                        ? this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, objectId )
                        : `${dataDescriptorAlias}_${objectId}`;

                    if ( dataDescriptorToDataAndMetaModel[ realDataDescriptorAlias ] !== undefined ) {
                        retVal[ realDataDescriptorAlias ] = this.getOriginalValueFromDataAndMetaModelMapping( dataDescriptorToDataAndMetaModel[ realDataDescriptorAlias ] );
                    }
                }
            }
        }

        if ( this.debug ) {
            console.log( `%cbuildDataDescriptorToOriginalValueMapping - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 102 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
        }

        return retVal;
    }

    /**
     * Builds the layoutElement to itemKeyCheck mapping.
     *
     * memoizable
     *
     * @param dataDescriptors
     * @param dataDescriptorMappings
     *
     * @private
     */
    buildDataDescriptorsToItemKeyCheckMapping( dataDescriptors : DataDescriptors, dataDescriptorMappings : DataDescriptorMappings ) : DataDescriptorToItemKeyCheckMapping | undefined {
        const startTime = this.debug ? performance.now() : 0;

        const { dataContext }                  = this.props,
              dataDescriptorKeys               = Object.keys( dataDescriptors ) as string[],
              dataDescriptorToDataAndMetaModel = dataDescriptorMappings.dataAndMetaModel;

        let retVal : DataDescriptorToItemKeyCheckMapping | undefined = {};

        for ( const dataDescriptorAlias of dataDescriptorKeys ) {
            const dataDescriptor = dataDescriptors[ dataDescriptorAlias ];

            if ( dataDescriptor?.type === 'item' && dataDescriptor.attribute === 'key' ) {
                const dataAndMetaModel = dataDescriptorToDataAndMetaModel?.[ dataDescriptorAlias ];

                if ( dataAndMetaModel?.type === 'item' && dataAndMetaModel?.objects?.length ) {
                    for ( const itemId of dataAndMetaModel.objects ) {
                        const item                    = global.data[ dataContext ].items[ itemId ],
                              realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, itemId );

                        if ( dataAndMetaModel?.objects?.length === 1 ) {
                            retVal[ dataDescriptorAlias ] = {
                                itemId        : item.id,
                                itemTypeId    : item.itemTypeId,
                                originalKey   : dataAndMetaModel?.originalValues?.[ item.id ] as string ?? '',
                                lastCheckedKey: dataAndMetaModel?.originalValues?.[ item.id ] as string ?? '',
                                isKeyUnique   : true
                            };
                        }

                        retVal[ realDataDescriptorAlias ] = {
                            itemId        : item.id,
                            itemTypeId    : item.itemTypeId,
                            originalKey   : dataAndMetaModel?.originalValues?.[ item.id ] as string ?? '',
                            lastCheckedKey: dataAndMetaModel?.originalValues?.[ item.id ] as string ?? '',
                            isKeyUnique   : true
                        };
                    }
                }
            }
        }

        if ( !Object.keys( retVal ).length ) {
            retVal = undefined;
        }

        if ( this.debug ) {
            console.log( `%cbuildDataDescriptorsToItemKeyCheckMapping - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 102 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
        }

        return retVal;
    }

    /**
     * Builds the layoutElement to referenceInsertableCheck mapping.
     *
     * memoizable
     *
     * @param dataDescriptors
     * @param dataDescriptorMappings
     *
     * @private
     */
    buildDataDescriptorsToReferenceInsertableCheckMapping( dataDescriptors : DataDescriptors, dataDescriptorMappings : DataDescriptorMappings ) : DataDescriptorToReferenceInsertableCheckMapping | undefined {
        const startTime = this.debug ? performance.now() : 0;

        const {
                  schema,
                  dataContext
              }                                = this.props,
              dataDescriptorKeys               = Object.keys( dataDescriptors ) as string[],
              dataDescriptorToDataAndMetaModel = dataDescriptorMappings.dataAndMetaModel;

        let retVal : DataDescriptorToReferenceInsertableCheckMapping | undefined = {};

        for ( const dataDescriptorAlias of dataDescriptorKeys ) {
            const dataDescriptor = dataDescriptors[ dataDescriptorAlias ];

            if ( dataDescriptor?.type === 'reference' && dataDescriptor.attribute === 'id' ) {
                const dataAndMetaModel = dataDescriptorToDataAndMetaModel?.[ dataDescriptorAlias ];

                if ( dataAndMetaModel?.type === 'reference' && dataAndMetaModel?.objects?.length ) {
                    for ( const referenceId of dataAndMetaModel.objects ) {
                        const reference               = global.data[ dataContext ].references[ referenceId ],
                              realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, referenceId );

                        if ( dataAndMetaModel?.objects?.length === 1 ) {
                            retVal[ dataDescriptorAlias ] = {
                                schema,
                                referenceSetId       : reference.referenceSetId,
                                srcItemId            : reference.srcItemId,
                                destItemId           : reference.destItemId,
                                referenceIdsToExclude: [ reference.id ],
                                isReferenceInsertable: true
                            };
                        }

                        retVal[ realDataDescriptorAlias ] = {
                            schema,
                            referenceSetId       : reference.referenceSetId,
                            srcItemId            : reference.srcItemId,
                            destItemId           : reference.destItemId,
                            referenceIdsToExclude: [ reference.id ],
                            isReferenceInsertable: true
                        };
                    }
                }
            }
        }

        if ( !Object.keys( retVal ).length ) {
            retVal = undefined;
        }

        if ( this.debug ) {
            console.log( `%cbuildDataDescriptorsToReferenceInsertableCheckMapping - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 102 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
        }

        return retVal;
    }

    /**
     * Builds the layoutElement to cache mapping.
     *
     * memoizable
     *
     * @param props
     * @param dataDescriptorMappings
     * @param justLoaded
     *
     * @private
     */
    async buildLayoutElementToCacheMapping( dataDescriptorMappings : DataDescriptorMappings, props : AllProps = this.props, justLoaded : boolean, currentLayoutElementMappings? : LayoutElementMappings ) : Promise<LayoutElementToCacheMapping> {
        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cbuildLayoutElementToCacheMapping%c'.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: normal;' );
        }

        const startTime                            = this.debug ? performance.now() : 0,
              retVal : LayoutElementToCacheMapping = {},
              {
                  uid,
                  schema,
                  dataContext,
                  rootItemId,
                  dataDescriptors,
                  dataStructure,
                  layoutElements,
                  layoutStructure,
                  readOnly
              }                                    = props,
              formLayoutElementId                  = layoutStructure.layoutElementId,
              formFunctions                        = ( layoutElements[ formLayoutElementId ] as LoadFormData )?.data?.functions,
              layoutElementKeys                    = Object.keys( layoutElements ).map( key => Number( key ) ) as number[],
              dataDescriptorToDataAndMetaModel     = dataDescriptorMappings.dataAndMetaModel,
              treeVariables                        = { ...this.props.treeVariables };

        if ( formLayoutElementId ) {
            // clone layoutElements data
            for ( const layoutElementId of layoutElementKeys ) {
                const layoutElement = layoutElements[ layoutElementId ];

                retVal[ layoutElementId ] = omniclone( layoutElement.data );

                if ( layoutElement.type === 'TABCONTAINER' ) {
                    ( retVal[ layoutElementId ] as TabContainerProperties ).activeTabIndex = layoutElement.data.activeTabIndex ?? 0;
                }
            }

            // execute functions on clone
            if ( rootItemId && formFunctions?.length ) {
                const functionsStartTime                                = this.debug ? performance.now() : 0,
                      functionDataContext                               = `changeDataAndLayout_functions`,
                      clonedObjectCache : ClonedObjectCache             = { items: {}, references: {}, originalStates: { items: {}, references: {} } },
                      clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} },
                      errorMessages : string[]                          = [];

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'%cexecute functions%c'.asLogLine( 99 )}`, `font-weight: normal;`, `font-weight: bold;`, 'font-weight: normal;' );
                }

                let allResolvedLayoutElements : ResolvedLayoutElements = {},
                    allResolvedDataObjects : ResolvedDataObjects       = {},
                    aborted : boolean                                  = false;

                delete this.dynamicFunctionGlobalParameters;

                for ( const formFunction of formFunctions ) {
                    if ( formFunction.function ) {
                        const functionStartTime = this.debug ? performance.now() : 0;

                        if ( this.debug ) {
                            console.groupCollapsed( `%c${`execute function %c${formFunction.key}%c`.asLogLine( 97 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                        }

                        let resolvedFormElements : ResolvedLayoutElements = {},
                            resolvedDataObjects : ResolvedDataObjects     = {};

                        try {
                            resolvedFormElements = Object.keys( formFunction.formElements )
                                .reduce(
                                    ( innerRetVal, label ) => {
                                        const formElement = retVal[ formFunction.formElements[ label ] ];

                                        if ( label === formElement.key ) {
                                            throw ( __( 'YN.base.form.error.noLabelForFormElement', { key: formElement.key, id: formElement.id } ) );
                                        }

                                        innerRetVal[ label ] = formElement;

                                        return innerRetVal;
                                    },
                                    {} as ResolvedLayoutElements
                                );
                            resolvedDataObjects = this.getResolvedDataObjects( formFunction.dataObjects, functionDataContext, clonedObjectCache, clonedObjectsIdMappings, props );

                            if ( !( resolvedDataObjects.rootObject as ResolvedDataObject )?.item ) {
                                console.log( '%cexecution aborted due to missing rootObject', `color: #${debugColorScheme.error}; font-weight: bold;`, [ formFunction, rootItemId, rootItemId ? global.data[ dataContext ].items[ rootItemId ]?.toJSON() : undefined, Object.keys( resolvedDataObjects ) ] );

                                aborted = true;
                                break;
                            }

                            // merge in newly added items and references
                            for ( const previouslyResolvedDataObjectKey of Object.keys( allResolvedDataObjects ) ) {
                                if ( resolvedDataObjects[ previouslyResolvedDataObjectKey ] ) {
                                    resolvedDataObjects[ previouslyResolvedDataObjectKey ] = allResolvedDataObjects[ previouslyResolvedDataObjectKey ];
                                }
                            }

                            await this.buildDynamicFunction(
                                {
                                    ...this.getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects ),
                                    ...getResolvedConfigs( formFunction.configs ?? {} ),
                                    ...resolvedFormElements,
                                    treeVariables
                                },
                                formFunction.function,
                                resolvedDataObjects
                            )();

                            allResolvedLayoutElements = {
                                ...allResolvedLayoutElements,
                                ...resolvedFormElements
                            };

                            allResolvedDataObjects = {
                                ...allResolvedDataObjects,
                                ...resolvedDataObjects
                            };
                        }
                        catch ( e ) {
                            const errorTitle = `<span class="yn-confirmpopup-title">${__(
                                'YN.base.form.executeFunction.error.detail.title',
                                {
                                    id  : String( formFunction.id ) ?? '',
                                    key : formFunction.key ?? `order: ${formFunction.order}`,
                                    name: formFunction.name ?? ''
                                }
                            )}</span>`;

                            let errorDetail : string;

                            if ( e instanceof Error ) {
                                console.error( e, {
                                    function            : formFunction.function,
                                    resolvedFormElements: Object.keys( resolvedFormElements ),
                                    resolvedDataObjects : Object.keys( resolvedDataObjects )
                                } );

                                errorDetail = this.getDynamicFunctionErrorDetail( e, formFunction.function );
                            }
                            else if ( typeof e === 'string' ) {
                                errorDetail = e;
                            }
                            else {
                                errorDetail = 'unkown error';
                            }

                            errorMessages.push( `${errorTitle}<br /><br />${errorDetail}` );

                            aborted = true;

                            if ( this.debug ) {
                                console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                            }

                            break;
                        }

                        if ( this.debug ) {
                            console.log( `%c${Math.round( 1000 * ( performance.now() - functionStartTime ) ) / 1000} ms%c`.asLogLine( 96 ), `font-weight: bold;`, 'font-weight: unset;' );
                            console.groupEnd();
                        }
                    }
                }

                // show errors
                if ( errorMessages.length ) {
                    this.setState( { dynamicFunctionErrors: errorMessages } );
                }

                // apply modifications
                if ( !aborted ) {
                    const isDeferred = this.deferredRender;

                    if ( !isDeferred ) {
                        this.deferRender();
                    }

                    try {
                        if ( treeVariables ) {
                            this.props.treeSetVariables?.( treeVariables );
                        }
                        if ( Object.values( allResolvedLayoutElements ).length ) {
                            this.applyDynamicFunctionTabIndexChanges( props, allResolvedLayoutElements );
                        }
                        this.applyDynamicFunctionVariablesChanges( props );
                        this.applyDynamicFunctionDataChanges( functionDataContext, allResolvedDataObjects, clonedObjectsIdMappings, props );
                    }
                    catch ( e ) {
                        console.error( e );
                    }

                    // force render
                    if ( !isDeferred && this.deferredRender ) {
                        setTimeout( () => {
                            this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                        }, 5 );
                    }
                }

                // cleanup
                delete global.data[ functionDataContext ];

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - functionsStartTime ) ) / 1000} ms%c`.asLogLine( 98 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            }

            // TODO: performance tuning - cache metaModel defaults for type
            // apply inheritance and processing
            // form
            retVal[ formLayoutElementId ] = {
                ...dataExtraction.getDefaultsForItemType( 'CFG', 'Form' ) as RequiredOf<FormProperties>,
                ...retVal[ formLayoutElementId ]
            };
            retVal[ formLayoutElementId ].readOnly = retVal[ formLayoutElementId ].readOnly || readOnly;
            retVal[ formLayoutElementId ].elementStyle = {
                minWidth: `calc( ${retVal[ formLayoutElementId ]?.minWidth}${widthUnitMapping[ retVal[ formLayoutElementId ].minWidthUnit as WidthUnit ]} + 1rem )`, // p-card-body padding-left: 0.5rem + p-card-body padding-right: 0.5rem
                maxWidth: `calc( ${retVal[ formLayoutElementId ]?.maxWidth}${widthUnitMapping[ retVal[ formLayoutElementId ].maxWidthUnit as WidthUnit ]} + 1rem )`  // p-card-body padding-left: 0.5rem + p-card-body padding-right: 0.5rem
            };

            // layoutStructure
            const applyInheritanceAndProcessingRecursive = ( containerChildren : ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[], inheritedProps : FormInheritedProperties | ( FormInheritedProperties & ContainerCommonInheritedProperties & ContainerToContainerInheritedProperties ) ) => {
                for ( const layoutElementStructure of containerChildren ) {
                    const layoutElementId = layoutElementStructure.layoutElementId;

                    if ( layoutElementStructure.type === 'CONTAINER' ) {
                        // TODO: performance tuning - cache metaModel defaults for type
                        retVal[ layoutElementId ] = {
                            ...dataExtraction.getDefaultsForItemType( 'CFG', 'Container' ) as RequiredOf<ContainerProperties>,
                            ..._.pick( inheritedProps, containerInheritedProperties ),
                            ...retVal[ layoutElementId ],

                            key: `${uid}_container_${layoutElementId}`
                        } as ContainerProperties;

                        const containerLayoutElement = layoutElements[ layoutElementId ] as LoadContainerData;

                        if ( containerLayoutElement.isRepeatedForDataDescriptorAlias ) {
                            const dataDescriptorAlias = containerLayoutElement.isRepeatedForDataDescriptorAlias ?? '',
                                  dataDescriptor      = dataDescriptors[ dataDescriptorAlias ],
                                  dataAndMetaModel    = dataDescriptorToDataAndMetaModel?.[ dataDescriptorAlias ] ?? {} as DataAndMetaModelMapping,
                                  referenceSet        = dataAndMetaModel?.type === 'reference' ? metaModel[ schema ].getReferenceSet( dataAndMetaModel.metaModels[ 0 ] ?? 0 ) : undefined;

                            if ( referenceSet && dataDescriptor.type === 'reference' ) {
                                let referenceDirection                  = Object.values( dataDescriptor.dataObject.reference?.referenceConfiguration ?? {} )?.[ 0 ].referenceDirection,
                                    referenceRootItemId : number        = 0,
                                    newReferenceId : number | undefined = undefined;

                                if ( referenceDirection ) {
                                    // reverse the referenceDirection from dataObject since it is seen from the referenced items dataObject to parent items dataObject (e.g. rootItem)
                                    if ( referenceDirection === 'SrcToDest' ) {
                                        referenceDirection = 'DestToSrc';
                                    }
                                    else {
                                        referenceDirection = 'SrcToDest';
                                    }
                                }
                                else if ( rootItemId && dataAndMetaModel.type === 'reference' ) {
                                    const item = dataUtils.getData(
                                        buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                        dataStructure,
                                        dataContext,
                                        rootItemId
                                    )?.[ 0 ].object as Item;

                                    if ( referenceSet.srcItemTypeId === item.itemTypeId ) {
                                        referenceDirection = 'DestToSrc';
                                    }
                                    else {
                                        referenceDirection = 'SrcToDest';
                                    }
                                }

                                if ( rootItemId ) {
                                    const parentDataObject = getParentDataObject( dataDescriptor.dataObject.id, dataStructure ),
                                          item             = parentDataObject
                                              ? dataUtils.getData(
                                                  buildDataDescriptor( { attributePath: 'item.id' }, parentDataObject ) as ObjectDataDescriptor,
                                                  dataStructure,
                                                  dataContext,
                                                  rootItemId
                                              )?.[ 0 ].object as Item
                                              : undefined;

                                    if ( item ) {
                                        referenceRootItemId = item.id;
                                    }
                                }

                                if ( dataAndMetaModel.type === 'reference' && dataAndMetaModel.objects && currentLayoutElementMappings?.cache?.[ layoutElementId ]?.repeatConfig?.referenceIds && Array.isArray( currentLayoutElementMappings?.cache?.[ layoutElementId ]?.repeatConfig?.referenceIds ) ) {
                                    for ( const referenceId of dataAndMetaModel.objects ) {
                                        if ( !currentLayoutElementMappings.cache[ layoutElementId ].repeatConfig.referenceIds.includes( referenceId ) ) {
                                            newReferenceId = referenceId;
                                            break;
                                        }
                                    }
                                }

                                retVal[ layoutElementId ].repeatConfig = {
                                    dataDescriptorAlias,
                                    rootItemId        : referenceRootItemId,
                                    referenceSetId    : referenceSet.id,
                                    referenceDirection: referenceDirection,
                                    referenceIds      : dataAndMetaModel.objects,
                                    newReferenceId
                                };
                            }
                            else {
                                // TODO: how to handle error?
                            }
                        }

                        if ( layoutElementStructure.children?.length ) {
                            applyInheritanceAndProcessingRecursive( layoutElementStructure.children, _.pick( retVal[ layoutElementId ], formFieldInheritedProperties ) as FormFieldInheritedProperties );
                        }
                    }
                    else if ( layoutElementStructure.type === 'TABCONTAINER' ) {
                        // TODO: performance tuning - cache metaModel defaults for type
                        retVal[ layoutElementId ] = {
                            ...dataExtraction.getDefaultsForItemType( 'CFG', 'TabContainer' ) as RequiredOf<TabContainerProperties>,
                            ..._.pick( inheritedProps, containerInheritedProperties ),
                            ...retVal[ layoutElementId ],

                            key: `${uid}_tabcontainer_${layoutElementId}`
                        } as TabContainerProperties;

                        if ( layoutElementStructure.children?.length ) {
                            applyInheritanceAndProcessingRecursive( layoutElementStructure.children, _.pick( retVal[ layoutElementId ], formFieldInheritedProperties ) as FormFieldInheritedProperties );
                        }
                    }
                    else {
                        // TODO: performance tuning - cache metaModel defaults for type
                        const formFieldType         = ( retVal[ layoutElementId ] as FormFieldData ).type,
                              formFieldDefaultsType = formFieldType === 'VARIABLE' ? ( retVal[ layoutElementId ] as VariableFieldProperties ).variableType : formFieldType,
                              formFieldDefaults     = dataExtraction.getDefaultsForItemType( 'CFG', fieldTypeToItemTypeMapping[ formFieldDefaultsType ] ) as RequiredOf<FormFieldProperties>

                        retVal[ layoutElementId ] = {
                            ...formFieldDefaults,
                            ..._.pick( inheritedProps, formFieldInheritedProperties ),
                            ...retVal[ layoutElementId ],

                            key: `${uid}_field-${layoutElementId}`,
                            uid: `${uid}_field-${layoutElementId}`,

                            // use FormField.key as input.name
                            name: retVal[ layoutElementId ].key

                        } as FormFieldProperties;

                        const layoutElement                                              = layoutElements[ layoutElementId ] as FormFieldData,
                              {
                                  readOnly: layoutElementReadonly,
                                  type,
                                  images,
                                  messages
                              }                                                          = retVal[ layoutElementId ],
                              dataDescriptorAlias                                        = layoutElement.dataDescriptorAlias ?? '',
                              dataDescriptor                                             = dataDescriptors[ dataDescriptorAlias ] ?? {},
                              dataAndMetaModel                                           = dataDescriptorToDataAndMetaModel?.[ dataDescriptorAlias ] ?? {} as DataAndMetaModelMapping,
                              additionalMessages : FormFieldData[ 'data' ][ 'messages' ] = {},
                              disabled : boolean                                         = layoutElementReadonly || !user || ( layoutElement.type !== 'BUTTON' && layoutElement.type !== 'OVERVIEW' && layoutElement.type !== 'VARIABLE' && dataAndMetaModel.type !== 'reference' && !dataAndMetaModel.isSettable ) || ( layoutElement.type !== 'OVERVIEW' && layoutElement.type !== 'BUTTON' && dataAndMetaModel.hasNoParent );

                        if ( dataDescriptor.type !== 'expression' ) {
                            let propertySet : PropertySet | ReferencePropertySet | undefined,
                                referenceSet : ReferenceSet | undefined,
                                defaultLabel : string | undefined,
                                autoComplete : boolean | undefined,
                                maxLength : number | undefined;

                            if ( dataAndMetaModel.type === 'reference' ) {
                                referenceSet = metaModel[ schema ].getReferenceSet( dataAndMetaModel.metaModels[ 0 ] );
                            }
                            else if ( dataAndMetaModel.type === 'property' || dataAndMetaModel.type === 'enumProperty' ) {
                                propertySet = metaModel[ schema ].getPropertySet( dataAndMetaModel.metaModels[ 0 ] );
                            }
                            else if ( dataAndMetaModel.type === 'referenceProperty' || dataAndMetaModel.type === 'enumReferenceProperty' ) {
                                propertySet = metaModel[ schema ].getReferencePropertySet( dataAndMetaModel.metaModels[ 0 ] );
                            }

                            // max length for item.key and item.name
                            if (
                                dataDescriptor.type === 'item'
                                && (
                                    dataDescriptor.attribute === 'key'
                                    || dataDescriptor.attribute === 'name'
                                )
                            ) {
                                maxLength = 255;
                            }

                            // no autocomplete for reference and static fields
                            if (
                                dataDescriptor.type === 'reference'
                                || dataDescriptor.type === 'static'
                                || !dataAndMetaModel?.metaModels?.[ 0 ]
                            ) {
                                autoComplete = false;
                            }

                            retVal[ layoutElementId ] = {
                                ...retVal[ layoutElementId ],

                                disabled,
                                maxLength,
                                markDirty: !disabled /*&& !justLoaded*/ && formFieldType !== 'VARIABLE',

                                useHookForm,

                                autoCompleteRequestParameters: dataDescriptor.type !== 'reference' && dataDescriptor.type !== 'static' && dataAndMetaModel?.metaModels?.[ 0 ]
                                    ? ( retVal[ layoutElementId ].autoCompleteQuery
                                        ? {
                                            schema,
                                            type      : 'query',
                                            id        : retVal[ layoutElementId ].autoCompleteQuery.id,
                                            parameters: retVal[ layoutElementId ].autoCompleteQuery.parameters
                                        }
                                        : {
                                            schema,
                                            type     : dataDescriptor.type,
                                            id       : dataAndMetaModel.metaModels[ 0 ],
                                            attribute: dataDescriptor.attribute
                                        } )
                                    : undefined
                            };

                            if ( autoComplete !== undefined ) {
                                retVal[ layoutElementId ].autoComplete = autoComplete;
                            }

                            // process additional parameters by building a dataDescriptor for values containing an attributePath
                            if ( retVal[ layoutElementId ].additionalParameters ) {
                                const dataObjects = extractDataObjectsFromDataStructure( dataStructure );

                                for ( const parameter of Object.keys( retVal[ layoutElementId ].additionalParameters ) ) {
                                    const value          = retVal[ layoutElementId ].additionalParameters[ parameter ],
                                          attributePaths = value.match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu );

                                    if ( attributePaths ) {
                                        for ( let attributePath of attributePaths ) {
                                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                                            const attributePathParts = attributePath.split( '.' ),
                                                  dataObjectKey      = attributePathParts.shift(),
                                                  dataObject         = dataObjectKey === 'rootItem'
                                                      ? dataStructure
                                                      : dataObjects.find( dO => dO.item.key === dataObjectKey );

                                            if ( dataObject ) {
                                                if ( !retVal[ layoutElementId ].additionalParametersDataDescriptors ) {
                                                    retVal[ layoutElementId ].additionalParametersDataDescriptors = {};
                                                }
                                                if ( !retVal[ layoutElementId ].additionalParametersDataDescriptors[ parameter ] ) {
                                                    retVal[ layoutElementId ].additionalParametersDataDescriptors[ parameter ] = [];
                                                }
                                                retVal[ layoutElementId ].additionalParametersDataDescriptors[ parameter ].push( buildDataDescriptor( { attributePath: attributePathParts.join( '.' ) }, dataObject ) );

                                                if ( dataObjectKey === 'rootItem' ) {
                                                    retVal[ layoutElementId ].additionalParameters[ parameter ] = retVal[ layoutElementId ].additionalParameters[ parameter ].replaceAll( /\$\{rootItem./g, `\${${dataObject.item.key}.` );
                                                }
                                            }
                                            else if ( dataObjectKey === 'user' ) {
                                                retVal[ layoutElementId ].additionalParameters[ parameter ] = this.messageTemplateStringResolver?.( retVal[ layoutElementId ].additionalParameters[ parameter ], true ) ?? retVal[ layoutElementId ].additionalParameters[ parameter ];
                                            }
                                        }
                                    }
                                }
                            }

                            // process filters by building dataDescriptors for filters containing attributePaths
                            if ( retVal[ layoutElementId ].filters ) {
                                const dataObjects    = extractDataObjectsFromDataStructure( dataStructure ),
                                      value          = retVal[ layoutElementId ].filters,
                                      attributePaths = value.match( /(?:\$\{)((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)(?:\})/gimu );

                                if ( attributePaths ) {
                                    for ( let attributePath of attributePaths ) {
                                        attributePath = attributePath.replaceAll( /(\$\{|\}$)/g, '' );

                                        const attributePathParts = attributePath.split( '.' ),
                                              dataObjectKey      = attributePathParts.shift(),
                                              dataObject         = dataObjectKey === 'rootItem'
                                                  ? dataStructure
                                                  : dataObjects.find( dO => dO.item.key === dataObjectKey );

                                        if ( dataObject ) {
                                            if ( !retVal[ layoutElementId ].additionalFiltersDataDescriptors ) {
                                                retVal[ layoutElementId ].additionalFiltersDataDescriptors = [];
                                            }
                                            retVal[ layoutElementId ].additionalFiltersDataDescriptors.push( buildDataDescriptor( { attributePath: attributePathParts.join( '.' ) }, dataObject ) );

                                            if ( dataObjectKey === 'rootItem' ) {
                                                retVal[ layoutElementId ].filters = retVal[ layoutElementId ].filters.replaceAll( /\$\{rootItem./g, `\${${dataObject.item.key}.` );
                                            }
                                        }
                                        else if ( dataObjectKey === 'user' ) {
                                            retVal[ layoutElementId ].filters = this.messageTemplateStringResolver?.( retVal[ layoutElementId ].filters, true ) ?? retVal[ layoutElementId ].filters;
                                        }
                                    }
                                }
                            }

                            // process fixFilters by building dataDescriptors for fixFilters containing attributePaths
                            if ( retVal[ layoutElementId ].fixFilters ) {
                                const dataObjects    = extractDataObjectsFromDataStructure( dataStructure ),
                                      value          = retVal[ layoutElementId ].fixFilters,
                                      attributePaths = value.match( /(?:\$\{)((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)(?:\})/gimu );

                                if ( attributePaths ) {
                                    for ( let attributePath of attributePaths ) {
                                        attributePath = attributePath.replaceAll( /(\$\{|\}$)/g, '' );

                                        const attributePathParts = attributePath.split( '.' ),
                                              dataObjectKey      = attributePathParts.shift(),
                                              dataObject         = dataObjectKey === 'rootItem'
                                                  ? dataStructure
                                                  : dataObjects.find( dO => dO.item.key === dataObjectKey );

                                        if ( dataObject ) {
                                            if ( !retVal[ layoutElementId ].additionalFixFiltersDataDescriptors ) {
                                                retVal[ layoutElementId ].additionalFixFiltersDataDescriptors = [];
                                            }
                                            retVal[ layoutElementId ].additionalFixFiltersDataDescriptors.push( buildDataDescriptor( { attributePath: attributePathParts.join( '.' ) }, dataObject ) );

                                            if ( dataObjectKey === 'rootItem' ) {
                                                retVal[ layoutElementId ].fixFilters = retVal[ layoutElementId ].fixFilters.replaceAll( /\$\{rootItem./g, `\${${dataObject.item.key}.` );
                                            }
                                        }
                                        else if ( dataObjectKey === 'user' ) {
                                            retVal[ layoutElementId ].fixFilters = this.messageTemplateStringResolver?.( retVal[ layoutElementId ].fixFilters, true ) ?? retVal[ layoutElementId ].fixFilters;
                                        }
                                    }
                                }
                            }

                            // remove defaultValue
                            delete retVal[ layoutElementId ].defaultValue;

                            if ( ( type === 'FILE' || type === 'REFERENCE' ) ) {
                                if ( referenceSet ) {
                                    let referenceDirection           = Object.values( dataDescriptor.dataObject.reference?.referenceConfiguration ?? {} )?.[ 0 ].referenceDirection,
                                        referenceRootItemId : number = 0,
                                        newStatusId : number         = metaModel[ schema ].getStatus( 'active' )!.id;

                                    if ( referenceDirection ) {
                                        // reverse the referenceDirection from dataObject since it is seen from the referenced items dataObject to parent items dataObject (e.g. rootItem)
                                        if ( referenceDirection === 'SrcToDest' ) {
                                            referenceDirection = 'DestToSrc';
                                        }
                                        else {
                                            referenceDirection = 'SrcToDest';
                                        }
                                    }
                                    else if ( rootItemId && dataAndMetaModel.type === 'reference' ) {
                                        const item = dataUtils.getData(
                                            buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                            dataStructure,
                                            dataContext,
                                            rootItemId
                                        )?.[ 0 ].object as Item;

                                        if ( referenceSet.srcItemTypeId === item.itemTypeId ) {
                                            referenceDirection = 'DestToSrc';
                                        }
                                        else {
                                            referenceDirection = 'SrcToDest';
                                        }
                                    }

                                    if ( rootItemId ) {
                                        const parentDataObject = getParentDataObject( dataDescriptor.dataObject.id, dataStructure ),
                                              item             = parentDataObject
                                                  ? dataUtils.getData(
                                                      buildDataDescriptor( { attributePath: 'item.id' }, parentDataObject ) as ObjectDataDescriptor,
                                                      dataStructure,
                                                      dataContext,
                                                      rootItemId
                                                  )?.[ 0 ].object as Item
                                                  : undefined,
                                              reference        = dataUtils.getData(
                                                  buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                                  dataStructure,
                                                  dataContext,
                                                  rootItemId
                                              )?.[ 0 ].object as Reference;

                                        if ( item ) {
                                            referenceRootItemId = item.id;
                                        }
                                        if ( reference ) {
                                            newStatusId = reference.statusId;
                                        }
                                    }

                                    if ( type === 'REFERENCE' ) {
                                        // default label
                                        if ( !messages?.label ) {
                                            defaultLabel = getItemTypeDisplayName( referenceSet[ referenceDirection === 'DestToSrc' ? 'srcItemTypeId' : 'destItemTypeId' ], schema );
                                        }

                                        retVal[ layoutElementId ] = {
                                            label: defaultLabel,
                                            ...messages,
                                            ...retVal[ layoutElementId ],
                                            schema,
                                            dataContext,
                                            rootItemId    : referenceRootItemId,
                                            referenceSetId: referenceSet.id,
                                            newStatusId,
                                            referenceDirection,
                                            disabled      : dataAndMetaModel.isSettable && ( retVal[ layoutElementId ].disabled || referenceRootItemId === 0 )
                                        };
                                        retVal[ layoutElementId ].props = _.pick( retVal[ layoutElementId ], [ 'key' ].concat( referenceFieldComponentProps ) ) as ReferenceFieldComponentProps;
                                    }
                                    else {
                                        // default label
                                        if ( !messages?.label ) {
                                            defaultLabel = 'YN.base.common.files';
                                        }

                                        retVal[ layoutElementId ] = {
                                            label: defaultLabel,
                                            ...messages,
                                            ...retVal[ layoutElementId ],
                                            schema,
                                            dataContext,
                                            rootItemId    : referenceRootItemId,
                                            referenceSetId: referenceSet.id,
                                            newStatusId,
                                            disabled      : retVal[ layoutElementId ].disabled || referenceRootItemId === 0
                                        };
                                        retVal[ layoutElementId ].props = _.pick( retVal[ layoutElementId ], [ 'key' ].concat( fileFieldComponentProps ) ) as FileFieldComponentProps;
                                    }
                                }
                                else {
                                    retVal[ layoutElementId ] = {
                                        ...messages,
                                        ...retVal[ layoutElementId ]
                                    };
                                    retVal[ layoutElementId ].props = { ...retVal[ layoutElementId ] };

                                    console.error( 'no referenceSet: %o, %o', dataAndMetaModel, retVal[ layoutElementId ] );
                                }
                            }
                            else if ( propertySet && type === 'ENUM' ) {
                                let newStatusId : number = metaModel[ schema ].getStatus( 'active' )!.id;

                                if ( rootItemId ) {
                                    if ( dataAndMetaModel.type === 'enumProperty' ) {
                                        const item = dataUtils.getData(
                                            buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                            dataStructure,
                                            dataContext,
                                            rootItemId
                                        )?.[ 0 ].object as Item;

                                        if ( item ) {
                                            newStatusId = item.statusId;
                                        }
                                    }
                                    else {
                                        const reference = dataUtils.getData(
                                            buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                            dataStructure,
                                            dataContext,
                                            rootItemId
                                        )?.[ 0 ].object as Reference;

                                        if ( reference ) {
                                            newStatusId = reference.statusId;
                                        }
                                    }
                                }

                                // default label
                                if ( !messages?.label ) {
                                    if ( dataAndMetaModel.type === 'enumProperty' ) {
                                        defaultLabel = getPropertySetDisplayName( propertySet.id, schema );
                                    }
                                    else {
                                        defaultLabel = getReferencePropertySetDisplayName( propertySet.id, schema );
                                    }
                                }

                                retVal[ layoutElementId ] = {
                                    label: defaultLabel,
                                    ...messages,
                                    ...retVal[ layoutElementId ],
                                    schema                : propertySet.schema,
                                    propertySetId         : dataAndMetaModel.type === 'enumProperty' ? propertySet.id : undefined,
                                    referencePropertySetId: dataAndMetaModel.type === 'enumProperty' ? undefined : propertySet.id,
                                    newStatusId,
                                    keyEditorParams       : retVal[ layoutElementId ].fields?.key
                                        ? {
                                            ...fieldUtils.extractPropsForValueTypeFields( {
                                                ...retVal[ layoutElementId ].fields?.key.data
                                            } as FormFieldProperties ),
                                            type : retVal[ layoutElementId ].fields?.key.type,
                                            query: retVal[ layoutElementId ].fields?.key.data.query
                                        }
                                        : undefined,
                                    valueEditorParams     : retVal[ layoutElementId ].fields?.value
                                        ? {
                                            ...fieldUtils.extractPropsForValueTypeFields( {
                                                ...retVal[ layoutElementId ].fields?.value.data
                                            } as FormFieldProperties ),
                                            type : retVal[ layoutElementId ].fields?.value.type,
                                            query: retVal[ layoutElementId ].fields?.value.data.query
                                        }
                                        : undefined
                                };

                                if ( retVal[ layoutElementId ].keyEditorParams?.autoCompleteQuery ) {
                                    retVal[ layoutElementId ].keyEditorParams.autoCompleteRequestParameters = {
                                        schema,
                                        type      : 'query',
                                        id        : retVal[ layoutElementId ].keyEditorParams.autoCompleteQuery.id,
                                        parameters: retVal[ layoutElementId ].keyEditorParams.autoCompleteQuery.parameters
                                    };
                                }

                                if ( retVal[ layoutElementId ].valueEditorParams?.autoCompleteQuery ) {
                                    retVal[ layoutElementId ].valueEditorParams.autoCompleteRequestParameters = {
                                        schema,
                                        type      : 'query',
                                        id        : retVal[ layoutElementId ].valueEditorParams.autoCompleteQuery.id,
                                        parameters: retVal[ layoutElementId ].valueEditorParams.autoCompleteQuery.parameters
                                    };
                                }

                                if ( retVal[ layoutElementId ].valueEditorParams?.type === 'JSON' ) {
                                    try {
                                        if ( retVal[ layoutElementId ].valueEditorParams.jsonSchema ) {
                                            retVal[ layoutElementId ].valueEditorParams.jsonSchema = JSON.parse( retVal[ layoutElementId ].valueEditorParams.jsonSchema );
                                        }
                                        if ( retVal[ layoutElementId ].valueEditorParams.uiSchema ) {
                                            retVal[ layoutElementId ].valueEditorParams.uiSchema = JSON.parse( retVal[ layoutElementId ].valueEditorParams.uiSchema );
                                        }
                                    }
                                    catch ( e ) {
                                        console.error( e );
                                    }
                                }

                                retVal[ layoutElementId ].props = _.pick( retVal[ layoutElementId ], [ 'key' ].concat( enumFieldComponentProps ) ) as EnumFieldComponentProps;
                            }
                            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                            else if ( ( propertySet?.valueSetId || retVal[ layoutElementId ].valueSetId ) && ( dataDescriptor.attribute === 'value' || type === 'VARIABLE' ) ) {
                                // default label
                                if ( propertySet && !messages?.label ) {
                                    if ( dataAndMetaModel.type === 'property' ) {
                                        defaultLabel = getPropertySetDisplayName( propertySet.id, schema );
                                    }
                                    else {
                                        defaultLabel = getReferencePropertySetDisplayName( propertySet.id, schema );
                                    }
                                }

                                retVal[ layoutElementId ] = {
                                    label: defaultLabel,
                                    ...messages,
                                    ...retVal[ layoutElementId ],
                                    schema,
                                    propertySetId         : dataAndMetaModel.type === 'property' ? propertySet?.id : undefined,
                                    referencePropertySetId: dataAndMetaModel.type === 'property' ? undefined : propertySet?.id,
                                    hasValueSet           : true
                                };
                                retVal[ layoutElementId ].props = _.pick( retVal[ layoutElementId ], [ 'key' ].concat( valueSetFieldComponentProps ) ) as ValueSetFieldComponentProps;
                            }
                            else {
                                // get definitions as placeholder for item.key and item.name if there is no one
                                if ( !messages?.placeholder && dataAndMetaModel.type === 'item' ) {

                                    if ( dataDescriptor.attribute === 'key' || dataDescriptor.attribute === 'name' ) {
                                        const itemType = metaModel[ schema ].getItemType( dataAndMetaModel.metaModels[ 0 ] ?? 0 );

                                        additionalMessages.placeholder = itemType?.[ `${dataDescriptor.attribute}Definition` ];
                                    }
                                }

                                // default label
                                if ( !messages?.label ) {
                                    if ( dataAndMetaModel.type === 'item' ) {
                                        const itemType = metaModel[ schema ].getItemType( dataAndMetaModel.metaModels[ 0 ] ?? 0 );

                                        if ( itemType && dataDescriptor.attribute === 'key' ) {
                                            defaultLabel = getItemKeyDisplayName( itemType.id, schema );
                                        }
                                        else if ( itemType && dataDescriptor.attribute === 'name' ) {
                                            defaultLabel = getItemNameDisplayName( itemType.id, schema );
                                        }
                                        else {
                                            defaultLabel = `YN.base.common.attribute.${dataDescriptor.attribute}`;
                                        }
                                    }
                                    else if ( dataAndMetaModel.type === 'reference' ) {
                                        defaultLabel = `YN.base.common.attribute.${dataDescriptor.attribute}`;
                                    }
                                    else if ( propertySet ) {
                                        if ( dataAndMetaModel.type === 'property' ) {
                                            defaultLabel = getPropertySetDisplayName( propertySet.id, schema );
                                        }
                                        else {
                                            defaultLabel = getReferencePropertySetDisplayName( propertySet.id, schema );
                                        }

                                        if ( dataDescriptor.attribute === 'key' ) {
                                            if ( propertySet.isValueSettable ) {
                                                defaultLabel += propertySet.keyLabel;
                                            }
                                        }
                                        else if ( dataDescriptor.attribute !== 'value' ) {
                                            defaultLabel += ` ${__( `YN.base.common.attribute.${dataDescriptor.attribute}` )}`;
                                        }
                                    }
                                }

                                if ( type === 'BUTTON' ) {
                                    retVal[ layoutElementId ].icon = images?.icon ? `yn-icon ${getIconCssClassName( 'byRef', images.icon, 'CFG' )}` : retVal[ layoutElementId ].iconCls;
                                }

                                else if ( type === 'JSON' || ( type === 'VARIABLE' && ( retVal[ layoutElementId ] as VariableFieldProperties ).variableType === 'JSON' ) ) {
                                    //retVal[ layoutElementId ].definition = propertySet?.valueDefinition;
                                    try {
                                        if ( retVal[ layoutElementId ].jsonSchema ) {
                                            retVal[ layoutElementId ].jsonSchema = JSON.parse( retVal[ layoutElementId ].jsonSchema );
                                        }
                                        if ( retVal[ layoutElementId ].uiSchema ) {
                                            retVal[ layoutElementId ].uiSchema = JSON.parse( retVal[ layoutElementId ].uiSchema );
                                        }
                                    }
                                    catch ( e ) {
                                        console.error( e );
                                    }
                                }

                                else if ( type === 'STATIC' && !retVal[ layoutElementId ].content ) {
                                    if ( images?.content ) {
                                        retVal[ layoutElementId ].content = formatValue( images.content, { type: 'Image', options: { unit: '%', width: 100 } } );
                                    }
                                    else if ( messages?.content ) {
                                        retVal[ layoutElementId ].content = messages.content;
                                    }
                                }

                                if ( type === 'BOOLEAN' && retVal[ layoutElementId ].useCheckboxLabelAsDefault ) {
                                    retVal[ layoutElementId ] = {
                                        checkboxLabel: defaultLabel,
                                        propertySetId: propertySet?.id,
                                        ...images,
                                        ...messages,
                                        ...additionalMessages,
                                        ...retVal[ layoutElementId ],
                                        valueUnit: propertySet?.valueUnit
                                    };
                                }
                                else {
                                    retVal[ layoutElementId ] = {
                                        label        : defaultLabel,
                                        propertySetId: propertySet?.id,
                                        ...images,
                                        ...messages,
                                        ...additionalMessages,
                                        ...retVal[ layoutElementId ],
                                        valueUnit: propertySet?.valueUnit
                                    };
                                }

                                retVal[ layoutElementId ].props = fieldUtils.extractPropsForValueTypeFields( {
                                    ...retVal[ layoutElementId ]
                                } );
                            }

                            delete retVal[ layoutElementId ].props?.id;
                        }
                    }
                }
            };

            if ( layoutStructure.children?.length ) {
                applyInheritanceAndProcessingRecursive( layoutStructure.children, _.pick( retVal[ formLayoutElementId ], formInheritedProperties ) as FormInheritedProperties );
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    /**
     * Builds the layoutElement to error mapping.
     *
     * memoizable
     *
     * @param layoutElements
     * @param layoutElementMappings
     * @param dataDescriptorMappings
     * @param dataDescriptors
     * @param variables
     * @param variablesEditValues
     * @param user
     * @param checkItemKeyUniqueAndReferenceInsertable
     *
     * @private
     */
    async buildLayoutElementToErrorsMapping( layoutElements : LayoutElements, layoutElementMappings : LayoutElementMappings, dataDescriptorMappings : DataDescriptorMappings, dataDescriptors : DataDescriptors, variables? : Variables, variablesEditValues? : Variables, user? : User, checkItemKeyUniqueAndReferenceInsertable? : boolean ) : Promise<LayoutElementToErrorsMapping> {
        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cbuildLayoutElementToErrorsMapping%c'.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: normal;' );
        }

        const startTime = this.debug ? performance.now() : 0;

        const retVal : LayoutElementToErrorsMapping = {},
              layoutElementKeys                     = Object.keys( layoutElements ).map( key => Number( key ) ) as number[],
              dataDescriptorToDataAndMetaModel      = dataDescriptorMappings.dataAndMetaModel,
              dataDescriptorToValue                 = dataDescriptorMappings.value;

        for ( const layoutElementId of layoutElementKeys ) {
            const type = layoutElements[ layoutElementId ].type;

            if (
                type !== 'FORM'
                && type !== 'CONTAINER'
                && type !== 'TABCONTAINER'
                && type !== 'CALENDAR'
                && type !== 'CHART'
                && type !== 'GRAPH'
                && type !== 'MAP'
                && type !== 'OVERVIEW'
                && type !== 'BUTTON'
                && type !== 'STATIC'
            ) {
                const layoutElement                    = layoutElements[ layoutElementId ] as FormFieldData,
                      layoutElementDataDescriptorAlias = layoutElement.dataDescriptorAlias ?? '';

                let parentIds : ( number | undefined )[] = [ undefined ];

                if ( type !== 'VARIABLE' && dataDescriptorToDataAndMetaModel?.[ layoutElementDataDescriptorAlias ] ) {
                    if ( dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].type === 'item' || dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].type === 'reference' ) {
                        parentIds = parentIds.concat( dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].objects as number[] );
                    }
                    else {
                        parentIds = parentIds.concat( Object.keys( dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].objects as {
                            [ parentId : number ] : any
                        } ) );
                    }
                }

                for ( const parentId of parentIds ) {
                    const realLayoutElementId                  = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parentId ),
                          realLayoutElementDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( layoutElementDataDescriptorAlias, parentId ),
                          value                                = type === 'VARIABLE'
                              ? variablesEditValues?.repeated?.[ ( layoutElement.data as VariableFieldProperties ).variableName ]?.[ parentId ?? 0 ]
                              ?? variablesEditValues?.[ ( layoutElement.data as VariableFieldProperties ).variableName ]
                              ?? variables?.repeated?.[ ( layoutElement.data as VariableFieldProperties ).variableName ]?.[ parentId ?? 0 ]
                              ?? variables?.[ ( layoutElement.data as VariableFieldProperties ).variableName ]
                              : dataDescriptorToDataAndMetaModel?.[ realLayoutElementDataDescriptorAlias ]?.isDefault
                                  ? undefined
                                  : dataDescriptorToValue[ realLayoutElementDataDescriptorAlias ];

                    const validationErrors = await this.validateValueForLayoutElement( layoutElement, parentId, layoutElementMappings, value, dataDescriptorMappings, dataDescriptors, checkItemKeyUniqueAndReferenceInsertable, undefined, variables );

                    if ( validationErrors ) {
                        retVal[ realLayoutElementId ] = validationErrors;
                    }
                }
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    /**
     * Builds the errorData.
     *
     * memoizable
     *
     * @param layoutElements
     * @param dataDescriptorMappings
     * @param layoutElementMappings
     * @param schema
     *
     * @private
     */
    buildErrorData( layoutElements : LayoutElements, dataDescriptors : DataDescriptors, dataStructure : DataStructure, dataDescriptorMappings : DataDescriptorMappings, layoutElementMappings : LayoutElementMappings, schema : Schema, rootItemId? : number ) : PanelErrorData[ 'errorData' ] {
        const startTime                             = this.debug ? performance.now() : 0,
              {
                  dataContext
              }                                     = this.props,
              retVal : PanelErrorData[ 'errorData'] = {
                  count  : 0,
                  tooltip: [],
                  message: []
              },
              setValueErrors                        = layoutElementMappings?.setValueErrors ?? {},
              setValueErrorKeys                     = Object.keys( setValueErrors ) as string[],
              validationErrors                      = layoutElementMappings?.errors ?? {},
              validationErrorKeys                   = Object.keys( validationErrors ) as string[];

        if ( setValueErrorKeys.length || validationErrorKeys.length ) {
            const layoutElementKeys = Object.keys( layoutElements ).map( key => Number( key ) ) as number[];

            for ( const layoutElementId of layoutElementKeys ) {
                if (
                    layoutElements[ layoutElementId ].type !== 'FORM'
                    && layoutElements[ layoutElementId ].type !== 'CONTAINER'
                    && layoutElements[ layoutElementId ].type !== 'TABCONTAINER'
                ) {
                    const layoutElement     = layoutElements[ layoutElementId ] as FormFieldData,
                          layoutElementData = layoutElementMappings.cache?.[ layoutElementId ];

                    let layoutElementErrorKeys = setValueErrorKeys.reduce(
                        ( errorKeys, key ) => {
                            if ( key.indexOf( `${layoutElementId}_` ) === 0 ) {
                                errorKeys.push( key );
                            }

                            return errorKeys;
                        },
                        [] as string[]
                    );

                    layoutElementErrorKeys = layoutElementErrorKeys.concat(
                        validationErrorKeys.reduce(
                            ( errorKeys, key ) => {
                                if ( key.indexOf( `${layoutElementId}_` ) === 0 && !layoutElementErrorKeys.includes( key ) ) {
                                    errorKeys.push( key );
                                }

                                return errorKeys;
                            },
                            [] as string[]
                        )
                    );

                    if ( !layoutElementErrorKeys.length && ( setValueErrors?.[ layoutElementId ] || validationErrors?.[ layoutElementId ] ) ) {
                        layoutElementErrorKeys.push( String( layoutElementId ) );
                    }

                    for ( const errorKey of layoutElementErrorKeys ) {
                        const {
                                  parentId
                              }                                  = this.getLayoutElementIdAndParentIdFromRealLayoutElementId( errorKey ),
                              realLayoutElementId                = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parentId ),
                              realDataDescriptorAlias            = layoutElement.dataDescriptorAlias
                                  ? this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( layoutElement.dataDescriptorAlias, parentId )
                                  : undefined,
                              layoutElementValue                 = (
                                  realDataDescriptorAlias
                                      ? dataDescriptorMappings.value[ realDataDescriptorAlias ]
                                      : {}
                              ) as EnumValue,
                              setValueErrorMessages : string[]   = ( setValueErrors?.[ realLayoutElementId ] ?? [] ).map( errorMessage => {
                                  // handle enum property errors
                                  try {
                                      const enumValueErrors : EnumValueErrors = JSON.parse( errorMessage ),
                                            errorMessages : string[]          = [];

                                      for ( const enumValueId of Object.keys( enumValueErrors ) ) {
                                          const row        = Object.values( layoutElementValue ).findIndex( v => v.id === Number( enumValueId ) ) + 1,
                                                attributes = Object.keys( enumValueErrors[ enumValueId ] ) as string[];

                                          for ( const attribute of attributes ) {
                                              let attributeLabel : string = attribute;

                                              switch ( attribute ) {
                                                  case 'key':
                                                      if ( enumValueErrors[ enumValueId ][ attribute ].propertySetId ) {
                                                          attributeLabel = getPropertyKeyDisplayName( enumValueErrors[ enumValueId ][ attribute ].propertySetId!, schema );
                                                      }
                                                      else if ( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId ) {
                                                          attributeLabel = getReferencePropertyKeyDisplayName( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId!, schema );
                                                      }
                                                      break;
                                                  case 'value':
                                                      if ( enumValueErrors[ enumValueId ][ attribute ].propertySetId ) {
                                                          attributeLabel = getPropertyValueDisplayName( enumValueErrors[ enumValueId ][ attribute ].propertySetId!, schema );
                                                      }
                                                      else if ( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId ) {
                                                          attributeLabel = getReferencePropertyValueDisplayName( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId!, schema );
                                                      }
                                                      break;
                                              }

                                              errorMessages[ row ] = __( 'YN.base.form.error.detail.enumError', {
                                                  row         : String( row ),
                                                  attribute   : attributeLabel,
                                                  errorMessage: __( enumValueErrors[ enumValueId ][ attribute ].errorMessage )
                                              } );
                                          }
                                      }

                                      errorMessage = errorMessages.join( '<br/>' ).replace( /^(<br\/>)*/g, '' );
                                  }
                                      // eslint-disable-next-line no-empty
                                  catch ( e ) {
                                  }

                                  return errorMessage;
                              } ),
                              validationErrorMessages : string[] = ( validationErrors?.[ realLayoutElementId ] ?? [] ).map( errorMessage => {
                                  // handle enum property errors
                                  try {
                                      const enumValueErrors : EnumValueErrors = JSON.parse( errorMessage ),
                                            errorMessages : string[]          = [];

                                      for ( const enumValueId of Object.keys( enumValueErrors ) ) {
                                          const row        = Object.values( layoutElementValue ).findIndex( v => v.id === Number( enumValueId ) ) + 1,
                                                attributes = Object.keys( enumValueErrors[ enumValueId ] ) as string[];

                                          for ( const attribute of attributes ) {
                                              let attributeLabel : string = attribute;

                                              switch ( attribute ) {
                                                  case 'key':
                                                      if ( enumValueErrors[ enumValueId ][ attribute ].propertySetId ) {
                                                          attributeLabel = getPropertyKeyDisplayName( enumValueErrors[ enumValueId ][ attribute ].propertySetId!, schema );
                                                      }
                                                      else if ( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId ) {
                                                          attributeLabel = getReferencePropertyKeyDisplayName( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId!, schema );
                                                      }
                                                      break;
                                                  case 'value':
                                                      if ( enumValueErrors[ enumValueId ][ attribute ].propertySetId ) {
                                                          attributeLabel = getPropertyValueDisplayName( enumValueErrors[ enumValueId ][ attribute ].propertySetId!, schema );
                                                      }
                                                      else if ( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId ) {
                                                          attributeLabel = getReferencePropertyValueDisplayName( enumValueErrors[ enumValueId ][ attribute ].referencePropertySetId!, schema );
                                                      }
                                                      break;
                                              }

                                              errorMessages[ row ] = __( 'YN.base.form.error.detail.enumError', {
                                                  row         : String( row ),
                                                  attribute   : attributeLabel,
                                                  errorMessage: __( enumValueErrors[ enumValueId ][ attribute ].errorMessage )
                                              } );
                                          }
                                      }

                                      errorMessage = errorMessages.join( '<br/>' ).replace( /^(<br\/>)*/g, '' );
                                  }
                                      // eslint-disable-next-line no-empty
                                  catch ( e ) {
                                  }

                                  return errorMessage;
                              } ),
                              messages                           = ( setValueErrorMessages ).concat( validationErrorMessages ).map( ( message, index ) =>
                                  <React.Fragment key={`message_${index}`}>{parse( message )}<br/></React.Fragment> );

                        let title = __( 'YN.base.form.error.detail.header', {
                            fieldLabel: __( String( layoutElementData?.label ) )
                        } );

                        // improve error message by adding parent(s) info (if)
                        if ( parentId && rootItemId && parentId !== rootItemId ) {
                            const parentTitles : string[] = [];

                            let parentItem : Item | undefined                       = global.data[ dataContext ].items[ parentId ],
                                parentDataObject : DataObjectProperties | undefined = layoutElement.dataDescriptorAlias
                                    ? dataDescriptors[ layoutElement.dataDescriptorAlias ]?.dataObject
                                    : undefined;

                            // if no parent item - try to get by reference
                            if ( !parentItem && global.data[ dataContext ].references[ parentId ] && parentDataObject ) {
                                const parentReference = global.data[ dataContext ].references[ parentId ],
                                      parentItems     = ( dataUtils.getData(
                                          buildDataDescriptor( { attributePath: 'item.id' }, parentDataObject ) as ObjectDataDescriptor,
                                          dataStructure,
                                          dataContext,
                                          rootItemId
                                      )?.map( itemData => itemData.object as Item ) ?? [] ) as Item[];

                                for ( const pI of parentItems ) {
                                    if ( pI.id === parentReference.srcItemId || pI.id === parentReference.destItemId ) {
                                        parentItem = pI;
                                        break;
                                    }
                                }
                            }

                            while ( parentItem && parentItem.id !== rootItemId ) {
                                const parentItemId = parentItem.id;

                                parentTitles.unshift( __( 'YN.base.form.error.detail.parent.header', {
                                    type : parentItem.itemType.label,
                                    label: parentItem.displayAttributeValue
                                } ) );

                                parentItem = undefined;

                                // chain up
                                if ( parentDataObject ) {
                                    const parentObject = getParentDataObject( parentDataObject.id, dataStructure );

                                    if ( parentObject ) {
                                        const subDataStructure = getDataStructure( parentDataObject.id, dataStructure );

                                        if ( subDataStructure ) {
                                            parentItem = dataUtils.getData(
                                                buildDataDescriptor( { attributePath: 'item.id' }, parentObject ) as ObjectDataDescriptor,
                                                subDataStructure,
                                                dataContext,
                                                parentItemId
                                            )?.[ 0 ].object as Item;

                                            parentDataObject = parentObject;
                                        }
                                    }
                                }
                            }

                            if ( parentTitles.length ) {
                                title = `${parentTitles.join( ' - ' )} - ${title}`;
                            }
                        }

                        ( retVal.count as number )++;

                        if ( retVal.count! < 12 ) {
                            retVal.tooltip!.push( <div key={`error_title_${retVal.count}`} className="yn-tooltip-subheader">{title}</div> );
                            retVal.tooltip!.push( <React.Fragment key={`error_message_${retVal.count}`}>{messages}</React.Fragment> );

                            if ( retVal.message!.length ) {
                                retVal.message!.push( <hr key={`error_ruler_${retVal.count}`} className="yn-error-ruler"/> );
                            }
                            retVal.message!.push( <div key={`error_title_${retVal.count}`} className="yn-error-subtitle">{title}</div> );
                            retVal.message!.push( <div key={`error_message_${retVal.count}`}
                                                       className="yn-error-message">{messages}</div> );
                        }
                        else if ( retVal.count! === 12 ) {
                            retVal.tooltip!.push( <div key={`error_title_${retVal.count}`} className="yn-tooltip-subheader">...</div> );

                            if ( retVal.message!.length ) {
                                retVal.message!.push( <hr key={`error_ruler_${retVal.count}`} className="yn-error-ruler"/> );
                            }
                            retVal.message!.push( <div key={`error_title_${retVal.count}`} className="yn-error-subtitle">...</div> );
                        }
                    }
                }
            }
        }

        if ( this.debug ) {
            console.log( `%cbuildErrorData - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
        }

        return retVal;
    }

    /**
     * Builds the dirty state.
     *
     * memoizable
     *
     * @param props
     * @param elusiveData
     *
     * @private
     */
    buildDirty( props : AllProps, elusiveData? : PanelElusiveData ) : boolean {
        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cbuildDirty%c'.asLogLine( 101 )}`, 'font-weight: normal;', `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: normal;' );
        }

        const startTime            = this.debug ? performance.now() : 0,
              {
                  dataContext,
                  schema,
                  rootItemId,
                  saveConfig
              }                    = props,
              dataIdList           = elusiveData?.data,
              modifiedData         = rootItemId
                  ? getModifiedDataRecursive( dataContext, schema, rootItemId )
                  : getModifiedData( dataContext, schema ),
              modifiedItemIds      = modifiedData.items.map( i => i.id ),
              modifiedReferenceIds = modifiedData.references.map( r => r.id );

        let retVal = false;

        // check items
        for ( const itemId of Object.keys( dataIdList?.items ?? {} ) ) {
            if ( modifiedItemIds.includes( Number( itemId ) ) ) {
                retVal = true;

                if ( this.debug ) {
                    console.log( '    items modified' );
                    console.log( '        item', global.data[ dataContext ]?.items[ itemId ] );
                }

                break;
            }
        }

        // check references
        if ( !retVal ) {
            for ( const referenceId of Object.keys( dataIdList?.references ?? {} ) ) {
                if ( modifiedReferenceIds.includes( Number( referenceId ) ) ) {
                    retVal = true;

                    if ( this.debug ) {
                        console.log( '    references modified' );
                        console.log( '        reference', global.data[ dataContext ]?.references[ referenceId ] );
                    }

                    break;
                }
            }
        }

        // check save request parameters
        if ( !retVal && saveConfig.requestParams ) {
            const currentSaveRequestParameters = JSON.stringify( Form.resolveRequestParameters( saveConfig.requestParams, schema, dataContext, rootItemId!, elusiveData?.variables ) );

            if (
                currentSaveRequestParameters !== '{}'
                // save request parameters changed
                && currentSaveRequestParameters !== ( elusiveData?.lastSaveRequestParameters ?? '{}' )
            ) {
                if ( this.debug ) {
                    console.log( '    save request parameters changed' );
                    console.log( '        last', elusiveData?.lastSaveRequestParameters );
                    console.log( '        current', JSON.stringify( Form.resolveRequestParameters( saveConfig.requestParams, schema, dataContext, rootItemId!, elusiveData?.variables ) ) );
                }

                retVal = true;
            }
        }

        // check views
        if ( !retVal ) {
            for ( const view of Object.values( this.views ) ) {
                if ( view?.dirty() ) {
                    retVal = true;
                    break;
                }
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 100 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    /**
     * Handles the setting of FormField defaultValues.
     *
     * @param props
     *
     * @private
     */
    async handleDefaultValues( props : AllProps ) : Promise<boolean> {

        if ( this.debug ) {
            console.groupCollapsed( `%c${`%chandleDefaultValues%c`.asLogLine( 104 )}`, 'font-weight: normal', `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: normal;' );
        }

        const startTime                        = this.debug ? performance.now() : 0,
              {
                  layoutElements,
                  elusiveData
              }                                = props,
              layoutElementKeys                = Object.keys( layoutElements ).map( key => Number( key ) ) as number[],
              dataDescriptorToDataAndMetaModel = elusiveData?.dataDescriptorMappings?.dataAndMetaModel,
              dataDescriptorToValue            = elusiveData?.dataDescriptorMappings?.value;

        let retVal : boolean = false;

        for ( const layoutElementId of layoutElementKeys ) {
            const type = layoutElements[ layoutElementId ].type;

            if (
                type !== 'FORM'
                && type !== 'CONTAINER'
                && type !== 'TABCONTAINER'
                && type !== 'CALENDAR'
                && type !== 'CHART'
                && type !== 'GRAPH'
                && type !== 'MAP'
                && type !== 'OVERVIEW'
                && type !== 'BUTTON'
                && type !== 'STATIC'
            ) {
                const layoutElement                    = layoutElements[ layoutElementId ] as FormFieldData,
                      {
                          defaultValue
                      }                                = layoutElement.data,
                      layoutElementsData               = elusiveData?.layoutElementMappings?.cache ?? {},
                      layoutElementData                = layoutElementsData[ layoutElementId ],
                      {
                          visibility
                      }                                = layoutElementData,
                      layoutElementDataDescriptorAlias = layoutElement.dataDescriptorAlias ?? '';

                let parentIds : ( number | undefined )[] = [ undefined ];

                if ( type !== 'VARIABLE' && dataDescriptorToDataAndMetaModel?.[ layoutElementDataDescriptorAlias ] ) {
                    if ( dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].type === 'item' || dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].type === 'reference' ) {
                        parentIds = parentIds.concat( dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].objects as number[] );
                    }
                    else {
                        parentIds = parentIds.concat( Object.keys( dataDescriptorToDataAndMetaModel[ layoutElementDataDescriptorAlias ].objects as {
                            [ parentId : number ] : any
                        } ) );
                    }
                }

                for ( const parentId of parentIds ) {
                    const realLayoutElementDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( layoutElementDataDescriptorAlias, parentId ),
                          isDefault                            = dataDescriptorToDataAndMetaModel?.[ realLayoutElementDataDescriptorAlias ]?.isDefault ?? false,
                          value                                = dataDescriptorToValue?.[ realLayoutElementDataDescriptorAlias ];

                    if ( visibility === 'visible' && ( value === undefined || isDefault ) && defaultValue !== '' && defaultValue !== undefined && defaultValue !== null ) {
                        retVal = true;

                        await this.onValueChangeAsync(
                            this.getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parentId ),
                            defaultValue,
                            undefined,
                            props
                        );
                    }
                }
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.shouldComponentUpdateBuildFunction}; font-weight: bold;`, 'font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    /**
     * Sets the given value for the given layoutElement (form field)
     *
     * @param layoutElement
     * @param parentId
     * @param value
     * @param props
     * @param originalEvent
     *
     * @private
     */
    async setValueForLayoutElement( layoutElement : FormFieldData, parentId : number | undefined, value : AttributeValue | EnumUpdateValue | ReferenceValue | ReferenceValue[] | undefined, props : AllProps = this.props, originalEvent? : any ) : Promise<string[] | undefined> {
        if ( this.debug ) {
            console.groupCollapsed( `%c${'%cForm - %csetValueForLayoutElement%c'.asLogLine( 106 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.setValueForLayoutElement};`, 'font-weight: normal;' );
            console.log( '    layoutElementId', layoutElement.data.id );
            console.log( '    value', { value } );
        }

        const startTime                     = this.debug ? performance.now() : 0,
              {
                  uid,
                  dataContext,
                  schema,
                  dataStructure,
                  dataDescriptors,
                  rootItemId,
                  elusiveData
              }                             = props,
              layoutElementId               = layoutElement.data.id,
              additionalElusiveDataContexts = this.props.additionalElusiveDataContexts.concat( [ uid ] ),
              isDeferred                    = this.deferredRender;

        let retVal : string[] | undefined = [];

        if ( elusiveData?.dataDescriptorMappings ) {
            const layoutElementDataDescriptorAlias = layoutElement.dataDescriptorAlias ?? '',
                  dataDescriptorMappings           = { ...elusiveData.dataDescriptorMappings },
                  layoutElementMappings            = { ...elusiveData.layoutElementMappings },
                  layoutElementDataDescriptor      = dataDescriptors[ layoutElementDataDescriptorAlias ],
                  dataAndMetaModel                 = dataDescriptorMappings?.dataAndMetaModel?.[ layoutElementDataDescriptorAlias ];

            if ( layoutElementDataDescriptor && dataAndMetaModel ) {
                try {
                    // item
                    if (
                        dataAndMetaModel.type === 'item' && layoutElementDataDescriptor.type === 'item'
                        && itemOnlyAttributesSettable.includes( layoutElementDataDescriptor.attribute )
                    ) {
                        const itemId = parentId ?? dataAndMetaModel.objects?.[ 0 ];

                        if ( this.debug ) {
                            console.log( '    item' );
                            console.log( '        itemId', itemId );
                        }

                        if ( itemId ) {
                            if ( this.debug ) {
                                console.log( '        -> update' );
                            }

                            const data : ItemOnlyAttributesSettable = {};

                            switch ( layoutElementDataDescriptor.attribute ) {
                                case 'key':
                                    data.key = String( value ?? '' );
                                    break;

                                case 'name':
                                    data.name = String( value ?? '' );
                                    break;

                                case 'statusId':
                                    data.statusId = Number( value );
                                    break;
                            }

                            if ( this.debug ) {
                                console.log( '            data', data );
                            }

                            if ( !isDeferred ) {
                                this.deferRender();
                            }

                            this.props.updateItem(
                                dataContext,
                                itemId,
                                data,
                                additionalElusiveDataContexts
                            );
                        }
                        else {
                            if ( this.debug ) {
                                console.error( 'no item' );
                            }
                        }
                    }

                    // property
                    else if (
                        dataAndMetaModel.type === 'property' && layoutElementDataDescriptor.type === 'property'
                        && propertyAttributesSettable.includes( layoutElementDataDescriptor.attribute )
                    ) {
                        let itemId = parentId ?? Object.keys( dataAndMetaModel.objects )[ 0 ] as number | undefined,
                            item : Item | undefined;

                        const propertySet    = metaModel[ schema ].getPropertySet( dataAndMetaModel.metaModels[ 0 ] ),
                              propertyTypeId = propertySet?.propertyTypeId;

                        if ( !itemId ) {
                            item = dataUtils.getData(
                                buildDataDescriptor( { attributePath: 'item.id' }, layoutElementDataDescriptor.dataObject ) as ObjectDataDescriptor,
                                dataStructure,
                                dataContext,
                                rootItemId!
                            )?.[ 0 ].object as Item;

                            itemId = item?.id;
                        }
                        else {
                            item = global.data[ dataContext ].items[ itemId ];
                        }

                        if ( this.debug ) {
                            console.log( '    property' );
                            console.log( '        itemId', itemId );
                            console.log( '        propertyTypeId', propertyTypeId );
                        }

                        if ( item && propertyTypeId ) {
                            const data : ItemAttributesSettable = {};

                            let noUpdate = false;

                            data.properties = {};
                            data.properties[ propertyTypeId ] = {};

                            // no value -> delete property (will be reverted by Item.update)
                            if ( value === undefined || value === null || value === '' || ( Array.isArray( value ) && !value.length ) ) {
                                if ( item.properties.has( propertyTypeId ) ) {
                                    if ( this.debug ) {
                                        console.log( '        -> delete' );
                                    }

                                    data.properties[ propertyTypeId ].statusId = metaModel[ schema ].getStatus( 'deleted' )!.id;
                                }
                                else {
                                    noUpdate = true;
                                }
                            }

                            // update
                            else {
                                const property = item.properties.get( propertyTypeId );

                                // init new property
                                if ( !property ) {
                                    if ( this.debug ) {
                                        console.log( '        -> new' );
                                    }

                                    data.properties[ propertyTypeId ].statusId = item.statusId;
                                }

                                // restore deleted property
                                else if ( property.isDeleted() ) {
                                    if ( this.debug ) {
                                        console.log( '        -> update (undelete)' );
                                    }

                                    data.properties[ propertyTypeId ].statusId = property.attributesModified.get( 'statusId' ) as number ?? item.statusId;
                                }
                                else {
                                    if ( this.debug ) {
                                        console.log( '        -> update' );
                                    }
                                }

                                // convert 'now' for DATE, TIME, TIMESTAMP
                                if ( value === 'now' && ( propertySet.valueType === 'DATE' || propertySet.valueType === 'TIME' || propertySet.valueType === 'TIMESTAMP' ) ) {
                                    value = new Date();
                                }

                                data.properties[ propertyTypeId ].value = value as AttributeValue;
                            }

                            if ( !noUpdate ) {
                                if ( this.debug ) {
                                    console.log( '            data', data );
                                }

                                if ( !isDeferred ) {
                                    this.deferRender();
                                }

                                this.props.updateItem(
                                    dataContext,
                                    itemId,
                                    data,
                                    additionalElusiveDataContexts
                                );
                            }
                            else {
                                if ( this.debug ) {
                                    console.log( '        -> no update' );
                                }
                            }
                        }
                        else {
                            if ( this.debug ) {
                                console.error( 'no item' );
                            }
                        }
                    }

                    // enumProperty
                    else if (
                        dataAndMetaModel.type === 'enumProperty' && layoutElementDataDescriptor.type === 'property'
                    ) {
                        let itemId = parentId ?? Object.keys( dataAndMetaModel.objects )[ 0 ] as number | undefined,
                            item : Item | undefined;

                        const propertySet    = metaModel[ schema ].getPropertySet( dataAndMetaModel.metaModels[ 0 ] ),
                              propertyTypeId = propertySet?.propertyTypeId;

                        if ( !itemId ) {
                            item = dataUtils.getData(
                                buildDataDescriptor( { attributePath: 'item.id' }, layoutElementDataDescriptor.dataObject ) as ObjectDataDescriptor,
                                dataStructure,
                                dataContext,
                                rootItemId!
                            )?.[ 0 ].object as Item;

                            itemId = item?.id;
                        }
                        else {
                            item = global.data[ dataContext ].items[ itemId ];
                        }

                        if ( this.debug ) {
                            console.log( '    enumProperty' );
                            console.log( '        itemId', itemId );
                            console.log( '        propertyTypeId', propertyTypeId );
                        }

                        if ( item && propertySet && propertyTypeId ) {
                            const stringKeys = Object.keys( value ) as string[],
                                  stringKeysMapping : {
                                      [ originalStringKey : string ] : string
                                  }          = this.remappedEnumValueKeys?.[ layoutElementDataDescriptorAlias ] ?? {};

                            let errorStrings : EnumValueErrors = {};

                            if ( layoutElementMappings?.setValueErrors?.[ layoutElementId ]?.length ) {
                                try {
                                    errorStrings = JSON.parse( layoutElementMappings.setValueErrors[ layoutElementId ][ 0 ] );
                                }
                                    // eslint-disable-next-line no-empty
                                catch ( e ) {
                                }
                            }

                            for ( const originalStringKey of stringKeys ) {
                                const stringKey                     = stringKeysMapping[ originalStringKey ] ?? originalStringKey,
                                      data : ItemAttributesSettable = {},
                                      entry                         = ( value as EnumUpdateValue )[ originalStringKey ];

                                let doNothing : boolean   = false,
                                    clearErrors : boolean = false,
                                    throwError : Error | LocalizableError | StackError | undefined;

                                data.enumProperties = {};
                                data.enumProperties[ propertyTypeId ] = {
                                    properties: {}
                                };

                                if ( this.debug ) {
                                    console.log( '            stringKey', originalStringKey );
                                    if ( stringKey !== originalStringKey ) {
                                        console.log( '                mapped to', stringKey );
                                    }
                                }

                                data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {};

                                // new entry to delete -> do nothing
                                if (
                                    entry.id === 0
                                    && entry.statusId === metaModel[ schema ].getStatus( 'deleted' )!.id
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (new - to delete)' );
                                    }

                                    doNothing = true;
                                    clearErrors = true;
                                }

                                // new entry and key and value empty -> do nothing
                                else if (
                                    entry.id === 0
                                    && (
                                        propertySet.isKeySettable
                                        && (
                                            !Object.hasOwn( entry, 'key' )
                                            || entry.key === undefined
                                            || entry.key === null
                                            || entry.key === ''
                                        )
                                    )
                                    && (
                                        propertySet.isValueSettable
                                        && (
                                            !Object.hasOwn( entry, 'value' )
                                            || entry.value === undefined
                                            || entry.value === null
                                            || entry.value === ''
                                            || ( Array.isArray( entry.value ) && !entry.value.length )
                                        )
                                    )
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (new - key and value empty)' );
                                    }

                                    doNothing = true;
                                    clearErrors = true;
                                }

                                // key is settable and empty -> error on key
                                else if (
                                    propertySet.isKeySettable
                                    && Object.hasOwn( entry, 'key' )
                                    && (
                                        entry.key === undefined
                                        || entry.key === null
                                        || entry.key === ''
                                    )
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (key empty)' );
                                    }

                                    throwError = new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.isEmpty', undefined, undefined, undefined, 20133 );
                                }

                                // value is settable and empty -> error on value
                                else if (
                                    propertySet.isValueSettable
                                    && entry.statusId !== metaModel[ schema ].getStatus( 'deleted' )!.id
                                    && Object.hasOwn( entry, 'value' )
                                    && (
                                        entry.value === undefined
                                        || entry.value === null
                                        || entry.value === ''
                                        || ( Array.isArray( entry.value ) && !entry.value.length )
                                    )
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (value empty)' );
                                    }

                                    throwError = new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.empty', undefined, undefined, undefined, 20143 );
                                }

                                // update
                                else {
                                    const enumProperty = item.enumProperties.get( propertyTypeId ),
                                          property     = enumProperty?.properties.get( stringKey );

                                    // convert 'now' for DATE, TIME, TIMESTAMP
                                    if ( entry.key === 'now' && ( propertySet.keyType === 'DATE' || propertySet.keyType === 'TIME' || propertySet.keyType === 'TIMESTAMP' ) ) {
                                        entry.key = new Date();
                                    }
                                    if ( entry.value === 'now' && ( propertySet.valueType === 'DATE' || propertySet.valueType === 'TIME' || propertySet.valueType === 'TIMESTAMP' ) ) {
                                        entry.value = new Date();
                                    }

                                    // init new property
                                    if ( !property ) {
                                        if ( this.debug ) {
                                            console.log( '            -> new' );
                                        }

                                        data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {
                                            statusId: item.statusId,
                                            ...entry
                                        };
                                    }

                                    // restore deleted property
                                    else if ( property.isDeleted() ) {
                                        if ( this.debug ) {
                                            console.log( '            -> update (undelete)' );
                                        }

                                        data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {
                                            statusId: property.attributesModified.get( 'statusId' ) as number ?? item.statusId,
                                            ...entry
                                        };
                                    }
                                    else {
                                        if ( this.debug ) {
                                            console.log( '            -> update' );
                                        }

                                        data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {
                                            ...entry
                                        };
                                    }
                                }

                                if ( this.debug ) {
                                    console.log( '            data', data );
                                    console.log( '            doNothing', doNothing );
                                    console.log( '            throwError', throwError );
                                }

                                // collect all errors and add stringKey
                                if ( !doNothing ) {
                                    try {
                                        if ( throwError ) {
                                            throw throwError;
                                        }

                                        if ( !isDeferred ) {
                                            this.deferRender();
                                        }

                                        this.props.updateItem(
                                            dataContext,
                                            itemId,
                                            data,
                                            additionalElusiveDataContexts
                                        );

                                        // remap changed key
                                        item = global.data[ dataContext ].items[ itemId ];
                                        if ( item ) {
                                            const enumProperty = item.enumProperties.get( propertyTypeId );

                                            if ( enumProperty ) {
                                                // remap key ordered enumProperty stringKeys
                                                if ( propertySet.isKeyOrdered ) {
                                                    for ( const oSK of stringKeys ) {
                                                        const valueId = ( value as EnumUpdateValue )[ oSK ].id;

                                                        if ( valueId !== 0 ) {
                                                            const propertiesIterator = enumProperty.properties.values();

                                                            let prop : Property | undefined,
                                                                done : any | undefined;

                                                            do {
                                                                ( { value: prop, done } = propertiesIterator.next() );

                                                                if ( done || prop?.id !== valueId ) {
                                                                    prop = undefined;
                                                                }
                                                                else {
                                                                    done = true;
                                                                }
                                                            }
                                                            while ( !done )

                                                            if ( prop && prop.stringKey !== oSK ) {
                                                                stringKeysMapping[ oSK ] = prop.stringKey;
                                                            }
                                                        }
                                                    }
                                                }
                                                else if ( entry.key !== '' && entry.key !== undefined && entry.key !== null ) {
                                                    const propertiesIterator = enumProperty.properties.values();

                                                    let prop : Property | undefined,
                                                        done : any | undefined;

                                                    do {
                                                        ( { value: prop, done } = propertiesIterator.next() );

                                                        if ( done || prop?.key !== entry.key ) {
                                                            prop = undefined;
                                                        }
                                                        else {
                                                            done = true;
                                                        }
                                                    }
                                                    while ( !done )

                                                    if ( prop && prop.stringKey !== originalStringKey ) {
                                                        stringKeysMapping[ originalStringKey ] = prop.stringKey;
                                                    }
                                                }
                                            }
                                        }

                                        // clear error for updated attributes or deleted property
                                        if ( errorStrings[ originalStringKey ] ) {
                                            const newStringKey = stringKeysMapping[ originalStringKey ] ?? originalStringKey;

                                            // remap errors if stringKey changed
                                            if ( newStringKey !== originalStringKey ) {
                                                errorStrings[ newStringKey ] = {
                                                    ...errorStrings[ originalStringKey ]
                                                };

                                                delete errorStrings[ originalStringKey ];
                                            }

                                            for ( const attribute of Object.keys( entry ) ) {
                                                delete errorStrings[ newStringKey ][ attribute ];
                                            }

                                            if ( entry?.statusId === metaModel[ schema ].getStatus( 'deleted' )!.id || !Object.keys( errorStrings[ newStringKey ] ).length ) {
                                                delete errorStrings[ newStringKey ];
                                            }
                                        }

                                        clearErrors = true;
                                    }
                                    catch ( e ) {
                                        const newStringKey = stringKeysMapping[ originalStringKey ] ?? originalStringKey;

                                        let attribute                        = '',
                                            errorMessage                     = String( e ),
                                            errorPlaceholders : Placeholders = {};

                                        if ( this.debug ) {
                                            console.log( '            -> error', e );
                                        }

                                        if ( e instanceof LocalizableError ) {
                                            errorMessage = e.name;
                                            errorPlaceholders = e.placeholders;

                                            if ( e.number ) {
                                                if ( e.number > 20120 && e.number < 20130 ) {
                                                    attribute = 'statusId';
                                                }
                                                else if ( e.number > 20130 && e.number < 20140 ) {
                                                    attribute = 'key';
                                                }
                                                else if ( e.number > 20140 && e.number < 20150 ) {
                                                    attribute = 'value';
                                                }
                                                else if ( e.number > 20150 && e.number < 20160 ) {
                                                    attribute = 'orderNum';
                                                }
                                            }
                                        }

                                        // remap errors if stringKey changed
                                        if ( newStringKey !== originalStringKey ) {
                                            errorStrings[ newStringKey ] = {
                                                ...errorStrings[ originalStringKey ]
                                            };

                                            delete errorStrings[ originalStringKey ];
                                        }

                                        errorStrings[ newStringKey ] = {
                                            ...errorStrings[ newStringKey ],
                                            [ attribute ]: {
                                                propertySetId: propertySet.id,
                                                errorMessage,
                                                errorPlaceholders
                                            }
                                        };
                                    }
                                }

                                if ( this.debug ) {
                                    console.log( '            clearErrors', clearErrors );
                                    console.log( '            stringKeysMapping', stringKeysMapping );
                                }

                                // cache remapped keys
                                this.remappedEnumValueKeys = {
                                    ...this.remappedEnumValueKeys,
                                    [ layoutElementDataDescriptorAlias ]: stringKeysMapping
                                };

                                if ( clearErrors ) {
                                    if ( errorStrings[ originalStringKey ] ) {
                                        delete errorStrings[ originalStringKey ];
                                    }

                                    if ( errorStrings[ stringKeysMapping[ originalStringKey ] ] ) {
                                        delete errorStrings[ stringKeysMapping[ originalStringKey ] ];
                                    }
                                }
                            }

                            // remap changed stringKeys in value
                            for ( const originalStringKey in stringKeysMapping ) {
                                ( value as EnumUpdateValue )[ stringKeysMapping[ originalStringKey ] ] = ( value as EnumUpdateValue )[ originalStringKey ];
                                delete ( value as EnumUpdateValue )[ originalStringKey ];
                            }

                            if ( Object.keys( errorStrings ).length ) {
                                throw ( JSON.stringify( errorStrings ) );
                            }
                        }
                        else {
                            if ( this.debug ) {
                                console.error( 'no item' );
                            }
                        }
                    }

                    // reference
                    else if (
                        dataAndMetaModel.type === 'reference' && layoutElementDataDescriptor.type === 'reference'
                    ) {
                        if ( Array.isArray( value ) ) {
                            value = value as ReferenceValue[];
                        }
                        else if ( value ) {
                            value = [ value as ReferenceValue ];
                        }
                        else {
                            value = [] as ReferenceValue[];
                        }

                        for ( const refValue of value ) {
                            const deletedStatusIds = metaModel[ schema ].getDeletedStatus().map( s => s.id );

                            if ( this.debug ) {
                                console.log( '    reference' );
                                console.log( '        referenceId', refValue.id );
                                console.log( '        referenceSetId', refValue.referenceSetId );
                            }

                            // delete
                            if ( deletedStatusIds.includes( refValue.statusId ) ) {
                                if ( this.debug ) {
                                    console.log( '        -> delete' );
                                }

                                // delete referenced new item if
                                if ( rootItemId ) {
                                    const reference = global.data[ dataContext ].references[ refValue.id ];

                                    if ( reference ) {
                                        const items = ( dataUtils.getData(
                                                  buildDataDescriptor( { attributePath: 'item.id' }, layoutElementDataDescriptor.dataObject ) as ObjectDataDescriptor,
                                                  dataStructure,
                                                  dataContext,
                                                  rootItemId
                                              ) ?? [] ).map( data => data.object as Item ),
                                              item  = items?.find( i => i.id === reference.srcItemId || i.id === reference.destItemId );

                                        if ( item ) {
                                            await this.onReferenceAction( layoutElementId, 'deleteReference', item, reference, originalEvent, props );
                                        }

                                        if ( !isDeferred ) {
                                            this.deferRender();
                                        }

                                        this.props.deleteReference(
                                            dataContext,
                                            refValue.id,
                                            true,
                                            additionalElusiveDataContexts
                                        );

                                        // new item -> delete
                                        // never delete item since the new save method exclude if no path to it exist (dangling)
                                        /*if ( item && item.id < 0 ) {
                                            if ( this.debug ) {
                                                console.log( '            -> delete referenced new item', item.id );
                                            }

                                            this.props.deleteItem(
                                                dataContext,
                                                item.id,
                                                true,
                                                additionalElusiveDataContexts
                                            );
                                        }*/
                                    }
                                }
                            }

                            // add
                            else if ( refValue.id === 0 ) {
                                if ( this.debug ) {
                                    console.log( '        -> new' );
                                }

                                let item : Item | undefined = refValue.item;

                                // add item for new reference to dataContext
                                if ( refValue.item ) {
                                    item = global.data[ dataContext ]?.items?.[ refValue.item.id ];

                                    if ( !item ) {
                                        if ( refValue.item.id > 0 ) {
                                            if ( this.debug ) {
                                                console.log( '            -> add item' );
                                            }

                                            let itemData : ItemAttributesSettable | undefined;

                                            if ( refValue.item.isModified() ) {
                                                itemData = refValue.item.toUpdateJSON( 'modified' );

                                                refValue.item.reset();
                                            }

                                            if ( this.debug ) {
                                                console.log( '                data', itemData );
                                            }

                                            if ( !isDeferred ) {
                                                this.deferRender();
                                            }

                                            item = this.props.addItem(
                                                dataContext,
                                                { ...refValue.item.toJSON( 'all' ) } as ItemAttributesForCreation,
                                                schema,
                                                additionalElusiveDataContexts
                                            );

                                            if ( itemData ) {
                                                this.props.updateItem(
                                                    dataContext,
                                                    item.id,
                                                    itemData,
                                                    additionalElusiveDataContexts
                                                );

                                                refValue.item.update( itemData );
                                            }
                                        }
                                        else {
                                            if ( this.debug ) {
                                                console.log( '            -> create item' );
                                            }

                                            const itemData = refValue.item.toUpdateJSON( 'all' );

                                            if ( this.debug ) {
                                                console.log( '                data', itemData );
                                            }

                                            if ( !isDeferred ) {
                                                this.deferRender();
                                            }

                                            item = this.props.addItem(
                                                dataContext,
                                                { itemTypeId: refValue.item.itemTypeId } as ItemAttributesForCreation,
                                                schema,
                                                additionalElusiveDataContexts
                                            );

                                            this.props.updateItem(
                                                dataContext,
                                                item.id,
                                                itemData,
                                                additionalElusiveDataContexts
                                            );
                                        }
                                    }
                                    else {
                                        if ( this.debug ) {
                                            console.log( '            -> update item' );
                                        }

                                        const itemData = this.getUpdateDataWithRemovedNewProperties( item, refValue.item );

                                        if ( this.debug ) {
                                            console.log( '                data', itemData );
                                        }

                                        if ( !isDeferred ) {
                                            this.deferRender();
                                        }

                                        this.props.updateItem(
                                            dataContext,
                                            item.id,
                                            itemData,
                                            additionalElusiveDataContexts
                                        );
                                    }
                                }

                                const data : ReferenceAttributesForCreation = {
                                    referenceSetId: refValue.referenceSetId,
                                    statusId      : refValue.statusId,
                                    srcItemId     : refValue.srcItemId === refValue.item?.id && item ? item.id : refValue.srcItemId,
                                    destItemId    : refValue.destItemId === refValue.item?.id && item ? item.id : refValue.destItemId
                                };

                                if ( this.debug ) {
                                    console.log( '            data', data );
                                }

                                if ( !isDeferred ) {
                                    this.deferRender();
                                }

                                const reference = this.props.addReference(
                                    dataContext,
                                    data,
                                    schema,
                                    additionalElusiveDataContexts
                                );

                                if ( item ) {
                                    if ( refValue.additionalData ) {
                                        refValue.additionalData.references.push( reference );

                                        try {
                                            this.applyAdditionalReferenceDataChanges(
                                                refValue,
                                                {
                                                    ...props,
                                                    elusiveData: {
                                                        ...props.elusiveData,
                                                        dataDescriptorMappings,
                                                        layoutElementMappings
                                                    }
                                                },
                                                refValue.item?.id && item
                                                    ? {
                                                        [ refValue.item.id ]: item.id
                                                    }
                                                    : undefined
                                            );
                                        }
                                        catch ( e ) {
                                            console.error( e );
                                        }
                                    }

                                    await this.onReferenceAction( layoutElementId, 'addReference', item, reference, originalEvent, props );
                                }
                            }

                            // update
                            else {
                                if ( this.debug ) {
                                    console.log( '        -> update' );
                                }

                                const data : ReferenceAttributesSettable = {
                                    statusId: refValue.statusId
                                };

                                if ( this.debug ) {
                                    console.log( '            data', data );
                                }

                                const reference = global.data[ dataContext ]?.references?.[ refValue.id ];
                                if ( reference && refValue.item ) {
                                    if ( deletedStatusIds.includes( reference.statusId ) && !deletedStatusIds.includes( refValue.statusId ) ) {
                                        await this.onReferenceAction( layoutElementId, 'addReference', refValue.item, reference, originalEvent, props );
                                    }
                                    else if ( !deletedStatusIds.includes( reference.statusId ) && deletedStatusIds.includes( refValue.statusId ) ) {
                                        await this.onReferenceAction( layoutElementId, 'deleteReference', refValue.item, reference, originalEvent, props );
                                    }
                                }

                                let item : Item | undefined = refValue.item;

                                // update item for updated reference to dataContext
                                if ( refValue.item ) {
                                    item = global.data[ dataContext ]?.items?.[ refValue.item.id ];

                                    if ( item ) {
                                        if ( this.debug ) {
                                            console.log( '            -> update item' );
                                        }

                                        const itemData = this.getUpdateDataWithRemovedNewProperties( item, refValue.item );

                                        if ( this.debug ) {
                                            console.log( '                data', itemData );
                                        }

                                        if ( !isDeferred ) {
                                            this.deferRender();
                                        }

                                        this.props.updateItem(
                                            dataContext,
                                            item.id,
                                            itemData,
                                            additionalElusiveDataContexts
                                        );
                                    }
                                }

                                if ( !isDeferred ) {
                                    this.deferRender();
                                }

                                this.props.updateReference(
                                    dataContext,
                                    refValue.id,
                                    data,
                                    additionalElusiveDataContexts
                                );

                                if ( item ) {
                                    if ( refValue.additionalData ) {
                                        try {
                                            this.applyAdditionalReferenceDataChanges(
                                                refValue,
                                                {
                                                    ...props,
                                                    elusiveData: {
                                                        ...props.elusiveData,
                                                        dataDescriptorMappings,
                                                        layoutElementMappings
                                                    }
                                                }
                                            );
                                        }
                                        catch ( e ) {
                                            console.error( e );
                                        }
                                    }

                                    await this.onReferenceAction( layoutElementId, 'addReference', item, reference, originalEvent, props );
                                }
                            }
                        }
                    }

                    // referenceProperty
                    else if (
                        dataAndMetaModel.type === 'referenceProperty' && layoutElementDataDescriptor.type === 'referenceProperty'
                        && referencePropertyAttributesSettable.includes( layoutElementDataDescriptor.attribute )
                    ) {
                        let referenceId = parentId ?? Object.keys( dataAndMetaModel.objects )[ 0 ] as number | undefined,
                            reference : Reference | undefined;

                        const referencePropertySet = metaModel[ schema ].getReferencePropertySet( dataAndMetaModel.metaModels[ 0 ] ),
                              propertyTypeId       = referencePropertySet?.propertyTypeId;

                        if ( !referenceId ) {
                            if ( rootItemId ) {
                                reference = dataUtils.getData(
                                    buildDataDescriptor( { attributePath: 'reference.id' }, layoutElementDataDescriptor.dataObject ) as ObjectDataDescriptor,
                                    dataStructure,
                                    dataContext,
                                    rootItemId
                                )?.[ 0 ].object as Reference;

                                referenceId = reference?.id;
                            }
                        }
                        else {
                            reference = global.data[ dataContext ].references[ referenceId ];
                        }

                        if ( this.debug ) {
                            console.log( '    referenceProperty' );
                            console.log( '        referenceId', referenceId );
                            console.log( '        propertyTypeId', propertyTypeId );
                        }

                        if ( referenceId && reference && propertyTypeId ) {
                            const data : ReferenceAttributesSettable = {};

                            let noUpdate = false;

                            data.properties = {};
                            data.properties[ propertyTypeId ] = {};

                            // no value -> delete property (will be reverted by Item.update)
                            if ( value === undefined || value === null || value === '' || ( Array.isArray( value ) && !value.length ) ) {
                                if ( reference.properties.has( propertyTypeId ) ) {
                                    if ( this.debug ) {
                                        console.log( '        -> delete' );
                                    }

                                    data.properties[ propertyTypeId ].statusId = metaModel[ schema ].getStatus( 'deleted' )!.id;
                                }
                                else {
                                    noUpdate = true;
                                }
                            }

                            // update
                            else {
                                const property = reference.properties.get( propertyTypeId );

                                // init new property
                                if ( !property ) {
                                    if ( this.debug ) {
                                        console.log( '        -> new' );
                                    }

                                    data.properties[ propertyTypeId ].statusId = reference.statusId;
                                }

                                // restore deleted property
                                else if ( property.isDeleted() ) {
                                    if ( this.debug ) {
                                        console.log( '        -> update (undelete)' );
                                    }

                                    data.properties[ propertyTypeId ].statusId = property.attributesModified.get( 'statusId' ) as number ?? reference.statusId;
                                }
                                else {
                                    if ( this.debug ) {
                                        console.log( '        -> update' );
                                    }
                                }

                                // convert 'now' for DATE, TIME, TIMESTAMP
                                if ( value === 'now' && ( referencePropertySet.valueType === 'DATE' || referencePropertySet.valueType === 'TIME' || referencePropertySet.valueType === 'TIMESTAMP' ) ) {
                                    value = new Date();
                                }

                                data.properties[ propertyTypeId ].value = value as AttributeValue;
                            }

                            if ( this.debug ) {
                                console.log( '            data', data );
                            }

                            if ( !noUpdate ) {
                                if ( this.debug ) {
                                    console.log( '            data', data );
                                }

                                if ( !isDeferred ) {
                                    this.deferRender();
                                }

                                this.props.updateReference(
                                    dataContext,
                                    referenceId,
                                    data,
                                    additionalElusiveDataContexts
                                );
                            }
                            else {
                                if ( this.debug ) {
                                    console.log( '        -> no update' );
                                }
                            }
                        }
                        else {
                            if ( this.debug ) {
                                console.error( 'no reference' );
                            }
                        }
                    }

                    // referenceEnumProperty
                    else if (
                        dataAndMetaModel.type === 'enumReferenceProperty' && layoutElementDataDescriptor.type === 'referenceProperty'
                    ) {
                        let referenceId = parentId ?? Object.keys( dataAndMetaModel.objects )[ 0 ] as number | undefined,
                            reference : Reference | undefined;

                        const referencePropertySet = metaModel[ schema ].getReferencePropertySet( dataAndMetaModel.metaModels[ 0 ] ),
                              propertyTypeId       = referencePropertySet?.propertyTypeId;

                        if ( !referenceId ) {
                            if ( rootItemId ) {
                                reference = dataUtils.getData(
                                    buildDataDescriptor( { attributePath: 'reference.id' }, layoutElementDataDescriptor.dataObject ) as ObjectDataDescriptor,
                                    dataStructure,
                                    dataContext,
                                    rootItemId
                                )?.[ 0 ].object as Reference;

                                referenceId = reference?.id;
                            }
                        }
                        else {
                            reference = global.data[ dataContext ].references[ referenceId ];
                        }

                        if ( this.debug ) {
                            console.log( '    enumReferenceProperty' );
                            console.log( '        referenceId', referenceId );
                            console.log( '        propertyTypeId', propertyTypeId );
                        }

                        if ( referenceId && reference && referencePropertySet && propertyTypeId ) {
                            const stringKeys = Object.keys( value ) as string[],
                                  stringKeysMapping : {
                                      [ originalStringKey : string ] : string
                                  }          = this.remappedReferenceEnumValueKeys?.[ layoutElementDataDescriptorAlias ] ?? {};

                            let errorStrings : EnumValueErrors = {};

                            if ( layoutElementMappings?.setValueErrors?.[ layoutElementId ]?.length ) {
                                try {
                                    errorStrings = JSON.parse( layoutElementMappings.setValueErrors[ layoutElementId ][ 0 ] );
                                }
                                    // eslint-disable-next-line no-empty
                                catch ( e ) {
                                }
                            }

                            for ( const originalStringKey of stringKeys ) {
                                const stringKey                          = stringKeysMapping[ originalStringKey ] ?? originalStringKey,
                                      data : ReferenceAttributesSettable = {},
                                      entry                              = ( value as EnumUpdateValue )[ originalStringKey ];

                                let doNothing : boolean   = false,
                                    clearErrors : boolean = false,
                                    throwError : Error | LocalizableError | StackError | undefined;

                                data.enumProperties = {};
                                data.enumProperties[ propertyTypeId ] = {
                                    properties: {}
                                };

                                if ( this.debug ) {
                                    console.log( '            stringKey', originalStringKey );
                                    if ( stringKey !== originalStringKey ) {
                                        console.log( '                mapped to', stringKey );
                                    }
                                }

                                data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {};

                                // new entry to delete -> do nothing
                                if (
                                    entry.id === 0
                                    && entry.statusId === metaModel[ schema ].getStatus( 'deleted' )!.id
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (new - to delete)' );
                                    }

                                    doNothing = true;
                                    clearErrors = true;
                                }

                                // new entry and key and value empty -> do nothing
                                else if (
                                    entry.id === 0
                                    && (
                                        referencePropertySet.isKeySettable
                                        && (
                                            !Object.hasOwn( entry, 'key' )
                                            || entry.key === undefined
                                            || entry.key === null
                                            || entry.key === ''
                                        )
                                    )
                                    && (
                                        referencePropertySet.isValueSettable
                                        && (
                                            !Object.hasOwn( entry, 'value' )
                                            || entry.value === undefined
                                            || entry.value === null
                                            || entry.value === ''
                                            || ( Array.isArray( entry.value ) && !entry.value.length )
                                        )
                                    )
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (new - key and value empty)' );
                                    }

                                    doNothing = true;
                                    clearErrors = true;
                                }

                                // key is settable and empty -> error on key
                                else if (
                                    referencePropertySet.isKeySettable
                                    && Object.hasOwn( entry, 'key' )
                                    && (
                                        entry.key === undefined
                                        || entry.key === null
                                        || entry.key === ''
                                    )
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (key empty)' );
                                    }

                                    throwError = new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.isEmpty', undefined, undefined, undefined, 40133 );
                                }

                                // value is settable and empty -> error on value
                                else if (
                                    referencePropertySet.isValueSettable
                                    && entry.statusId !== metaModel[ schema ].getStatus( 'deleted' )!.id
                                    && Object.hasOwn( entry, 'value' )
                                    && (
                                        entry.value === undefined
                                        || entry.value === null
                                        || entry.value === ''
                                        || ( Array.isArray( entry.value ) && !entry.value.length )
                                    )
                                ) {
                                    if ( this.debug ) {
                                        console.log( '            -> do nothing (value empty)' );
                                    }

                                    throwError = new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.empty', undefined, undefined, undefined, 40143 );
                                }

                                // update
                                else {
                                    const enumProperty = reference.enumProperties.get( propertyTypeId ),
                                          property     = enumProperty?.properties.get( stringKey );

                                    // convert 'now' for DATE, TIME, TIMESTAMP
                                    if ( entry.key === 'now' && ( referencePropertySet.keyType === 'DATE' || referencePropertySet.keyType === 'TIME' || referencePropertySet.keyType === 'TIMESTAMP' ) ) {
                                        entry.key = new Date();
                                    }
                                    if ( entry.value === 'now' && ( referencePropertySet.valueType === 'DATE' || referencePropertySet.valueType === 'TIME' || referencePropertySet.valueType === 'TIMESTAMP' ) ) {
                                        entry.value = new Date();
                                    }

                                    // init new property
                                    if ( !property ) {
                                        if ( this.debug ) {
                                            console.log( '            -> new' );
                                        }

                                        data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {
                                            statusId: reference.statusId,
                                            ...entry
                                        };
                                    }

                                    // restore deleted property
                                    else if ( property.isDeleted() ) {
                                        if ( this.debug ) {
                                            console.log( '            -> update (undelete)' );
                                        }

                                        data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {
                                            statusId: property.attributesModified.get( 'statusId' ) as number ?? reference.statusId,
                                            ...entry
                                        };
                                    }
                                    else {
                                        if ( this.debug ) {
                                            console.log( '            -> update' );
                                        }

                                        data.enumProperties[ propertyTypeId ].properties![ stringKey ] = {
                                            ...entry
                                        };
                                    }
                                }

                                if ( this.debug ) {
                                    console.log( '            data', data );
                                    console.log( '            doNothing', doNothing );
                                    console.log( '            throwError', throwError );
                                }

                                // collect all errors and add stringKey
                                if ( !doNothing ) {
                                    try {
                                        if ( throwError ) {
                                            throw throwError;
                                        }

                                        if ( !isDeferred ) {
                                            this.deferRender();
                                        }

                                        this.props.updateReference(
                                            dataContext,
                                            referenceId,
                                            data,
                                            additionalElusiveDataContexts
                                        );

                                        // remap changed key
                                        reference = global.data[ dataContext ].references[ referenceId ];
                                        if ( reference ) {
                                            const enumProperty = reference.enumProperties.get( propertyTypeId );

                                            if ( enumProperty ) {
                                                // remap key ordered enumProperty stringKeys
                                                if ( referencePropertySet.isKeyOrdered ) {
                                                    for ( const oSK of stringKeys ) {
                                                        const valueId = ( value as EnumUpdateValue )[ oSK ].id;

                                                        if ( valueId !== 0 ) {
                                                            const propertiesIterator = enumProperty.properties.values();

                                                            let prop : ReferenceProperty | undefined,
                                                                done : any | undefined;

                                                            do {
                                                                ( { value: prop, done } = propertiesIterator.next() );

                                                                if ( done || prop?.id !== valueId ) {
                                                                    prop = undefined;
                                                                }
                                                                else {
                                                                    done = true;
                                                                }
                                                            }
                                                            while ( !done )

                                                            if ( prop && prop.stringKey !== oSK ) {
                                                                stringKeysMapping[ oSK ] = prop.stringKey;
                                                            }
                                                        }
                                                    }
                                                }
                                                else if ( entry.key !== '' && entry.key !== undefined && entry.key !== null ) {
                                                    const propertiesIterator = enumProperty.properties.values();

                                                    let prop : ReferenceProperty | undefined,
                                                        done : any | undefined;

                                                    do {
                                                        ( { value: prop, done } = propertiesIterator.next() );

                                                        if ( done || prop?.key !== entry.key ) {
                                                            prop = undefined;
                                                        }
                                                        else {
                                                            done = true;
                                                        }
                                                    }
                                                    while ( !done )

                                                    if ( prop && prop.stringKey !== originalStringKey ) {
                                                        stringKeysMapping[ originalStringKey ] = prop.stringKey;
                                                    }
                                                }
                                            }
                                        }

                                        // clear error for updated attributes or deleted property
                                        if ( errorStrings[ originalStringKey ] ) {
                                            const newStringKey = stringKeysMapping[ originalStringKey ] ?? originalStringKey;

                                            // remap errors if stringKey changed
                                            if ( newStringKey !== originalStringKey ) {
                                                errorStrings[ newStringKey ] = {
                                                    ...errorStrings[ originalStringKey ]
                                                };

                                                delete errorStrings[ originalStringKey ];
                                            }

                                            for ( const attribute of Object.keys( entry ) ) {
                                                delete errorStrings[ newStringKey ][ attribute ];
                                            }

                                            if ( entry?.statusId === metaModel[ schema ].getStatus( 'deleted' )!.id || !Object.keys( errorStrings[ newStringKey ] ).length ) {
                                                delete errorStrings[ newStringKey ];
                                            }
                                        }

                                        clearErrors = true;
                                    }
                                    catch ( e ) {
                                        const newStringKey = stringKeysMapping[ originalStringKey ] ?? originalStringKey;

                                        let attribute                        = '',
                                            errorMessage                     = String( e ),
                                            errorPlaceholders : Placeholders = {};

                                        if ( this.debug ) {
                                            console.log( '            -> error', e );
                                        }

                                        if ( e instanceof LocalizableError ) {
                                            errorMessage = e.name;
                                            errorPlaceholders = e.placeholders;

                                            if ( e.number ) {
                                                if ( e.number > 40120 && e.number < 40130 ) {
                                                    attribute = 'statusId';
                                                }
                                                else if ( e.number > 40130 && e.number < 40140 ) {
                                                    attribute = 'key';
                                                }
                                                else if ( e.number > 40140 && e.number < 40150 ) {
                                                    attribute = 'value';
                                                }
                                                else if ( e.number > 40150 && e.number < 40160 ) {
                                                    attribute = 'orderNum';
                                                }
                                            }
                                        }

                                        // remap errors if stringKey changed
                                        if ( newStringKey !== originalStringKey ) {
                                            errorStrings[ newStringKey ] = {
                                                ...errorStrings[ originalStringKey ]
                                            };

                                            delete errorStrings[ originalStringKey ];
                                        }

                                        errorStrings[ newStringKey ] = {
                                            ...errorStrings[ newStringKey ],
                                            [ attribute ]: {
                                                referencePropertySetId: referencePropertySet.id,
                                                errorMessage,
                                                errorPlaceholders
                                            }
                                        };
                                    }
                                }

                                if ( this.debug ) {
                                    console.log( '            clearErrors', clearErrors );
                                    console.log( '            stringKeysMapping', stringKeysMapping );
                                }

                                // cache remapped keys
                                this.remappedReferenceEnumValueKeys = {
                                    ...this.remappedReferenceEnumValueKeys,
                                    [ layoutElementDataDescriptorAlias ]: stringKeysMapping
                                };

                                if ( clearErrors ) {
                                    if ( errorStrings[ originalStringKey ] ) {
                                        delete errorStrings[ originalStringKey ];
                                    }

                                    if ( errorStrings[ stringKeysMapping[ originalStringKey ] ] ) {
                                        delete errorStrings[ stringKeysMapping[ originalStringKey ] ];
                                    }
                                }
                            }

                            // remap changed stringKeys in value
                            for ( const originalStringKey in stringKeysMapping ) {
                                ( value as EnumUpdateValue )[ stringKeysMapping[ originalStringKey ] ] = ( value as EnumUpdateValue )[ originalStringKey ];
                                delete ( value as EnumUpdateValue )[ originalStringKey ];
                            }

                            if ( Object.keys( errorStrings ).length ) {
                                throw ( JSON.stringify( errorStrings ) );
                            }
                        }
                        else {
                            if ( this.debug ) {
                                console.error( 'no reference' );
                            }
                        }
                    }
                }
                catch ( e ) {
                    let errorString = String( e );

                    if ( this.debug ) {
                        console.log( '            -> error', errorString );
                    }

                    if ( e instanceof LocalizableError ) {
                        errorString = e.toString( false );
                    }

                    retVal.push( errorString );
                }
            }
        }

        if ( !retVal.length ) {
            retVal = undefined;
        }

        // force render if deferred
        if ( !isDeferred && this.deferredRender ) {
            setTimeout( () => {
                this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
            }, 5 );
        }

        if ( this.debug ) {
            console.log( '    return', retVal );
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `color: #${debugColorScheme.setValueForLayoutElement}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    /**
     * Validates the given value for the given layoutElement (form field)
     *
     * @param layoutElement
     * @param parentId
     * @param layoutElementMappings
     * @param value
     * @param dataDescriptorMappings
     * @param dataDescriptors
     * @param checkItemKeyUniqueAndReferenceInsertable
     * @param originalEvent
     * @param variables
     *
     * @private
     */
    async validateValueForLayoutElement( layoutElement : FormFieldData, parentId : number | undefined, layoutElementMappings : LayoutElementMappings, value : any, dataDescriptorMappings : DataDescriptorMappings, dataDescriptors : DataDescriptors, checkItemKeyUniqueAndReferenceInsertable : boolean = false, originalEvent? : any, variables? : Variables ) : Promise<string[] | undefined> {
        const startTime                            = this.debug ? performance.now() : 0,
              layoutElementsData                   = layoutElementMappings?.cache ?? {},
              layoutElementId                      = layoutElement.data.id,
              realLayoutElementId                  = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parentId ),
              layoutElementData                    = {
                  ...( layoutElementsData[ realLayoutElementId ] ?? layoutElementsData[ layoutElementId ] ),
                  ...variables?.repeatedElements?.[ layoutElement.data.key ]?.[ parentId ?? 0 ]
              },
              {
                  disabled,
                  readOnly,
                  visibility
              }                                    = layoutElementData,
              layoutElementDataDescriptorAlias     = layoutElement.dataDescriptorAlias ?? '',
              realLayoutElementDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( layoutElementDataDescriptorAlias, parentId ),
              dataDescriptor                       = dataDescriptors[ realLayoutElementDataDescriptorAlias ] ?? dataDescriptors[ layoutElementDataDescriptorAlias ],
              isDeferred                           = this.deferredRender;

        if ( this.debug ) {
            console.groupCollapsed( `%c${`%cForm - %cvalidateValueForLayoutElement - ${layoutElementData.key}%c`.asLogLine( 101 )}`, 'font-weight: normal;', 'font-weight: bold;', `color: #${debugColorScheme.validator};`, 'font-weight: normal;' );
        }

        let retVal : string[] | undefined = [];

        // do not validate value for read-only or hidden layoutElements
        if ( !readOnly && !disabled && visibility === 'visible' ) {
            const {
                      uid,
                      schema,
                      dataContext,
                      dataStructure,
                      rootItemId,
                      additionalCheckDataContexts
                  }                = this.props,
                  dataAndMetaModel = dataDescriptorMappings?.dataAndMetaModel?.[ layoutElementDataDescriptorAlias ],
                  itemType         = dataAndMetaModel?.type === 'item' ? metaModel[ schema ].getItemType( dataAndMetaModel.metaModels[ 0 ] ?? 0 ) : undefined;

            if ( Array.isArray( value ) ) {
                if ( !value.length ) {
                    value = undefined;
                }
                else if ( value.length === 1 ) {
                    value = value[ 0 ];
                }
            }

            // validate required (item.key and item.name are always required)
            if (
                (
                    layoutElementData.required
                    || (
                        dataDescriptor
                        && dataDescriptor.type === 'item'
                        && (
                            ( dataDescriptor.attribute === 'key' && !itemType?.isKeyAutogenerated )
                            || dataDescriptor.attribute === 'name'
                        )
                    )
                )
                && (
                    value === ''
                    || value === undefined
                    || value === null
                )
            ) {
                retVal.push( __( 'YN.base.form.field.value.required' ) );
            }

            // special metaModel validations
            if ( dataDescriptor ) {
                if ( !retVal.length && value !== undefined && value !== null && value !== '' ) {

                    // item - check key uniqueness
                    if ( dataDescriptor.type === 'item' && dataDescriptorMappings?.itemKeyCheck?.[ realLayoutElementDataDescriptorAlias ] && parentId !== dataDescriptorMappings.itemKeyCheck[ realLayoutElementDataDescriptorAlias ].itemId ) {
                        const itemKeyCheck = dataDescriptorMappings?.itemKeyCheck?.[ realLayoutElementDataDescriptorAlias ];

                        // item - validate key uniqueness
                        if ( itemKeyCheck ) {

                            // key check is running
                            if ( value === itemKeyCheck.lastCheckedKey && itemKeyCheck.isKeyUnique === undefined ) {
                                retVal.push( __( 'YN.base.form.item.key.check.unique' ) );
                            }

                            // key is not unique
                            else if ( value === itemKeyCheck.lastCheckedKey && itemKeyCheck.isKeyUnique === false ) {
                                retVal.push( __( 'YN.base.form.item.key.error.notUnique' ) );
                            }

                            // check
                            else if ( checkItemKeyUniqueAndReferenceInsertable && value !== itemKeyCheck.lastCheckedKey && value !== itemKeyCheck.originalKey ) {
                                retVal.push( __( 'YN.base.form.item.key.check.unique' ) );

                                if ( !isDeferred ) {
                                    this.deferRender();
                                }

                                const componentUid      = this.getItemKeyCheckComponentId(), // own componentUid to avoid showing loading mask on form
                                      requestUid        = this.getItemKeyCheckRequestId( layoutElementDataDescriptorAlias, parentId ),
                                      checkDataContexts = ( [ dataContext ] as string[] ).concat( additionalCheckDataContexts ),
                                      endUpCheckFnc     = async ( isKeyUnique : boolean ) => {
                                          const { elusiveData } = this.props;

                                          if ( elusiveData?.dataDescriptorMappings ) {
                                              const setValueErrors               = await this.setValueForLayoutElement( layoutElement, parentId, value ),
                                                    resultDataDescriptorMappings = {
                                                        ...elusiveData.dataDescriptorMappings,
                                                        editValue   : {
                                                            ...elusiveData.dataDescriptorMappings.editValue
                                                        },
                                                        itemKeyCheck: {
                                                            ...elusiveData.dataDescriptorMappings.itemKeyCheck
                                                        }
                                                    },
                                                    resultLayoutElementMappings  = {
                                                        ...elusiveData.layoutElementMappings,
                                                        setValueErrors: {
                                                            ...elusiveData.layoutElementMappings?.setValueErrors
                                                        }
                                                    };

                                              for ( const itemKeyCheckDataDescriptorAlias of Object.keys( resultDataDescriptorMappings.itemKeyCheck ?? {} ) ) {
                                                  if ( resultDataDescriptorMappings.itemKeyCheck[ itemKeyCheckDataDescriptorAlias ].itemId === itemKeyCheck.itemId ) {
                                                      resultDataDescriptorMappings.itemKeyCheck[ itemKeyCheckDataDescriptorAlias ] = {
                                                          ...resultDataDescriptorMappings.itemKeyCheck[ itemKeyCheckDataDescriptorAlias ],
                                                          lastCheckedKey: value,
                                                          isKeyUnique
                                                      };
                                                  }
                                              }

                                              // set errors
                                              if ( setValueErrors ) {
                                                  resultLayoutElementMappings.setValueErrors[ realLayoutElementId ] = setValueErrors;
                                              }
                                              // no errors -> delete set errors and edit value
                                              else {
                                                  delete resultLayoutElementMappings.setValueErrors[ realLayoutElementId ];
                                                  delete resultDataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ];

                                                  await this.onTriggerAction( realLayoutElementId, originalEvent, this.props );
                                              }

                                              this.deferRender();

                                              this.props.setElusiveData( uid, {
                                                  dataDescriptorMappings: resultDataDescriptorMappings,
                                                  layoutElementMappings : resultLayoutElementMappings
                                              } );
                                          }

                                          // force render if deferred
                                          if ( !isDeferred && this.deferredRender ) {
                                              setTimeout( () => {
                                                  this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                                              }, 5 );
                                          }
                                      };

                                let keyIsUnique : boolean = true;

                                // abort running check
                                xhr.abort( componentUid, requestUid );

                                for ( const checkDataContext of checkDataContexts ) {
                                    if ( !checkKeyUniqueFeOnly( checkDataContext, itemKeyCheck.itemTypeId, value, itemKeyCheck.itemId ).keyIsUnique ) {
                                        keyIsUnique = false;
                                        break;
                                    }
                                }

                                // check key uniqueness against frontend data
                                if ( !keyIsUnique ) {
                                    const delayFnc = async () => {
                                        await Promise.resolve();
                                        await endUpCheckFnc( false );
                                    };

                                    void delayFnc();
                                }

                                // check uniqueness against database
                                else {
                                    const doLoad = async () => {
                                        const params : CheckItemKeyUniquenessParameters = {
                                                  schema  : schema,
                                                  status  : 'all',
                                                  itemType: itemKeyCheck.itemTypeId,
                                                  key     : value
                                              },
                                              response                                  = await xhr.post(
                                                  `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                                                  params,
                                                  undefined,
                                                  componentUid,
                                                  requestUid
                                              );

                                        if ( response?.data ) {
                                            const responseData = response.data as CheckItemKeyUniquenessResult;

                                            if ( responseData.success ) {
                                                keyIsUnique = responseData.data.keyIsUnique;

                                                // check database item having the key within frontend data (item could be deleted or key changed)
                                                if ( !keyIsUnique && responseData.data.itemId ) {
                                                    keyIsUnique = true;

                                                    for ( const checkDataContext of checkDataContexts ) {
                                                        const item = global.data[ checkDataContext ]?.items[ responseData.data.itemId ];

                                                        if ( !item || ( item.key === value && item.status.name !== 'deleted' ) ) {
                                                            keyIsUnique = false;
                                                            break;
                                                        }
                                                    }
                                                }

                                                await endUpCheckFnc( keyIsUnique );
                                            }
                                        }
                                    };

                                    doLoad().catch( ( error ) => {
                                        console.log( error );
                                    } );

                                    // do not set to elusiveData, just modify given dataDescriptorMappings!
                                    dataDescriptorMappings.itemKeyCheck = {
                                        ...dataDescriptorMappings.itemKeyCheck
                                    };
                                    for ( const itemKeyCheckDataDescriptorAlias of Object.keys( dataDescriptorMappings.itemKeyCheck ?? {} ) ) {
                                        if ( dataDescriptorMappings.itemKeyCheck[ itemKeyCheckDataDescriptorAlias ].itemId === itemKeyCheck.itemId ) {
                                            dataDescriptorMappings.itemKeyCheck[ itemKeyCheckDataDescriptorAlias ] = {
                                                ...dataDescriptorMappings.itemKeyCheck[ itemKeyCheckDataDescriptorAlias ],
                                                lastCheckedKey: value,
                                                isKeyUnique   : undefined
                                            };
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // reference - validate insertable
                    if ( dataDescriptor.type === 'reference' && dataDescriptorMappings?.referenceInsertableCheck?.[ realLayoutElementDataDescriptorAlias ] ) {
                        const referenceInsertableCheck       = dataDescriptorMappings?.referenceInsertableCheck[ realLayoutElementDataDescriptorAlias ],
                              deletedReferenceIds : number[] = [],
                              deletedStatusIds               = metaModel[ schema ].getDeletedStatus().map( s => s.id );

                        let originalValue = dataDescriptorMappings?.originalValue?.[ layoutElementDataDescriptorAlias ];

                        if ( Array.isArray( value ) ) {
                            value = value as ReferenceValue[];
                        }
                        else if ( value ) {
                            value = [ value as ReferenceValue ];
                        }
                        else {
                            value = [] as ReferenceValue[];
                        }
                        if ( Array.isArray( originalValue ) ) {
                            originalValue = originalValue as ReferenceValue[];
                        }
                        else if ( originalValue ) {
                            originalValue = [ originalValue as ReferenceValue ];
                        }
                        else {
                            originalValue = [] as ReferenceValue[];
                        }

                        // get deleted references
                        // first from originally loaded references current status (delete)
                        for ( const refValue of originalValue ) {
                            if ( global.data[ dataContext ].references[ refValue.id ]?.isDeleted() && !deletedReferenceIds.includes( refValue.id ) ) {
                                deletedReferenceIds.push( refValue.id );
                            }
                        }
                        // second from value (delete or undelete)
                        for ( const refValue of value ) {
                            if ( deletedStatusIds.includes( refValue.statusId ) ) {
                                if ( !deletedReferenceIds.includes( refValue.id ) ) {
                                    deletedReferenceIds.push( refValue.id );
                                }
                            }
                            else if ( deletedReferenceIds.includes( refValue.id ) ) {
                                const index = deletedReferenceIds.findIndex( id => id === refValue.id );

                                if ( index !== -1 ) {
                                    deletedReferenceIds.splice( index, 1 );
                                }
                            }
                        }
                        deletedReferenceIds.sort();

                        for ( const refValue of value ) {
                            if ( refValue.id === 0 && !deletedStatusIds.includes( refValue.statusId ) ) {
                                const newReferenceInsertableCheck : DataDescriptorToReferenceInsertableCheckData = {
                                          schema,
                                          referenceSetId       : refValue.referenceSetId,
                                          srcItemId            : refValue.srcItemId,
                                          destItemId           : refValue.destItemId,
                                          referenceIdsToExclude: deletedReferenceIds
                                      },
                                      parametersDiff                                                             = objectUtils.getDiffKeys( referenceInsertableCheck ?? {}, newReferenceInsertableCheck, undefined, undefined, [ 'isReferenceInsertable', 'errorMessage', 'errorPlaceholders' ] );

                                // insertable check is running
                                if ( !parametersDiff.length && referenceInsertableCheck.isReferenceInsertable === undefined ) {
                                    retVal.push( __( 'YN.base.form.reference.cardinality.check.insertable' ) );
                                }

                                // not insertable
                                else if ( !parametersDiff.length && referenceInsertableCheck.isReferenceInsertable === false ) {
                                    if ( referenceInsertableCheck.errorMessage ) {
                                        retVal.push( __( referenceInsertableCheck.errorMessage, referenceInsertableCheck.errorPlaceholders ) );
                                    }
                                    else {
                                        retVal.push( __( 'YN.base.form.reference.cardinality.error.notInsertable' ) );
                                    }
                                }

                                // check
                                else if ( checkItemKeyUniqueAndReferenceInsertable && parametersDiff.length ) {
                                    retVal.push( __( 'YN.base.form.reference.cardinality.check.insertable' ) );

                                    if ( !isDeferred ) {
                                        this.deferRender();
                                    }

                                    const componentUid      = `${uid}_checkRI`, // own componentUid to avoid showing loading mask on form
                                          requestUid        = `checkReferenceInsertable_${layoutElementDataDescriptorAlias}`,
                                          checkDataContexts = ( [ dataContext ] as string[] ).concat( additionalCheckDataContexts ),
                                          endUpCheckFnc     = async ( isReferenceInsertable : boolean, reason? : {
                                              name : string;
                                              placeholders? : Placeholders;
                                          } ) => {
                                              const { elusiveData } = this.props;

                                              if ( elusiveData?.dataDescriptorMappings ) {
                                                  const setValueErrors               = await this.setValueForLayoutElement( layoutElement, parentId, value ),
                                                        resultDataDescriptorMappings = {
                                                            ...elusiveData.dataDescriptorMappings,
                                                            editValue               : {
                                                                ...elusiveData.dataDescriptorMappings.editValue
                                                            },
                                                            referenceInsertableCheck: {
                                                                ...elusiveData.dataDescriptorMappings.referenceInsertableCheck
                                                            }
                                                        },
                                                        resultLayoutElementMappings  = {
                                                            ...elusiveData.layoutElementMappings,
                                                            setValueErrors: {
                                                                ...elusiveData.layoutElementMappings?.setValueErrors
                                                            }
                                                        };

                                                  for ( const referenceInsertableCheckDataDescriptorAlias of Object.keys( resultDataDescriptorMappings.referenceInsertableCheck ?? {} ) ) {
                                                      if (
                                                          resultDataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ].referenceSetId === referenceInsertableCheck.referenceSetId
                                                          && resultDataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ].srcItemId === referenceInsertableCheck.srcItemId
                                                          && resultDataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ].destItemId === referenceInsertableCheck.destItemId
                                                      ) {
                                                          resultDataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ] = {
                                                              ...resultDataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ],
                                                              isReferenceInsertable,
                                                              errorMessage     : reason?.name,
                                                              errorPlaceholders: reason?.placeholders
                                                          };
                                                      }
                                                  }

                                                  // set errors
                                                  if ( setValueErrors ) {
                                                      resultLayoutElementMappings.setValueErrors[ realLayoutElementId ] = setValueErrors;
                                                  }
                                                  // no errors -> delete set errors and edit value
                                                  else {
                                                      delete resultLayoutElementMappings.setValueErrors[ realLayoutElementId ];
                                                      delete resultDataDescriptorMappings.editValue[ realLayoutElementDataDescriptorAlias ];

                                                      await this.onTriggerAction( realLayoutElementId, originalEvent, this.props );
                                                  }

                                                  this.deferRender();

                                                  this.props.setElusiveData( uid, {
                                                      dataDescriptorMappings: resultDataDescriptorMappings,
                                                      layoutElementMappings : resultLayoutElementMappings
                                                  } );
                                              }

                                              // force render if deferred
                                              if ( !isDeferred && this.deferredRender ) {
                                                  setTimeout( () => {
                                                      this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                                                  }, 5 );
                                              }
                                          };

                                    let referenceIsInsertable : boolean = true,
                                        referenceNotInsertableReason : {
                                            name : string;
                                            placeholders? : Placeholders;
                                        } | undefined;

                                    // abort running check
                                    xhr.abort( componentUid, requestUid );

                                    for ( const checkDataContext of checkDataContexts ) {
                                        const srcItem  = global.data[ dataContext ].items[ newReferenceInsertableCheck.srcItemId ],
                                              destItem = global.data[ dataContext ].items[ newReferenceInsertableCheck.destItemId ],
                                              check    = checkCardinalityFeOnly( checkDataContext, schema, newReferenceInsertableCheck.referenceSetId, srcItem, destItem );

                                        if ( !check.cardinalityCheck ) {
                                            referenceIsInsertable = false;
                                            referenceNotInsertableReason = {
                                                name: check.cardinalityError
                                            };
                                            break;
                                        }
                                    }

                                    // check key uniqueness against frontend data
                                    if ( !referenceIsInsertable ) {
                                        const delayFnc = async () => {
                                            await Promise.resolve();
                                            await endUpCheckFnc( false, referenceNotInsertableReason );
                                        };

                                        void delayFnc();
                                    }
                                    else {
                                        const doLoad = async () => {
                                            const params : CheckReferenceInsertableParameters = {
                                                      ..._.omit( newReferenceInsertableCheck, 'isReferenceInsertable' )
                                                  },
                                                  response                                    = await xhr.post(
                                                      `${BASE_URL}/api/data/checkReferenceInsertable`,
                                                      params,
                                                      undefined,
                                                      componentUid,
                                                      requestUid
                                                  );

                                            if ( response?.data ) {
                                                const responseData = response.data as CheckReferenceInsertableResult;

                                                if ( responseData.success ) {
                                                    referenceIsInsertable = responseData.data.insertable;
                                                    referenceNotInsertableReason = responseData.data.reason;

                                                    // check reference cardinality within dataContext
                                                    if ( !referenceIsInsertable && responseData.data.referenceId ) {
                                                        referenceIsInsertable = true;
                                                        referenceNotInsertableReason = undefined;

                                                        for ( const checkDataContext of checkDataContexts ) {
                                                            const reference = global.data[ checkDataContext ]?.references[ responseData.data.referenceId ];

                                                            if ( !reference || reference.status.name !== 'deleted' ) {
                                                                referenceIsInsertable = false;
                                                                referenceNotInsertableReason = responseData.data.reason;
                                                                break;
                                                            }
                                                        }
                                                    }

                                                    await endUpCheckFnc( referenceIsInsertable, referenceNotInsertableReason );
                                                }

                                            }
                                        };

                                        doLoad().catch( ( error ) => {
                                            console.log( error );
                                        } );

                                        // do not set to elusiveData, just modify given dataDescriptorMappings!
                                        dataDescriptorMappings.referenceInsertableCheck = {
                                            ...dataDescriptorMappings.referenceInsertableCheck
                                        };
                                        for ( const referenceInsertableCheckDataDescriptorAlias of Object.keys( dataDescriptorMappings.referenceInsertableCheck ?? {} ) ) {
                                            if (
                                                dataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ].referenceSetId === referenceInsertableCheck.referenceSetId
                                                && dataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ].srcItemId === referenceInsertableCheck.srcItemId
                                                && dataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ].destItemId === referenceInsertableCheck.destItemId
                                            ) {
                                                dataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ] = {
                                                    ...dataDescriptorMappings.referenceInsertableCheck[ referenceInsertableCheckDataDescriptorAlias ],
                                                    ...newReferenceInsertableCheck,
                                                    isReferenceInsertable: undefined
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // variable field validation against field type
            else if ( value !== '' && value !== undefined && value !== null && layoutElement.type === 'VARIABLE' ) {
                const type = ( layoutElement.data as VariableFieldProperties ).variableType;

                if ( isPropertyValueType( type ) ) {
                    try {
                        validateValueForType( value, type, true );
                    }
                    catch ( e ) {
                        let errorString = String( e );

                        if ( e instanceof LocalizableError ) {
                            errorString = e.toString( false );
                        }

                        if ( this.debug ) {
                            console.log( '            -> error', errorString );
                        }

                        retVal.push( errorString );
                    }
                }
            }

            // execute validator actions
            if ( !retVal.length && rootItemId && layoutElementData.validators?.length ) {
                const validatorsStartTime                               = this.debug ? performance.now() : 0,
                      validatorDataContext                              = `validators`,
                      clonedObjectCache : ClonedObjectCache             = { items: {}, references: {}, originalStates: { items: {}, references: {} } },
                      clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} },
                      treeVariables                                     = { ...this.props.treeVariables };

                let validatorExecuted : boolean = false,
                    aborted : boolean           = false;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${`%cexecute validators%c`.asLogLine( 97 )}`, `font-weight: normal;`, `font-weight: bold;`, 'font-weight: normal;' );
                }

                if ( !isDeferred ) {
                    this.deferRender();
                }

                delete this.dynamicFunctionGlobalParameters;

                for ( const validator of layoutElementData.validators ) {
                    if ( validator.function ) {
                        const validatorStartTime        = this.debug ? performance.now() : 0,
                              dataObjects : DataObjects = {
                                  ...validator.dataObjects
                              };

                        if ( this.debug ) {
                            console.groupCollapsed( `%c${`execute validator %c${validator.key}%c`.asLogLine( 95 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                        }

                        let resolvedDataObjects : ResolvedDataObjects = {};

                        // resolve fieldObject by default
                        if ( dataDescriptor && dataDescriptor.type !== 'expression' ) {
                            dataObjects.fieldObject = {
                                ...dataDescriptor.dataObject,
                                dataSelectionMode: 'single'
                            };
                        }

                        try {
                            resolvedDataObjects = this.getResolvedDataObjects( dataObjects, validatorDataContext, clonedObjectCache, clonedObjectsIdMappings );

                            if ( !( resolvedDataObjects.rootObject as ResolvedDataObject )?.item ) {
                                console.log( '%cexecution aborted due to missing rootObject', `color: #${debugColorScheme.error}; font-weight: bold;`, [ validator, rootItemId, rootItemId ? global.data[ dataContext ].items[ rootItemId ]?.toJSON() : undefined, Object.keys( resolvedDataObjects ) ] );
                                break;
                            }
                            if ( layoutElementData.type !== 'VARIABLE' && !( resolvedDataObjects.fieldObject as ResolvedDataObject )?.item ) {
                                console.log( '%cexecution aborted due to missing fieldObject', `color: #${debugColorScheme.error}; font-weight: bold;`, [ validator, rootItemId, rootItemId ? global.data[ dataContext ].items[ rootItemId ]?.toJSON() : undefined, Object.keys( resolvedDataObjects ) ] );
                                break;
                            }

                            const isValid = !!await this.buildDynamicFunction(
                                {
                                    ...this.getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects ),
                                    field: {
                                        id  : layoutElementData.id,
                                        key : layoutElementData.key,
                                        name: layoutElementData.name,
                                        type: layoutElementData.type,
                                        value
                                    },
                                    treeVariables
                                },
                                validator.function,
                                resolvedDataObjects
                            )();

                            validatorExecuted = true;

                            if ( !isValid ) {
                                if ( validator.message ) {
                                    retVal.push( messageUtils.resolveMessageForDataStructure( validator.message, dataStructure, dataContext, global.data[ dataContext ].items[ rootItemId ], undefined, ', ', { variables: this.variables } ) );
                                }
                                else {
                                    retVal.push( __( 'YN.base.form.executeValidator.error.default' ) );
                                }
                            }
                        }
                        catch ( e ) {
                            console.error( e, {
                                function           : validator.function,
                                resolvedDataObjects: Object.keys( resolvedDataObjects )
                            } );

                            const errorTitle  = `<span class="yn-error yn-confirmpopup-title">${__(
                                      'YN.base.form.executeValidator.error.title',
                                      {
                                          id  : String( validator.id ) ?? '',
                                          key : validator.key ?? `order: ${validator.order}`,
                                          name: validator.name ?? ''
                                      }
                                  )}</span>`,
                                  errorDetail = this.getDynamicFunctionErrorDetail( e, validator.function );

                            // wait on error
                            await new Promise( ( resolve ) => {
                                confirmPopup( {
                                    target         : originalEvent.currentTarget,
                                    content        : '',
                                    message        : parse( `${errorTitle}<br /><br />${errorDetail}` ),
                                    icon           : 'pi pi-exclamation-triangle yn-error',
                                    acceptLabel    : __( 'YN.base.common.ok' ),
                                    acceptClassName: 'p-button p-button-outlined',
                                    rejectClassName: 'hidden',
                                    accept         : () => {
                                        resolve( true );
                                    }
                                } );
                            } );

                            aborted = true;

                            if ( this.debug ) {
                                console.log( '%cexecution aborted due to error', `color: #${debugColorScheme.error}; font-weight: bold;` );
                            }

                            break;
                        }

                        if ( this.debug ) {
                            console.log( `%c${Math.round( 1000 * ( performance.now() - validatorStartTime ) ) / 1000} ms%c`.asLogLine( 94 ), `font-weight: bold;`, 'font-weight: unset;' );
                            console.groupEnd();
                        }

                        if ( retVal.length ) {
                            break;
                        }
                    }
                }

                // apply modifications
                if ( !aborted && validatorExecuted ) {
                    try {
                        if ( treeVariables ) {
                            this.props.treeSetVariables?.( treeVariables );
                        }
                        this.applyDynamicFunctionVariablesChanges( this.props );
                    }
                    catch ( e ) {
                        console.error( e );
                    }

                    // force render
                    if ( !isDeferred && this.deferredRender ) {
                        setTimeout( () => {
                            this.setState( { deferredRenderEndTrigger: this.state.deferredRenderEndTrigger + 1 } );
                        }, 5 );
                    }
                }

                // cleanup
                delete global.data[ validatorDataContext ];

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - validatorsStartTime ) ) / 1000} ms%c`.asLogLine( 96 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            }
        }

        if ( !retVal.length ) {
            retVal = undefined;
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 98 ), `color: #${debugColorScheme.validator}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }

        return retVal;
    }

    /**
     * Retrieves the corresponding objects based on the provided data and meta-model mapping configuration.
     *
     * @param {DataAndMetaModelMapping} dataAndMetaModel - The input mapping configuration that includes object types and their associations.
     * @return {Item | Item[] | Property | Property[] | EnumProperty | EnumProperty[] | Reference | Reference[] | ReferenceProperty | ReferenceProperty[] | EnumReferenceProperty | EnumReferenceProperty[] | undefined}
     * - Returns the corresponding objects based on the type defined in `dataAndMetaModel`.
     * - If no matching objects are found, it returns `undefined`.
     */
    getObjectFromDataAndMetaModelMapping( dataAndMetaModel : DataAndMetaModelMapping ) : Item | Item[] | Property | Property[] | EnumProperty | EnumProperty[] | Reference | Reference[] | ReferenceProperty | ReferenceProperty[] | EnumReferenceProperty | EnumReferenceProperty[] | undefined {
        const { dataContext } = this.props;

        let retVal : Item | Item[] | Property | Property[] | EnumProperty | EnumProperty[] | Reference | Reference[] | ReferenceProperty | ReferenceProperty[] | EnumReferenceProperty | EnumReferenceProperty[] | undefined = [],
            parentObjectIds : number[],
            propertyTypeIds : number[],
            parentObject : Item | Reference;

        if ( dataAndMetaModel.objects ) {
            switch ( dataAndMetaModel.type ) {
                case 'item':
                    retVal = [] as Item[];

                    for ( const itemId of dataAndMetaModel.objects ) {
                        retVal.push( global.data[ dataContext ].items[ itemId ] );
                    }

                    break;

                case 'property':
                    retVal = [] as Property[];
                    parentObjectIds = Object.keys( dataAndMetaModel.objects );

                    for ( const itemId of parentObjectIds ) {
                        parentObject = global.data[ dataContext ].items[ itemId ];

                        for ( const propertyTypeId of dataAndMetaModel.objects[ itemId ] ) {
                            retVal.push( parentObject.properties.get( propertyTypeId )! );
                        }
                    }

                    break;

                case 'enumProperty':
                    retVal = [] as EnumProperty[];
                    parentObjectIds = Object.keys( dataAndMetaModel.objects );

                    for ( const itemId of parentObjectIds ) {
                        parentObject = global.data[ dataContext ].items[ itemId ];
                        propertyTypeIds = Object.keys( dataAndMetaModel.objects[ itemId ] );

                        for ( const propertyTypeId of propertyTypeIds ) {
                            retVal.push( parentObject.enumProperties.get( propertyTypeId )! );
                        }
                    }

                    break;

                case 'reference':
                    retVal = [] as Reference[];

                    for ( const referenceId of dataAndMetaModel.objects ) {
                        retVal.push( global.data[ dataContext ].references[ referenceId ] );
                    }

                    break;

                case 'referenceProperty':
                    retVal = [] as ReferenceProperty[];
                    parentObjectIds = Object.keys( dataAndMetaModel.objects );

                    for ( const referenceId of parentObjectIds ) {
                        parentObject = global.data[ dataContext ].references[ referenceId ];

                        for ( const propertyTypeId of dataAndMetaModel.objects[ referenceId ] ) {
                            retVal.push( parentObject.properties.get( propertyTypeId )! );
                        }
                    }

                    break;

                case 'enumReferenceProperty':
                    retVal = [] as EnumReferenceProperty[];
                    parentObjectIds = Object.keys( dataAndMetaModel.objects );

                    for ( const referenceId of parentObjectIds ) {
                        parentObject = global.data[ dataContext ].references[ referenceId ];
                        propertyTypeIds = Object.keys( dataAndMetaModel.objects[ referenceId ] );

                        for ( const propertyTypeId of propertyTypeIds ) {
                            retVal.push( parentObject.enumProperties.get( propertyTypeId )! );
                        }
                    }

                    break;
            }
        }

        if ( !retVal.length ) {
            retVal = undefined;
        }
        else if ( retVal.length === 1 && dataAndMetaModel.type !== 'enumProperty' && dataAndMetaModel.type !== 'enumReferenceProperty' ) {
            retVal = retVal[ 0 ];
        }

        return retVal;
    }

    /**
     * Retrieves the value from the provided data and meta-model mapping based on its type and values.
     *
     * @param {DataAndMetaModelMapping} dataAndMetaModel - The mapping object that contains data, meta-model type, and associated values.
     * @return {AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined} - The extracted value from the data and meta-model mapping. May return undefined if no values are available.
     */
    getValueFromDataAndMetaModelMapping( dataAndMetaModel : DataAndMetaModelMapping ) : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined {
        let retVal : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined,
            values : ( AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined )[] = [];

        if (
            dataAndMetaModel.type === 'property'
            || dataAndMetaModel.type === 'enumProperty'
            || dataAndMetaModel.type === 'referenceProperty'
            || dataAndMetaModel.type === 'enumReferenceProperty'
        ) {
            for ( const objectValues of ( Object.values( dataAndMetaModel.values ?? {} ) as PropertyValue[] ) ) {
                for ( const propertyValue of Object.values( objectValues ) ) {
                    values.push( propertyValue );
                }
            }
        }
        else {
            values = Object.values( dataAndMetaModel.values! );
        }

        if ( !values.length ) {
            retVal = undefined;
        }
        else {
            if (
                dataAndMetaModel.type === 'referenceProperty'
                || dataAndMetaModel.type === 'enumReferenceProperty'
            ) {
                retVal = values[ 0 ] as EnumValue;
            }
            else if ( dataAndMetaModel.type === 'reference' ) {
                retVal = values as ReferenceValue[];
            }
            else {
                retVal = values[ 0 ] as AttributeValue;
            }
        }

        return retVal;
    }

    /**
     * Retrieves the original value from the provided DataAndMetaModelMapping instance.
     *
     * @param {DataAndMetaModelMapping} dataAndMetaModel The data and meta-model mapping object that may contain original values based on its type.
     * @return {AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined} The extracted original value(s).
     * Returns an AttributeValue, EnumValue, ReferenceValue, an array of ReferenceValue, or undefined if no values are found.
     */
    getOriginalValueFromDataAndMetaModelMapping( dataAndMetaModel : DataAndMetaModelMapping ) : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined {
        let retVal : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined,
            values : ( AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined )[] = [];

        if (
            dataAndMetaModel.type === 'property'
            || dataAndMetaModel.type === 'enumProperty'
            || dataAndMetaModel.type === 'referenceProperty'
            || dataAndMetaModel.type === 'enumReferenceProperty'
        ) {
            for ( const objectValues of ( Object.values( dataAndMetaModel.originalValues ?? {} ) as PropertyValue[] ) ) {
                for ( const propertyValue of Object.values( objectValues ) ) {
                    values.push( propertyValue );
                }
            }
        }
        else {
            values = Object.values( dataAndMetaModel.originalValues ?? {} );
        }

        if ( !values.length ) {
            retVal = undefined;
        }
        else {
            if (
                dataAndMetaModel.type === 'referenceProperty'
                || dataAndMetaModel.type === 'enumReferenceProperty'
            ) {
                retVal = values[ 0 ] as EnumValue;
            }
            else if ( dataAndMetaModel.type === 'reference' ) {
                retVal = values as ReferenceValue[];
            }
            else {
                retVal = values[ 0 ] as AttributeValue;
            }
        }

        return retVal;
    }

    getItemKeyCheckComponentId() {
        return `${this.props.uid}_checkIKU`;
    }

    getItemKeyCheckRequestId( layoutElementDataDescriptorAlias : string, parentId? : number ) {
        return `checkKeyUniqueness_${layoutElementDataDescriptorAlias}${parentId ? `_${parentId}` : ''}`;
    }

    getResolvedDataObjects( dataObjects : DataObjects, resolvedObjectsDataContext : string = 'resolvedObjectsDataContext', clonedObjectCache : ClonedObjectCache = { items: {}, references: {}, originalStates: { items: {}, references: {} } }, clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} }, props : AllProps = this.props ) : ResolvedDataObjects {
        const retVal : ResolvedDataObjects = {},
              {
                  schema,
                  dataContext,
                  dataStructure,
                  rootItemId,
                  formParentItemId,
                  treeParentItemId,
                  treeRootItemId
              }                            = props;

        if ( rootItemId ) {
            const dataObjectsWithDefaultObjects : DataObjects = {
                ...dataObjects,
                // rootObject (as fake DataObjects value)
                rootObject: {
                    id               : String( rootItemId ),
                    origId           : rootItemId,
                    item             : {
                        id  : rootItemId,
                        key : 'rootObject_key',
                        name: 'rootObject_name'
                    },
                    dataSelectionMode: 'single'
                },
                // formParentObject (as fake DataObjects value)
                formParentObject: {
                    id               : String( formParentItemId ),
                    origId           : formParentItemId ?? 0,
                    item             : {
                        id  : formParentItemId ?? 0,
                        key : 'formParentObject_key',
                        name: 'formParentObject_name'
                    },
                    dataSelectionMode: 'single'
                },
                // treeParentObject (as fake DataObjects value)
                treeParentObject: {
                    id               : String( treeParentItemId ),
                    origId           : treeParentItemId ?? 0,
                    item             : {
                        id  : treeParentItemId ?? 0,
                        key : 'treeParentObject_key',
                        name: 'treeParentObject_name'
                    },
                    dataSelectionMode: 'single'
                },
                // treeRootObject (as fake DataObjects value)
                treeRootObject: {
                    id               : String( treeRootItemId ),
                    origId           : treeRootItemId ?? 0,
                    item             : {
                        id  : treeRootItemId ?? 0,
                        key : 'treeRootObject_key',
                        name: 'treeRootObject_name'
                    },
                    dataSelectionMode: 'single'
                }
            };

            for ( const dataObjectLabel of Object.keys( dataObjectsWithDefaultObjects ) ) {
                let items : Item[]           = [],
                    references : Reference[] = [];

                if ( dataObjectLabel === dataObjectsWithDefaultObjects[ dataObjectLabel ].item.key ) {
                    throw ( __( 'YN.base.form.error.noLabelForDataObject', { key: dataObjectsWithDefaultObjects[ dataObjectLabel ].item.key, id: String( dataObjectsWithDefaultObjects[ dataObjectLabel ].origId ) } ) );
                }

                // get special objects (fake DataObjects value)
                if ( dataObjectLabel === 'rootObject' || dataObjectLabel === 'formParentObject' || dataObjectLabel === 'treeParentObject' || dataObjectLabel === 'treeRootObject' ) {
                    const item = global.data[ dataContext ].items[ dataObjectsWithDefaultObjects[ dataObjectLabel ].origId ];

                    if ( item ) {
                        items.push( item );
                    }

                    retVal[ `${dataObjectLabel}Info` ] = {
                        dataContext       : resolvedObjectsDataContext,
                        itemType          : item?.itemType.name ?? '',
                        referenceSetId    : 0,
                        referenceDirection: 'SrcToDest',
                        otherItem         : undefined,
                        otherItemType     : '',
                        state             : {
                            itemId     : 0,
                            item       : '',
                            referenceId: 0,
                            reference  : ''
                        }
                    };
                }
                else {
                    const dataObject                                        = getDataObjectByOrigId( dataObjectsWithDefaultObjects[ dataObjectLabel ].origId, dataStructure ),
                          parentDataObject                                  = getParentDataObject( dataObject?.id ?? '', dataStructure ),
                          itemObjectData : ItemData[] | undefined           = dataObject
                              ? dataUtils.getData(
                                  buildDataDescriptor( { attributePath: 'item.id' }, dataObject ) as ObjectDataDescriptor,
                                  dataStructure,
                                  dataContext,
                                  rootItemId
                              ) as ItemData[] | undefined
                              : undefined,
                          referenceObjectData : ReferenceData[] | undefined = dataObject
                              ? dataUtils.getData(
                                  buildDataDescriptor( { attributePath: 'reference.id' }, dataObject ) as ObjectDataDescriptor,
                                  dataStructure,
                                  dataContext,
                                  rootItemId
                              ) as ReferenceData[] | undefined
                              : undefined,
                          referenceItemObjectData : ItemData[] | undefined  = parentDataObject
                              ? dataUtils.getData(
                                  buildDataDescriptor( { attributePath: 'item.id' }, parentDataObject ) as ObjectDataDescriptor,
                                  dataStructure,
                                  dataContext,
                                  rootItemId
                              ) as ItemData[] | undefined
                              : undefined;

                    items = ( itemObjectData?.map( iOD => iOD.object ) ?? [] );
                    references = ( referenceObjectData?.map( rOD => rOD.object ) ?? [] );

                    if ( dataObject?.item.objectType ) {
                        const metaModelData = dataUtils.getMetaModelData(
                                  buildDataDescriptor( { attributePath: 'reference.id' }, dataObject ) as ObjectDataDescriptor,
                                  dataStructure,
                                  schema,
                                  dataContext,
                                  rootItemId
                              ),
                              referenceSet  = metaModelData?.[ 0 ]?.type === 'referenceSet' ? metaModelData?.[ 0 ]?.object : undefined;

                        let referenceDirection = Object.values( dataObject.reference?.referenceConfiguration ?? {} )?.[ 0 ]?.referenceDirection,
                            otherItem : Item | undefined;

                        if ( referenceDirection ) {
                            // reverse the referenceDirection from dataObject since it is seen from the referenced items dataObject to parent items dataObject (e.g. rootItem)
                            if ( referenceDirection === 'SrcToDest' ) {
                                referenceDirection = 'DestToSrc';
                            }
                            else {
                                referenceDirection = 'SrcToDest';
                            }
                        }
                        else if ( referenceItemObjectData?.[ 0 ]?.object ) {
                            if ( referenceSet?.srcItemTypeId === ( referenceItemObjectData[ 0 ].object ).itemTypeId ) {
                                referenceDirection = 'DestToSrc';
                            }
                            else {
                                referenceDirection = 'SrcToDest';
                            }
                        }
                        else {
                            referenceDirection = 'SrcToDest';
                        }

                        if (
                            (
                                referenceDirection === 'SrcToDest'
                                && referenceSet?.cardinality !== 'n:1'
                            )
                            || (
                                referenceDirection === 'DestToSrc'
                                && referenceSet?.cardinality !== '1:n'
                            )
                            || (
                                referenceItemObjectData?.length === 1
                                && referenceItemObjectData?.[ 0 ]?.object.id === rootItemId
                            )
                        ) {
                            otherItem = referenceItemObjectData?.[ 0 ]?.object;
                        }

                        retVal[ `${dataObjectLabel}Info` ] = {
                            dataContext   : resolvedObjectsDataContext,
                            itemType      : dataObject.item.objectType,
                            referenceSetId: referenceSet?.id ?? 0,
                            referenceDirection,
                            otherItem,
                            otherItemType : metaModel[ schema ].getItemType( referenceSet?.[ referenceDirection === 'SrcToDest' ? 'srcItemTypeId' : 'destItemTypeId' ] ?? 0 )?.name ?? '',
                            state         : dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single'
                                ? {
                                    itemId     : 0,
                                    item       : '',
                                    referenceId: 0,
                                    reference  : ''
                                }
                                : [] as ResolvedDataObjectState[]
                        };
                    }
                }

                if ( items.length ) {
                    for ( const item of items ) {
                        const reference = references.find( ref => item.references.includes( ref.id ) );

                        if (
                            !dataObjectsWithDefaultObjects[ dataObjectLabel ].parentId
                            || dataObjectsWithDefaultObjects[ dataObjectLabel ].parentId === item.id
                            || dataObjectsWithDefaultObjects[ dataObjectLabel ].parentId === reference?.id
                        ) {
                            // create item copy in action dataContext
                            if ( !clonedObjectCache?.items[ item.id ] ) {
                                if ( global.data[ resolvedObjectsDataContext ]?.items[ item.id ] ) {
                                    clonedObjectCache.items[ item.id ] = global.data[ resolvedObjectsDataContext ].items[ item.id ];
                                }
                                else {
                                    clonedObjectCache.items[ item.id ] = item.clone( undefined, undefined, resolvedObjectsDataContext );
                                }

                                if ( clonedObjectsIdMappings && item.id < 1 ) {
                                    clonedObjectsIdMappings.items[ clonedObjectCache.items[ item.id ].id ] = item.id;
                                }
                            }

                            // create reference copy in action dataContext
                            if ( reference && !clonedObjectCache.references[ reference.id ] ) {

                                if ( global.data[ resolvedObjectsDataContext ]?.references[ reference.id ] ) {
                                    clonedObjectCache.references[ reference.id ] = global.data[ resolvedObjectsDataContext ].references[ reference.id ];
                                }
                                else {
                                    clonedObjectCache.references[ reference.id ] = reference.clone( undefined, undefined, resolvedObjectsDataContext );
                                }

                                if ( reference.id < 1 ) {
                                    clonedObjectsIdMappings.references[ clonedObjectCache.references[ reference.id ].id ] = reference.id;
                                }
                            }

                            const resolvedDataObject : ResolvedDataObject = {
                                item    : clonedObjectCache.items[ item.id ],
                                property: new PropertyResolver( clonedObjectCache.items[ item.id ] )
                            };

                            if ( !clonedObjectCache.originalStates.items[ item.id ] ) {
                                clonedObjectCache.originalStates.items[ item.id ] = JSON.stringify( clonedObjectCache.items[ item.id ].toUpdateJSON( 'modified' ) );
                            }

                            if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                                ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).itemId = item.id;
                                ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).item = clonedObjectCache.originalStates.items[ item.id ];
                            }
                            else {
                                ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] ).push( {
                                    itemId     : item.id,
                                    item       : clonedObjectCache.originalStates.items[ item.id ],
                                    referenceId: 0,
                                    reference  : ''
                                } )
                            }

                            if ( reference ) {
                                resolvedDataObject.reference = clonedObjectCache.references[ reference.id ];
                                resolvedDataObject.referenceProperty = new ReferencePropertyResolver( clonedObjectCache.references[ reference.id ] );

                                if ( !clonedObjectCache.originalStates.references[ reference.id ] ) {
                                    clonedObjectCache.originalStates.references[ reference.id ] = JSON.stringify( clonedObjectCache.references[ reference.id ].toUpdateJSON( 'modified' ) );
                                }

                                if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                                    ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).referenceId = reference.id;
                                    ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).reference = clonedObjectCache.originalStates.references[ reference.id ];
                                }
                                else {
                                    ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] )[ ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] ).length - 1 ].referenceId = reference.id;
                                    ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] )[ ( retVal[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] ).length - 1 ].reference = clonedObjectCache.originalStates.references[ reference.id ];
                                }
                            }

                            if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                                retVal[ dataObjectLabel ] = resolvedDataObject;

                                break;
                            }
                            else {
                                if ( !retVal[ dataObjectLabel ] ) {
                                    retVal[ dataObjectLabel ] = [];
                                }

                                ( retVal[ dataObjectLabel ] as ResolvedDataObject[] ).push( resolvedDataObject );
                            }
                        }
                    }
                }
                else if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'single' ) {
                    retVal[ dataObjectLabel ] = {} as ResolvedDataObject;
                }
                else if ( dataObjectsWithDefaultObjects[ dataObjectLabel ].dataSelectionMode === 'all' ) {
                    retVal[ dataObjectLabel ] = [];
                }
            }
        }

        return retVal;
    }

    getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects : ResolvedDataObjects ) : MappedResolvedDataObjects {
        return ( Object.keys( resolvedDataObjects ) as string[] ).reduce(
            ( innerRetVal, dataObjectLabel ) => {
                if ( !dataObjectLabel.match( /Info$/gm ) ) {
                    innerRetVal[ dataObjectLabel ] = resolvedDataObjects[ dataObjectLabel ];
                }
                return innerRetVal;
            },
            {} as MappedResolvedDataObjects
        );
    }

    getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId : number, parentId? : number ) {
        let retVal : number | string = layoutElementId;

        if ( parentId !== undefined ) {
            retVal = `${layoutElementId}_${parentId}`;
        }

        return retVal;
    }

    getLayoutElementIdAndParentIdFromRealLayoutElementId( realLayoutElementId : number | string ) {
        const retVal : {
                  layoutElementId : number;
                  parentId : number | undefined;
              }              = {
                  layoutElementId: 0,
                  parentId       : undefined
              },
              delimiterIndex = String( realLayoutElementId ).indexOf( '_' );

        if ( typeof realLayoutElementId === 'number' ) {
            retVal.layoutElementId = realLayoutElementId;
        }
        else if ( delimiterIndex === -1 ) {
            retVal.layoutElementId = Number( realLayoutElementId );
        }
        else {
            retVal.layoutElementId = Number( realLayoutElementId.substring( 0, delimiterIndex ) );
            retVal.parentId = Number( realLayoutElementId.substring( delimiterIndex + 1 ) );
        }

        return retVal;
    }

    getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( layoutElementDataDescriptorAlias : string, parentId? : number ) {
        let retVal : string = layoutElementDataDescriptorAlias;

        if ( parentId ) {
            retVal = `${layoutElementDataDescriptorAlias}_${parentId}`;
        }

        return retVal;
    }

    getLayoutElementDataDescriptorAliasAndParentIdFromRealLayoutElementDataDescriptorAlias( realLayoutElementDataDescriptorAlias : string ) {
        const retVal : {
                  layoutElementDataDescriptorAlias : string;
                  parentId : number | undefined;
              }              = {
                  layoutElementDataDescriptorAlias: realLayoutElementDataDescriptorAlias,
                  parentId                        : undefined
              },
              delimiterIndex = String( realLayoutElementDataDescriptorAlias ).indexOf( '_' );

        if ( delimiterIndex !== -1 ) {
            retVal.layoutElementDataDescriptorAlias = realLayoutElementDataDescriptorAlias.substring( 0, delimiterIndex );
            retVal.parentId = Number( realLayoutElementDataDescriptorAlias.substring( delimiterIndex + 1 ) );
        }

        return retVal;
    }

    /**
     * Generates and returns the form element based on the layout structure and associated data.
     * Dynamically handles errors, styling, and content rendering for form construction.
     *
     * @return {React.ReactNode} A React element representing the constructed form. Returns an empty React fragment if layoutElementData is unavailable.
     */
    getForm() {
        const startTime                 = this.debug ? performance.now() : 0,
              {
                  uid,
                  layoutStructure,
                  elusiveData
              }                         = this.props,
              { dynamicFunctionErrors } = this.state,
              layoutElementData         = elusiveData?.layoutElementMappings?.cache?.[ layoutStructure.layoutElementId ];

        let retVal : React.ReactNode = <></>;

        this.tabIndex = 1;
        this.autoFocusField = undefined;

        if ( layoutElementData ) {
            retVal = <>
                {dynamicFunctionErrors &&
                    <ConfirmDialog
                        header={<div className="yn-error"><span
                            className="pi pi-exclamation-triangle p-confirm-dialog-icon"/>{__( 'YN.base.form.executeFunction.error.title' )}
                        </div>}
                        content=""
                        message={parse( dynamicFunctionErrors.join( '<br /><br /><br />' ) )}
                        visible={true}
                        acceptLabel={__( 'YN.base.common.ok' )}
                        acceptClassName="p-button p-button-outlined"
                        rejectClassName="hidden"
                        accept={() => {
                            this.setState( { dynamicFunctionErrors: undefined } );
                        }}
                    />
                }
                <ConfirmPopup content=""/>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div id={`${uid}_form`} className="yn-form" style={layoutElementData.elementStyle} onClick={this.onFormClick}
                     onDragEnd={this.onFormClick} onKeyUp={this.onFormKeyUp}>
                    {this.getContainerContent( { type: 'form', id: layoutElementData.id, gridSize: layoutElementData.gridSize }, layoutStructure.children ).content}
                </div>
            </>;

            if ( this.debug ) {
                console.log( `%cForm - getForm - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 104 ), `font-weight: bold;`, 'font-weight: unset;' );
            }
        }

        return retVal;
    }

    /**
     * Retrieves the content of a container layout, including its structure, properties, and other metadata.
     * Processes layout elements based on their visibility, width, and type, and computes the resulting layout details.
     *
     * @param {Parent} parent - The parent item that contains metadata about the container such as type and grid size.
     * @param {(FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure)[]} containerChildren
     *        - An array of child elements within the container, including form fields, sub-containers, and tab containers.
     * @return {GetContainerContent} The processed container content, encapsulating information about its layout and interaction states.
     */
    getContainerContent( parent : Parent, containerChildren : ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[] ) : GetContainerContent {
        const retVal : GetContainerContent                                  = {
                  isDirty  : false,
                  isInvalid: false,
                  content  : []
              },
              {
                  uid,
                  schema,
                  dataContext,
                  rootItemId,
                  dataStructure,
                  dataDescriptors,
                  layoutElements,
                  elusiveData,
                  mode,
                  user,
                  formParentItemId,
                  treeRootItemId,
                  treeParentItemId,
                  treeVariables,
                  tabIndexOffset,
                  activeTabContainerIndexes,
                  rootItemType,
                  rootItemKey,
                  viewKey,
                  configHash
              }                                                             = this.props,
              additionalElusiveDataContexts                                 = this.props.additionalElusiveDataContexts.concat( [ uid ] ),
              {
                  formGridUnitWidth,
                  remWidth
              }                                                             = this.state,
              rootItem                                                      = global.data[ dataContext ].items[ rootItemId ?? 0 ],
              variables                                                     = elusiveData?.variables ?? {},
              variablesEditValues                                           = elusiveData?.variablesEditValues ?? {},
              commonResolvedDataObjectsForMessage : ResolvedDataObjectChain = {
                  rootObject: {
                      item: rootItem
                  }
              },
              commonAdditionalResolverParametersForMessage                  = {
                  variables    : {
                      ...variables,
                      [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                          // toString
                          if ( hint === 'string' ) {
                              return nl2br( JSON.stringify( variables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                          }
                          // all other
                          else {
                              return null;
                          }
                      }
                  } as Variables,
                  treeVariables: {
                      ...treeVariables,
                      [ Symbol.toPrimitive ]: ( hint : 'default' | 'number' | 'string' ) => {
                          // toString
                          if ( hint === 'string' ) {
                              return nl2br( JSON.stringify( treeVariables, null, '\u00a0\u00a0\u00a0\u00a0' ) );
                          }
                          // all other
                          else {
                              return null;
                          }
                      }
                  } as Variables,
                  viewKey,
                  rootItemId,
                  rootItemKey,
                  rootItemType,
                  configHash
              },
              layoutElementsData                                            = elusiveData?.layoutElementMappings?.cache ?? {};

        let repeatingContainer : boolean = false;

        if ( parent.type === 'form' ) {
            let visibleFieldCount = 0;
            this.maxTabIndex = 0;

            for ( const layoutElementId of Object.keys( layoutElements ) ) {
                const data = layoutElementsData[ layoutElementId ];
                if ( data.visibility !== 'hidden' && layoutElements[ layoutElementId ].type !== 'CONTAINER' ) {
                    visibleFieldCount++;
                    this.maxTabIndex = Math.max( visibleFieldCount, data.tabOrder ?? 0, this.maxTabIndex );
                }
            }
        }

        if ( containerChildren ) {
            containerChildren = ( [] as ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[] ).concat( containerChildren );
            containerChildren.sort( this.orderLayoutStructure );

            let containerElementsFlexWidthSum : number     = 0,
                containerElementsGridUnitWidthSum : number = 0,
                containerElementsPixelWidthSum : number    = 0,
                containerElementsRemWidthSum : number      = 0,
                containerElementsPercentWidthSum : number  = 0;

            for ( const elementStructure of containerChildren ) {
                const layoutElementProp = layoutElementsData?.[ elementStructure.layoutElementId ] as ContainerProperties | FormFieldProperties | undefined;

                if ( layoutElementProp?.width ) {
                    if ( layoutElementProp.width < 0 ) {
                        containerElementsFlexWidthSum += -1 * layoutElementProp.width;
                    }
                    else {
                        switch ( layoutElementProp.widthUnit ) {
                            case 'gridunit':
                                containerElementsGridUnitWidthSum += layoutElementProp.width;
                                break;
                            case 'pixel':
                                containerElementsPixelWidthSum += layoutElementProp.width;
                                break;
                            case 'rem':
                                containerElementsRemWidthSum += layoutElementProp.width;
                                break;
                            case 'percent':
                                containerElementsPercentWidthSum += layoutElementProp.width;
                                break;
                        }
                    }
                }
            }

            const dataDescriptorToValueMapping     = elusiveData?.dataDescriptorMappings?.value ?? {},
                  containerElementsFlexWidthFactor = (
                          ( formGridUnitWidth * parent.gridSize
                              - formGridUnitWidth * .01 * parent.gridSize * containerElementsPercentWidthSum
                              - formGridUnitWidth * containerElementsGridUnitWidthSum
                              - remWidth * containerElementsRemWidthSum
                              - containerElementsPixelWidthSum )
                          / formGridUnitWidth )
                      / containerElementsFlexWidthSum;

            for ( const layoutElementStructure of containerChildren ) {
                const layoutElementId = layoutElementStructure.layoutElementId,
                      layoutElement   = layoutElements[ layoutElementId ];

                let layoutElementData = layoutElementsData[ layoutElementId ];

                if ( variables.repeatedElements?.[ layoutElement.data.key ]?.[ parent.itemId ?? 0 ] ) {
                    layoutElementData = {
                        ...layoutElementData,
                        ...variables.repeatedElements[ layoutElement.data.key ][ parent.itemId ?? 0 ]
                    };
                }

                const {
                          visibility
                      } = layoutElementData;

                // avoid rendering of hidden components
                if ( visibility !== 'hidden' ) {
                    let childrenGridSize : number          = parent.gridSize,
                        elementStyle : React.CSSProperties = {};

                    if ( layoutElementData.width ) {
                        if ( layoutElementData.width < 0 ) {
                            childrenGridSize = Math.min( parent.gridSize, Math.floor( -1 * layoutElementData.width * containerElementsFlexWidthFactor ) );
                        }
                        else {
                            switch ( layoutElementData.widthUnit ) {
                                case 'gridunit':
                                    childrenGridSize = Math.min( parent.gridSize, layoutElementData.width );
                                    break;
                                case'pixel':
                                    childrenGridSize = Math.min( parent.gridSize, layoutElementData.width / formGridUnitWidth );
                                    break;
                                case'rem':
                                    childrenGridSize = Math.min( parent.gridSize, remWidth * layoutElementData.width / formGridUnitWidth );
                                    break;
                                case 'percent':
                                    childrenGridSize = Math.min( parent.gridSize, parent.gridSize / 100 * layoutElementData.width );
                            }
                        }
                    }
                    elementStyle = this.getFormLayoutStyle( layoutElementData, parent.gridSize );

                    // containers
                    if ( layoutElementStructure.type === 'CONTAINER' ) {
                        const containerLayoutElementData = layoutElementData as ContainerProperties,
                              {
                                  repeatConfig,
                                  messages,
                                  isLayoutWrapper,
                                  titleAlignment
                              }                          = containerLayoutElementData,
                              titleVisibility            = messages?.title ? containerLayoutElementData.titleVisibility : 'hidden';

                        if ( !parent.isRepeating && repeatConfig ) {
                            const referenceSet       = metaModel[ schema ].getReferenceSet( repeatConfig.referenceSetId ),
                                  referenceField     = ( () : FormFieldData | undefined => {
                                      const recursiveFindFirstReferenceField = ( children : ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[] ) : FormFieldData | undefined => {
                                          let innerRetVal : FormFieldData | undefined = undefined;

                                          for ( const child of children ) {
                                              if ( child.type === 'CONTAINER' || child.type === 'TABCONTAINER' ) {
                                                  innerRetVal = recursiveFindFirstReferenceField( child.children );
                                                  if ( innerRetVal ) {
                                                      break;
                                                  }
                                              }
                                              else {
                                                  if ( ( layoutElements[ child.layoutElementId ] as FormFieldData ).dataDescriptorAlias === repeatConfig.dataDescriptorAlias ) {
                                                      innerRetVal = layoutElements[ child.layoutElementId ] as FormFieldData;
                                                      break;
                                                  }
                                              }
                                          }

                                          return innerRetVal;
                                      };

                                      return recursiveFindFirstReferenceField( layoutElementStructure.children );
                                  } )(),
                                  referenceFieldData = referenceField ? layoutElementsData[ referenceField.data.id ] : undefined,
                                  cardinality        = referenceSet?.cardinality.split( ':' ),
                                  deletedStatusIds   = metaModel[ schema ].getDeletedStatus().map( s => s.id );

                            let dataAndMetaModel = elusiveData?.dataDescriptorMappings?.dataAndMetaModel?.[ repeatConfig.dataDescriptorAlias ],
                                repeatIndex      = 0;

                            // build referenceId to tabIndex mapping
                            // (so visible tab stays on the reference even information changing the tab sort is changed)
                            retVal.tabIndexMapping = {};

                            // chained repeat
                            if ( parent.dataDescriptorAlias ) {
                                const dataDescriptor       = dataDescriptors[ repeatConfig.dataDescriptorAlias ],
                                      parentDataDescriptor = dataDescriptors[ parent.dataDescriptorAlias ];

                                if ( dataAndMetaModel && dataDescriptor && parentDataDescriptor ) {
                                    dataAndMetaModel = { ...dataAndMetaModel };

                                    // recompute dataAndModel.objects
                                    if ( parentDataDescriptor.dataObject ) {
                                        const subDataDescriptors = {
                                                  [ repeatConfig.dataDescriptorAlias ]: dataDescriptor
                                              } as DataDescriptors,
                                              subDataStructure   = getDataStructure( parentDataDescriptor.dataObject.id, dataStructure );

                                        let dataDescriptorMappings : DataDescriptorMappings | undefined;

                                        if ( subDataStructure ) {
                                            if ( parent.itemId ) {
                                                dataDescriptorMappings = subDataStructure
                                                    ? {
                                                        dataAndMetaModel: this.buildDataDescriptorToMetaModelMapping(
                                                            subDataDescriptors,
                                                            dataContext,
                                                            schema,
                                                            subDataStructure,
                                                            parent.itemId,
                                                            user
                                                        )
                                                    } as DataDescriptorMappings
                                                    : undefined;

                                                if ( dataDescriptorMappings ) {
                                                    dataDescriptorMappings.dataAndMetaModel = this.buildDataDescriptorToDataMapping(
                                                        subDataDescriptors,
                                                        dataDescriptorMappings,
                                                        dataContext,
                                                        schema,
                                                        subDataStructure,
                                                        parent.itemId
                                                    );

                                                    dataAndMetaModel.objects = dataDescriptorMappings.dataAndMetaModel[ repeatConfig.dataDescriptorAlias ].objects;
                                                }
                                            }
                                            else {
                                                dataAndMetaModel.objects = Array.isArray( dataAndMetaModel.objects )
                                                    ? []
                                                    : {};
                                            }
                                        }
                                    }
                                }
                            }

                            // empty container if a new reference could be added
                            if (
                                referenceField
                                && referenceSet
                                && (
                                    referenceFieldData?.addNew
                                    || referenceFieldData?.addExisting
                                )
                                && (
                                    ( repeatConfig.referenceDirection === 'SrcToDest' && cardinality?.[ 1 ] !== '1' )
                                    || ( repeatConfig.referenceDirection === 'DestToSrc' && cardinality?.[ 0 ] !== '1' )
                                )
                            ) {
                                const repeatedContainerContent = this.getContainerContent(
                                    {
                                        type    : parent.type,
                                        id      : containerLayoutElementData.id,
                                        gridSize: parent.gridSize,

                                        dataDescriptorAlias: repeatConfig.dataDescriptorAlias,
                                        referenceId        : 0,
                                        itemId             : 0,
                                        parentItemId       : parent.itemId,
                                        isRepeating        : true,
                                        hasNoParent        : parent.referenceId === 0,
                                        idSuffix           : parent.idSuffix
                                            ? `${parent.idSuffix}_0`
                                            : `_0`
                                    },
                                    [ layoutElementStructure ]
                                );

                                retVal.isDirty = retVal.isDirty || repeatedContainerContent.isDirty;
                                retVal.isInvalid = retVal.isInvalid || repeatedContainerContent.isInvalid;
                                retVal.content = retVal.content.concat( repeatedContainerContent.content );
                                retVal.tabIndexMapping[ 0 ] = repeatIndex;

                                repeatIndex++;
                            }

                            // repeat container as often as references given
                            if ( dataAndMetaModel?.type === 'reference' && dataAndMetaModel.objects?.length ) {

                                // sort references (like ReferenceField does)
                                const referenceSortMapping : ReferenceSortMapping[] = [],
                                      orderByDefinitions : OrderBy[]                = [],
                                      sortFnc                                       = ( value1 : ReferenceSortMapping, value2 : ReferenceSortMapping, orderByDefinition : OrderBy ) : -1 | 0 | 1 => {
                                          let orderValue1 = value1.item
                                                  ? messageUtils.getMessageTemplateStringResolver( value1.item )( `\${${orderByDefinition.key}}`, true ) ?? ''
                                                  : '',
                                              orderValue2 = value2.item
                                                  ? messageUtils.getMessageTemplateStringResolver( value2.item )( `\${${orderByDefinition.key}}`, true ) ?? ''
                                                  : '';

                                          // order strings case independent
                                          if ( orderValue1 && typeof orderValue1 === 'string' ) {
                                              orderValue1 = orderValue1.toLowerCase();
                                          }
                                          if ( orderValue2 && typeof orderValue2 === 'string' ) {
                                              orderValue2 = orderValue2.toLowerCase();
                                          }

                                          if ( orderValue1 > orderValue2 ) {
                                              return orderByDefinition.direction === 'asc' ? 1 : -1;
                                          }
                                          else if ( orderValue1 < orderValue2 ) {
                                              return orderByDefinition.direction === 'asc' ? -1 : 1;
                                          }
                                          else {
                                              return 0;
                                          }
                                      };

                                let references : Reference[] = [];

                                for ( const referenceId of dataAndMetaModel.objects ) {
                                    const reference = global.data[ dataContext ].references[ referenceId ],
                                          itemId    = reference[ repeatConfig.referenceDirection === 'SrcToDest' ? 'destItemId' : 'srcItemId' ],
                                          item      = global.data[ dataContext ].items[ itemId ];

                                    if ( item ) {
                                        let sortLabel : string;

                                        if ( referenceFieldData?.itemLabel ) {
                                            const labelMessageResolver = messageUtils.getMessageTemplateStringResolver( item, reference ?? referenceSet );

                                            sortLabel = labelMessageResolver( __( referenceFieldData.itemLabel ) );
                                        }
                                        else {
                                            sortLabel = item.displayAttributeValue;
                                        }

                                        referenceSortMapping.push( {
                                            reference,
                                            item,
                                            sortLabel
                                        } );
                                    }
                                }

                                if ( referenceFieldData?.orderBy ) {
                                    const orderByParts = referenceFieldData.orderBy.split( ',' );

                                    for ( const orderByPart of orderByParts ) {
                                        const orderByPartParts = ( orderByPart.trim().replaceAll( /\s+/g, ' ' ) ).split( ' ' );

                                        if ( orderByPartParts.length && orderByPartParts.length < 3 ) {
                                            const direction = ( orderByPartParts[ 1 ] ?? 'asc' ).toLowerCase();

                                            if ( direction === 'asc' || direction === 'desc' ) {
                                                orderByDefinitions.push( {
                                                    key: orderByPartParts[ 0 ],
                                                    direction
                                                } );
                                            }
                                        }
                                    }
                                }

                                // sort by ReferenceField:orderBy
                                if ( orderByDefinitions.length ) {
                                    referenceSortMapping.sort( ( value1, value2 ) => {
                                        let innerRetVal : number,
                                            orderByCount : number = 0;

                                        do {
                                            innerRetVal = sortFnc( value1, value2, orderByDefinitions[ orderByCount++ ] );
                                        } while ( innerRetVal === 0 && orderByCount < orderByDefinitions.length )

                                        return innerRetVal;
                                    } );
                                }
                                // sort by ReferenceFields itemLabel
                                else if ( referenceFieldData?.itemLabel ) {
                                    referenceSortMapping.sort( ( value1, value2 ) => {
                                        if ( value1.sortLabel.toLowerCase() > value2.sortLabel.toLowerCase() ) {
                                            return 1;
                                        }
                                        else if ( value1.sortLabel.toLowerCase() < value2.sortLabel.toLowerCase() ) {
                                            return -1;
                                        }
                                        else {
                                            return 0;
                                        }
                                    } );
                                }

                                if ( referenceSortMapping.length ) {
                                    references = referenceSortMapping.map( rSM => rSM.reference );
                                }
                                else {
                                    references = dataAndMetaModel.objects.map( referenceId => global.data[ dataContext ].references[ referenceId ] );
                                }

                                // process references
                                for ( let index = 0; index < references.length; index++ ) {
                                    const reference                       = references[ index ],
                                          realTabContainerLayoutElementId = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( parent.id, parent.referenceId );

                                    if ( !deletedStatusIds.includes( reference.statusId ) ) {
                                        const repeatedContainerContent = this.getContainerContent(
                                            {
                                                type    : parent.type,
                                                id      : containerLayoutElementData.id,
                                                gridSize: parent.gridSize,

                                                dataDescriptorAlias: repeatConfig.dataDescriptorAlias,
                                                referenceId        : reference.id,
                                                itemId             : repeatConfig.referenceDirection === 'SrcToDest'
                                                    ? reference.destItemId
                                                    : reference.srcItemId,
                                                parentItemId       : parent.itemId,
                                                isRepeating        : true,
                                                index,
                                                idSuffix           : parent.idSuffix
                                                    ? `${parent.idSuffix}_${reference.id}`
                                                    : `_${reference.id}`
                                            },
                                            [ layoutElementStructure ]
                                        );

                                        retVal.isDirty = retVal.isDirty || repeatedContainerContent.isDirty;
                                        retVal.isInvalid = retVal.isInvalid || repeatedContainerContent.isInvalid;
                                        retVal.content = retVal.content.concat( repeatedContainerContent.content );
                                        retVal.tabIndexMapping[ reference.id ] = repeatIndex;

                                        // if the reference was just added - activate tab therefor
                                        //  or there is a reference but none active
                                        if (
                                            parent.type === 'tabContainer'
                                            && (
                                                (
                                                    reference.id === repeatConfig.newReferenceId
                                                    && activeTabContainerIndexes[ realTabContainerLayoutElementId ] !== reference.id
                                                )
                                                || (
                                                    references.length
                                                    && activeTabContainerIndexes[ realTabContainerLayoutElementId ] === undefined
                                                )
                                            )
                                        ) {

                                            // TODO: remap reference id after save ( -1 => 1 )
                                            //  set reference mappings in saveData and use it

                                            this.props.setParam(
                                                uid,
                                                'activeTabContainerIndexes',
                                                {
                                                    ...activeTabContainerIndexes,
                                                    [ realTabContainerLayoutElementId ]: reference.id
                                                }
                                            );
                                        }

                                        repeatIndex++;
                                    }
                                }
                            }
                        }
                        else {
                            let containerContent : GetContainerContent               = {
                                    isDirty  : false,
                                    isInvalid: false,
                                    content  : []
                                },
                                title : React.ReactNode                              = '',
                                className                                            = [ `yn-visibility-${visibility}` ].concat( layoutElementData.elementClassName ?? [] ),
                                style                                                = { ...layoutElementData.elementStyle, ...elementStyle },
                                commonAdditionalResolverParametersForMessageEnhanced = commonAdditionalResolverParametersForMessage;

                            if ( parent.dataDescriptorAlias ) {
                                repeatingContainer = true;
                            }

                            if ( messages?.title ) {
                                const resolvedDataObjectsForMessage : ResolvedDataObjectChain = {
                                    ...commonResolvedDataObjectsForMessage
                                };

                                if ( parent.isRepeating ) {
                                    const repeatedReference = global.data[ dataContext ].references[ parent.referenceId ?? 0 ],
                                          repeatedItem      = global.data[ dataContext ].items[ parent.itemId ?? 0 ];

                                    resolvedDataObjectsForMessage[ 'repeatedObject' ] = {
                                        item     : repeatedItem,
                                        reference: repeatedReference
                                    };

                                    if ( parent.index !== undefined ) {
                                        commonAdditionalResolverParametersForMessageEnhanced = {
                                            ...commonAdditionalResolverParametersForMessageEnhanced,
                                            variables: {
                                                ...commonAdditionalResolverParametersForMessageEnhanced.variables,
                                                repeatIndex: parent.index
                                            }
                                        };
                                    }
                                }

                                title = this.resolveMessage( messages.title, resolvedDataObjectsForMessage, commonAdditionalResolverParametersForMessageEnhanced, parent.isRepeating ? parent.itemId : undefined );

                                if ( !isInvalidHTML( title as string ) ) {
                                    title = parse( title as string );
                                }
                            }

                            // style
                            if ( containerLayoutElementData.styles?.style ) {
                                className = className.concat( resolveStyleToClassNames( containerLayoutElementData.styles.style, dataDescriptorToValueMapping ) );
                                style = {
                                    ...style,
                                    ...resolveStyle( containerLayoutElementData.styles.style, {} )
                                };
                            }

                            // layout wrapper
                            if ( isLayoutWrapper ) {
                                containerContent = this.getContainerContent(
                                    {
                                        ...parent,
                                        type       : parent.type === 'form' || parent.type === 'layoutWrapper' ? 'layoutWrapper' : 'container',
                                        id         : containerLayoutElementData.id,
                                        gridSize   : childrenGridSize,
                                        isRepeating: false
                                    },
                                    layoutElementStructure.children
                                );

                                retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                retVal.content.push(
                                    <div
                                        key={`${containerLayoutElementData.key}${parent.idSuffix ?? ''}`}
                                        className={`yn-form-layout-wrapper ${className.join( ' ' )}`}
                                        style={style}
                                    >
                                        {containerContent.content}
                                    </div>
                                );
                            }

                            // card
                            else if ( parent.type === 'form' || parent.type === 'layoutWrapper' ) {
                                containerContent = this.getContainerContent(
                                    {
                                        ...parent,
                                        type       : 'card',
                                        id         : containerLayoutElementData.id,
                                        gridSize   : childrenGridSize,
                                        isRepeating: false
                                    },
                                    layoutElementStructure.children
                                );

                                retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                retVal.content.push(
                                    <div
                                        key={`${containerLayoutElementData.key}${parent.idSuffix ?? ''}`}
                                        className={`yn-form-card ${className.join( ' ' )}`}
                                        style={style}
                                    >
                                        <Card title={<span style={layoutElementData.titleStyle}>{title ?? ' '}</span>}
                                              className={`yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}>
                                            {containerContent.content}
                                        </Card>
                                    </div>
                                );
                            }

                            // tabPanel
                            else if ( parent.type === 'tabContainer' ) {
                                containerContent = this.getContainerContent(
                                    {
                                        ...parent,
                                        type       : 'container',
                                        id         : containerLayoutElementData.id,
                                        gridSize   : childrenGridSize,
                                        isRepeating: false
                                    },
                                    layoutElementStructure.children
                                );

                                retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                retVal.content.push(
                                    <TabPanel
                                        key={`${containerLayoutElementData.key}${parent.idSuffix ?? ''}_tabPanel`}

                                        header={title}

                                        disabled={containerLayoutElementData.disableTab}
                                        headerClassName={containerContent.isInvalid
                                            ? 'yn-error'
                                            : ( containerContent.isDirty
                                                    ? 'yn-dirty'
                                                    : ''
                                            )}
                                        className={`yn-form-tabpanel ${className.join( ' ' )} yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                                        style={layoutElementData.titleStyle}
                                    >
                                        <div className="yn-form-container-content">
                                            <div
                                                key={`${containerLayoutElementData.key}${parent.idSuffix ?? ''}`}
                                                className={`yn-form-container ${className.join( ' ' )}`}
                                                style={style}
                                            >
                                                {containerContent.content}
                                            </div>
                                        </div>
                                    </TabPanel>
                                );
                            }

                            // all others
                            else {
                                containerContent = this.getContainerContent(
                                    {
                                        ...parent,
                                        type       : 'container',
                                        id         : containerLayoutElementData.id,
                                        gridSize   : childrenGridSize,
                                        isRepeating: false
                                    },
                                    layoutElementStructure.children
                                );

                                retVal.isDirty = retVal.isDirty || containerContent.isDirty;
                                retVal.isInvalid = retVal.isInvalid || containerContent.isInvalid;
                                retVal.content.push(
                                    <div
                                        key={`${containerLayoutElementData.key}${parent.idSuffix ?? ''}`}
                                        className={`yn-form-container ${className.join( ' ' )}`}
                                        style={style}
                                    >
                                        {( title || titleVisibility === 'placeholder' ) &&
                                            <div
                                                className={`yn-form-container-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                                                style={layoutElementData.titleStyle}>{title ?? ' '}</div>
                                        }
                                        {containerContent.content}
                                    </div>
                                );
                            }
                        }
                    }

                    // tabView
                    else if ( layoutElementStructure.type === 'TABCONTAINER' ) {
                        const tabContainerLayoutElementData = layoutElementData as TabContainerProperties,
                              {
                                  messages,
                                  titleAlignment
                              }                             = tabContainerLayoutElementData,
                              titleVisibility               = messages?.title ? tabContainerLayoutElementData.titleVisibility : 'hidden',
                              className                     = [ `yn-visibility-${visibility}` ].concat( layoutElementData.elementClassName ? layoutElementData.elementClassName : [] ),
                              style                         = { ...layoutElementData.elementStyle, ...elementStyle },
                              tabContainerContent           = this.getContainerContent(
                                  {
                                      ...parent,
                                      type       : 'tabContainer',
                                      id         : tabContainerLayoutElementData.id,
                                      gridSize   : childrenGridSize,
                                      isRepeating: false
                                  },
                                  layoutElementStructure.children
                              ),
                              realLayoutElementId           = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parent.referenceId );

                        let activeIndex                                          = tabContainerContent.tabIndexMapping?.[ activeTabContainerIndexes[ realLayoutElementId ] ] ?? activeTabContainerIndexes[ realLayoutElementId ] ?? tabContainerLayoutElementData.activeTabIndex ?? 0,
                            title : React.ReactNode                              = '',
                            commonAdditionalResolverParametersForMessageEnhanced = commonAdditionalResolverParametersForMessage;

                        if ( activeIndex < 0 ) {
                            activeIndex = 0;
                        }

                        if ( parent.index ) {
                            commonAdditionalResolverParametersForMessageEnhanced = {
                                ...commonAdditionalResolverParametersForMessageEnhanced,
                                variables: {
                                    ...commonAdditionalResolverParametersForMessageEnhanced.variables,
                                    repeatIndex: parent.index
                                }
                            };
                        }

                        if ( messages?.title ) {
                            const resolvedDataObjectsForMessage : ResolvedDataObjectChain = {
                                ...commonResolvedDataObjectsForMessage
                            };

                            if ( parent.isRepeating ) {
                                const repeatedReference = global.data[ dataContext ].references[ parent.referenceId ?? 0 ],
                                      repeatedItem      = global.data[ dataContext ].items[ parent.itemId ?? 0 ];

                                resolvedDataObjectsForMessage[ 'repeatedObject' ] = {
                                    item     : repeatedItem,
                                    reference: repeatedReference
                                };
                            }

                            title = parse( this.resolveMessage( messages.title, resolvedDataObjectsForMessage, commonAdditionalResolverParametersForMessageEnhanced, parent.isRepeating ? parent.itemId : undefined ) );
                            //title = parse( this.messageTemplateStringResolver?.( __( messages.title ) ) ?? '' );
                        }

                        if ( tabContainerLayoutElementData.distributeTabsEvenly ) {
                            className.push( 'yn-distribute-tabs-evenly' );
                        }

                        retVal.isDirty = retVal.isDirty || tabContainerContent.isDirty;
                        retVal.isInvalid = retVal.isInvalid || tabContainerContent.isInvalid;
                        retVal.content.push(
                            <div
                                key={`${tabContainerLayoutElementData.key}${parent.idSuffix ?? ''}`}
                                id={`leid_${realLayoutElementId}`}
                                className={`yn-form-tabcontainer ${className.join( ' ' )}`}
                                style={style}
                            >
                                {( title || titleVisibility === 'placeholder' ) &&
                                    <div
                                        className={`yn-form-tabcontainer-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                                        style={layoutElementData.titleStyle}>{title ?? ' '}</div>
                                }
                                <TabView
                                    key={`${tabContainerLayoutElementData.key}${parent.idSuffix ?? ''}_tabView`}

                                    activeIndex={activeIndex}
                                    onTabChange={this.onTabContainerTabChange.bind( this, realLayoutElementId, tabContainerContent.tabIndexMapping )}

                                    pt={{
                                        navContent: {
                                            onScroll: this.onTabContainerScroll.bind( this, realLayoutElementId )
                                        }
                                    }}

                                    className="yn-form-tabview"
                                >
                                    {tabContainerContent.content}
                                </TabView>
                            </div>
                        );
                    }

                    // FormFields
                    else {
                        const formFieldLayoutElement     = layoutElement as FormFieldData,
                              formFieldLayoutElementData = layoutElementData as FormFieldProperties | VariableFieldProperties,
                              {
                                  required,
                                  tabOrder,
                                  type
                              }                          = formFieldLayoutElementData,
                              formFieldProps             = layoutElementData.props,
                              dataDescriptorAlias        = formFieldLayoutElement.dataDescriptorAlias ?? '',
                              dataDescriptor             = dataDescriptors[ dataDescriptorAlias ];

                        let formFieldLayoutElementId : number | string = layoutElementId,
                            dataAndMetaModel                           = elusiveData?.dataDescriptorMappings?.dataAndMetaModel?.[ dataDescriptorAlias ],
                            value                                      = elusiveData?.dataDescriptorMappings?.value?.[ dataDescriptorAlias ],
                            originalValue                              = elusiveData?.dataDescriptorMappings?.originalValue?.[ dataDescriptorAlias ],
                            parentId : number | undefined              = undefined;

                        // handle dataAndModel.objects, value, originalValue, setValueErrors and errors for reference repeating container fields
                        if ( parent.dataDescriptorAlias ) {
                            const parentDataDescriptor = dataDescriptors[ parent.dataDescriptorAlias ];

                            // recompute dataAndModel.objects, value and originalValue
                            if ( dataAndMetaModel && dataDescriptor && parentDataDescriptor.dataObject ) {

                                // fields handling the reference or the referenced item
                                if (
                                    dataDescriptor.dataObject
                                    && dataDescriptor.dataObject.id === parentDataDescriptor.dataObject.id
                                ) {
                                    if ( dataDescriptor.type === 'reference' || dataDescriptor.type === 'referenceProperty' ) {
                                        parentId = parent.referenceId;
                                    }
                                    else if ( dataDescriptor.type === 'item' || dataDescriptor.type === 'property' ) {
                                        parentId = parent.itemId;
                                    }
                                }

                                // all other fields (referenced to the referenced item)
                                else if ( parent.itemId !== undefined ) {
                                    const subDataDescriptors = {
                                              [ dataDescriptorAlias ]: dataDescriptor
                                          } as DataDescriptors,
                                          subDataStructure   = getDataStructure( parentDataDescriptor.dataObject.id, dataStructure );

                                    if ( subDataStructure ) {
                                        const dataDescriptorMappings = subDataStructure
                                            ? {
                                                dataAndMetaModel: this.buildDataDescriptorToMetaModelMapping(
                                                    subDataDescriptors,
                                                    dataContext,
                                                    schema,
                                                    subDataStructure,
                                                    parent.itemId,
                                                    user
                                                )
                                            } as DataDescriptorMappings
                                            : undefined;

                                        if ( dataDescriptorMappings ) {
                                            dataDescriptorMappings.dataAndMetaModel = this.buildDataDescriptorToDataMapping(
                                                subDataDescriptors,
                                                dataDescriptorMappings,
                                                dataContext,
                                                schema,
                                                subDataStructure,
                                                parent.itemId
                                            );

                                            if ( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects ) {
                                                if ( Array.isArray( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects ) ) {
                                                    parentId = ( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects as number[] )[ 0 ];
                                                }
                                                else if ( Object.keys( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects ).length ) {
                                                    parentId = Number( Object.keys( dataDescriptorMappings.dataAndMetaModel[ dataDescriptorAlias ].objects )[ 0 ] );
                                                }
                                            }
                                        }
                                    }
                                }

                                if ( parentId ) {
                                    const realDataDescriptorAlias = this.getRealLayoutElementDataDescriptorAliasFromLayoutElementDataDescriptorAliasAndParentId( dataDescriptorAlias, parentId ),
                                          newDataAndMetaModel     = elusiveData?.dataDescriptorMappings?.dataAndMetaModel?.[ realDataDescriptorAlias ];

                                    if ( newDataAndMetaModel ) {
                                        dataAndMetaModel = newDataAndMetaModel;
                                    }
                                    else if ( dataAndMetaModel.type !== 'static' ) {
                                        dataAndMetaModel = {
                                            ...dataAndMetaModel,
                                            values        : {},
                                            originalValues: {},
                                            hasNoParent   : false
                                        } as ObjectDataAndMetaModelMapping;

                                        if ( dataAndMetaModel.type === 'item' || dataAndMetaModel.type === 'reference' ) {
                                            dataAndMetaModel.objects = [ parentId ];
                                        }
                                        else {
                                            let propertySet : PropertySet | ReferencePropertySet | undefined;

                                            dataAndMetaModel.objects = {};

                                            if ( dataAndMetaModel.type === 'property' || dataAndMetaModel.type === 'enumProperty' ) {
                                                propertySet = metaModel[ schema ].getPropertySet( dataAndMetaModel.metaModels[ 0 ] );
                                            }
                                            else {
                                                propertySet = metaModel[ schema ].getReferencePropertySet( dataAndMetaModel.metaModels[ 0 ] );
                                            }

                                            if ( propertySet ) {
                                                dataAndMetaModel.objects[ parentId ] = [ propertySet.propertyTypeId ];
                                            }
                                        }
                                    }

                                    value = elusiveData?.dataDescriptorMappings?.value?.[ realDataDescriptorAlias ];
                                    originalValue = elusiveData?.dataDescriptorMappings?.originalValue?.[ realDataDescriptorAlias ];
                                }
                                else {
                                    dataAndMetaModel = { ...dataAndMetaModel };

                                    if (
                                        dataDescriptor.type === 'item'
                                        || dataDescriptor.type === 'reference'
                                    ) {
                                        dataAndMetaModel.objects = [];
                                    }
                                    else {
                                        dataAndMetaModel.objects = {};
                                    }

                                    dataAndMetaModel.values = {};
                                    dataAndMetaModel.originalValues = {};

                                    value = undefined;
                                    originalValue = undefined;
                                }

                                formFieldLayoutElementId = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parentId );
                            }
                            else if ( type === 'VARIABLE' || type === 'BUTTON' ) {
                                formFieldLayoutElementId = this.getRealLayoutElementIdFromLayoutElementIdAndParentId( layoutElementId, parent.itemId );
                            }
                        }

                        // extract value and originalValue from ReferenceValue / ReferenceValue[] for dataDescriptor.type === 'reference' and type !== 'REFERENCE' and type !== 'FILE'
                        if ( dataDescriptor && dataDescriptor.type === 'reference' && type !== 'REFERENCE' && type !== 'FILE' ) {
                            if ( value ) {
                                let eValue : ReferenceValue | undefined;

                                if ( Array.isArray( value ) ) {
                                    eValue = ( value as ReferenceValue[] )[ 0 ] as ReferenceValue;
                                }
                                else {
                                    eValue = value as ReferenceValue;
                                }

                                if ( isReferenceAttribute( dataDescriptor.attribute ) ) {
                                    value = eValue?.[ dataDescriptor.attribute ];
                                }
                                else {
                                    value = undefined;
                                }
                            }

                            if ( originalValue ) {
                                let eValue : ReferenceValue | undefined;

                                if ( Array.isArray( originalValue ) ) {
                                    eValue = ( originalValue as ReferenceValue[] )[ 0 ] as ReferenceValue;
                                }
                                else {
                                    eValue = originalValue as ReferenceValue;
                                }

                                if ( isReferenceAttribute( dataDescriptor.attribute ) ) {
                                    originalValue = eValue?.[ dataDescriptor.attribute ];
                                }
                                else {
                                    originalValue = undefined;
                                }
                            }
                        }

                        const setValueErrors = elusiveData?.layoutElementMappings?.setValueErrors?.[ formFieldLayoutElementId ],
                              errors         = elusiveData?.layoutElementMappings?.errors?.[ formFieldLayoutElementId ];

                        try {
                            // get dataValueType, data and metaModel mapping and checks
                            if ( dataDescriptor ) {
                                const dataValueType = ( dataUtils.getDataValueType( dataDescriptor, schema, dataStructure ) ?? 'STRING' ) as PropertyValueType;

                                // check metaModel exists
                                if ( !dataAndMetaModel?.metaModels?.length ) {
                                    throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): couldn't get metaModel` ) );
                                }

                                // check type mismatch between data type and possible field types
                                if (
                                    type !== 'BUTTON'
                                    && type !== 'CALENDAR'
                                    && type !== 'CHART'
                                    && type !== 'GRAPH'
                                    && type !== 'MAP'
                                    && type !== 'OVERVIEW'
                                    && type !== 'STATIC'
                                    && type !== 'VARIABLE'
                                    && dataAndMetaModel.type !== 'enumProperty'
                                    && dataAndMetaModel.type !== 'enumReferenceProperty'
                                    && dataAndMetaModel.type !== 'reference'
                                    && !fieldUtils.checkValueTypeForFieldType( dataValueType, type )
                                ) {
                                    throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): ${fieldTypeToItemTypeMapping[ type ]} is not allowed for data type ${dataValueType} (allowed: ${fieldUtils.getAllowedFieldTypesForValueType( dataValueType )?.join( ', ' )})` ) );
                                }

                                // check metaModels count
                                else if ( dataAndMetaModel.metaModels.length > 1 ) {
                                    throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): more than one matching metaModel` ) );
                                }

                                // validate data - check objects count
                                if (
                                    dataAndMetaModel?.objects
                                    && type !== 'CALENDAR'
                                    && type !== 'CHART'
                                    && type !== 'GRAPH'
                                    && type !== 'MAP'
                                    && type !== 'OVERVIEW'
                                    && type !== 'FILE'
                                    && type !== 'REFERENCE'
                                    && (
                                        Object.keys( dataAndMetaModel.objects ).length > 1
                                        || (
                                            (
                                                dataAndMetaModel.type === 'property'
                                                || dataAndMetaModel.type === 'enumProperty'
                                                || dataAndMetaModel.type === 'referenceProperty'
                                                || dataAndMetaModel.type === 'enumReferenceProperty'
                                            )
                                            && Object.keys( Object.values( dataAndMetaModel.objects ?? {} )[ 0 ] ?? {} ).length > 1
                                        )
                                    )
                                ) {
                                    throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): more than one matching data object` ) );
                                }

                                // enumerated (reference) property requires EnumField check
                                if (
                                    (
                                        dataAndMetaModel.type === 'enumProperty'
                                        || dataAndMetaModel.type === 'enumReferenceProperty'
                                    )
                                    && type !== 'ENUM'
                                ) {
                                    throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): wrong field type '${type}' for enumerated property` ) );
                                }
                            }
                            else if ( type !== 'BUTTON' && type !== 'CALENDAR' && type !== 'CHART' && type !== 'GRAPH' && type !== 'MAP' && type !== 'OVERVIEW' && type !== 'STATIC' && type !== 'VARIABLE' ) {
                                throw ( new Error( `'${layoutElementData.key}' (${layoutElementData.name}): no DataObject` ) );
                            }

                            // get dynamic formField props
                            const dynamicFormFieldProps : {
                                key : string;
                                uid : string;
                                value? : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[];
                                originalValue? : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[];
                                displayValue? : string | JSX.Element | JSX.Element[] | ( string | JSX.Element )[];
                                //
                                disabled : boolean;
                                markDirty? : boolean;
                                //
                                classNames : string[];
                                style : React.CSSProperties;
                                displayClassNames : string[];
                                displayStyle : React.CSSProperties;
                                inputClassNames : string[];
                                inputStyle : React.CSSProperties;
                                labelClassNames : string[];
                                labelStyle : React.CSSProperties;
                                messageClassNames : string[];
                                messageStyle : React.CSSProperties;
                                displayMode? : 'card' | 'field';
                                //
                                appendToElementId? : string;
                                tabIndex? : number;
                                required? : boolean;
                                errorMessage? : string;
                                tooltip? : string;
                                //
                                messageTemplateStringResolver? : ( templateString : string ) => string;
                                //
                                additionalParameters? : { [ parameter : string ] : any };
                                additionalQueryParameters : Required<LoadReferenceFieldItemsParameters>[ 'query' ][ 'parameters' ];
                                //
                                variables? : Variables;
                                //
                                filters? : string;
                                fixFilters? : string;
                                //
                                onChange : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) => void;
                                onIntermediateChange : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | undefined, originalEvent : any ) => void;
                                onInvalid? : ( errors : string[] ) => void,
                                onIntermediateInvalid? : ( errors : string[] ) => void,
                                onValid? : () => void,
                                onIntermediateValid? : () => void,
                                onTriggerAction : ( originalEvent : any ) => void;
                                onVariableChange? : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | {
                                    start : string;
                                    end : string;
                                } | undefined, originalEvent : any ) => void;
                                onIntermediateVariableChange? : ( value : AttributeValue | EnumValue | ReferenceValue | ReferenceValue[] | {
                                    start : string;
                                    end : string;
                                } | undefined, originalEvent : any ) => void;
                            } = {
                                key              : `${layoutElementData.key}${parent.idSuffix ?? ''}`,
                                uid              : `${layoutElementData.uid}${parent.idSuffix ?? ''}`,
                                disabled         : formFieldProps.disabled || parent.hasNoParent || ( type !== 'REFERENCE' && type !== 'BUTTON' && parent.dataDescriptorAlias && !parent.referenceId ),
                                classNames       : layoutElementData.elementClassNames ? ( [] as string[] ).concat( layoutElementData.elementClassNames ) : [],
                                style            : { ...layoutElementData.elementStyle, ...elementStyle },
                                displayClassNames: layoutElementData.displayClassNames ? ( [] as string[] ).concat( layoutElementData.displayClassNames ) : [],
                                displayStyle     : { ...layoutElementData.displayStyle },
                                inputClassNames  : layoutElementData.inputClassNames ? ( [] as string[] ).concat( layoutElementData.inputClassNames ) : [],
                                inputStyle       : { ...layoutElementData.inputStyle },
                                labelClassNames  : layoutElementData.labelClassNames ? ( [] as string[] ).concat( layoutElementData.labelClassNames ) : [],
                                labelStyle       : { ...layoutElementData.labelStyle },
                                messageClassNames: layoutElementData.messageClassNames ? ( [] as string[] ).concat( layoutElementData.messageClassNames ) : [],
                                messageStyle     : { ...layoutElementData.messageStyle },
                                displayMode      : parent.type !== 'form' && parent.type !== 'layoutWrapper'
                                    ? 'field'
                                    : 'card',
                                //
                                appendToElementId: mode === 'popup' || mode === 'standalonePopup' ? 'document' : undefined,
                                tabIndex         : type !== 'STATIC'
                                    ? ( tabOrder ?? 0 ) === -1
                                        ? tabOrder
                                        : tabIndexOffset + ( ( tabOrder ?? this.tabIndex ) * this.tabIndexStep )
                                    : undefined,
                                required,
                                errorMessage     : '',
                                // TODO: build messageResolver for Message messages (see Container:messages.title)
                                messageTemplateStringResolver: this.messageTemplateStringResolver,
                                //
                                additionalQueryParameters: {
                                    formParentItemId: String( formParentItemId ),
                                    treeRootItemId  : String( treeRootItemId ),
                                    treeParentItemId: String( treeParentItemId )
                                },
                                //
                                variables: {
                                    ...treeVariables,
                                    ...elusiveData?.variables,
                                    formDataContext : dataContext,
                                    fieldItemId     : layoutElementData.id,
                                    formRootItemId  : rootItemId,
                                    formParentItemId: formParentItemId,
                                    treeRootItemId  : treeRootItemId,
                                    treeParentItemId: treeParentItemId,
                                    propertyTypeName: dataDescriptor?.propertyTypeName
                                },
                                //
                                onChange                    : type === 'VARIABLE'
                                    ? this.onVariableChange.bind( this, formFieldLayoutElementId )
                                    : this.onValueChange.bind( this, formFieldLayoutElementId ),
                                onIntermediateChange        : type === 'VARIABLE'
                                    ? this.onIntermediateVariableChange.bind( this, formFieldLayoutElementId )
                                    : this.onIntermediateValueChange.bind( this, formFieldLayoutElementId ),
                                onInvalid                   : type === 'VARIABLE'
                                    ? this.onValueInvalid.bind( this, formFieldLayoutElementId )
                                    : this.onValueInvalid.bind( this, formFieldLayoutElementId ),
                                onIntermediateInvalid       : type === 'VARIABLE'
                                    ? this.onIntermediateValueInvalid.bind( this, formFieldLayoutElementId )
                                    : this.onIntermediateValueInvalid.bind( this, formFieldLayoutElementId ),
                                onValid                     : type === 'VARIABLE'
                                    ? this.onValueValid.bind( this, formFieldLayoutElementId )
                                    : this.onValueValid.bind( this, formFieldLayoutElementId ),
                                onIntermediateValid         : type === 'VARIABLE'
                                    ? this.onIntermediateValueValid.bind( this, formFieldLayoutElementId )
                                    : this.onIntermediateValueValid.bind( this, formFieldLayoutElementId ),
                                onTriggerAction             : type === 'VARIABLE' || type === 'OVERVIEW'
                                    ? ( originalEvent : any ) => {
                                        void this.onTriggerAction( formFieldLayoutElementId, originalEvent );
                                    }
                                    : ( originalEvent : any ) => {
                                        void this.onTriggerAction( formFieldLayoutElementId, originalEvent );
                                    },
                                onVariableChange            : this.onVariableChange.bind( this, formFieldLayoutElementId ),
                                onIntermediateVariableChange: this.onIntermediateVariableChange.bind( this, formFieldLayoutElementId )
                            };

                            let isInherited : boolean = false;

                            if ( type === 'ENUM' ) {
                                if ( formFieldProps.keyEditorParams?.autoCompleteRequestParameters ) {
                                    formFieldProps.keyEditorParams.autoCompleteRequestParameters.additionalParameters = {
                                        formParentItemId: String( formParentItemId ),
                                        treeRootItemId  : String( treeRootItemId ),
                                        treeParentItemId: String( treeParentItemId )
                                    };
                                }
                                if ( formFieldProps.valueEditorParams?.autoCompleteRequestParameters ) {
                                    formFieldProps.valueEditorParams.autoCompleteRequestParameters.additionalParameters = {
                                        formParentItemId: String( formParentItemId ),
                                        treeRootItemId  : String( treeRootItemId ),
                                        treeParentItemId: String( treeParentItemId )
                                    };
                                }
                            }

                            // count tabIndex up
                            this.tabIndex += 1;

                            // value, originalValue and status coloring
                            if ( dataDescriptor && dataAndMetaModel && dataDescriptor.type !== 'expression' ) {
                                // value and originalValue
                                dynamicFormFieldProps.value = value;
                                dynamicFormFieldProps.originalValue = originalValue;

                                // status coloring
                                const object = this.getObjectFromDataAndMetaModelMapping( dataAndMetaModel ) as Item | Property | Reference | ReferenceProperty | undefined;

                                if ( object && !object.isDeleted?.() ) {
                                    dynamicFormFieldProps.inputStyle = { ...dynamicFormFieldProps.inputStyle, ...getStatusColorStyleObject( object.statusId, schema ) };
                                }
                                else if ( dataAndMetaModel.type === 'property' ) {
                                    const item = dataUtils.getData(
                                        buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                        dataStructure,
                                        dataContext,
                                        rootItemId!
                                    )?.[ 0 ].object as Item;

                                    if ( item ) {
                                        dynamicFormFieldProps.inputStyle = { ...dynamicFormFieldProps.inputStyle, ...getStatusColorStyleObject( item.statusId, schema ) };

                                        // inject inheritance
                                        const propertyTypeName = _.lowerFirst( dataDescriptor.propertyTypeName ?? '' );
                                        if (
                                            treeVariables?.inheritedProps
                                            && item.id === rootItemId
                                            && (
                                                dynamicFormFieldProps.value === undefined
                                                || dataAndMetaModel?.isDefault
                                            )
                                            && Object.keys( treeVariables.inheritedProps ).includes( propertyTypeName )
                                        ) {
                                            isInherited = true;
                                            dynamicFormFieldProps.value = treeVariables.inheritedProps[ propertyTypeName ].value;
                                            dynamicFormFieldProps.tooltip = __( 'YN.base.form.tooltip.property.inheritance.title', {
                                                itemTypeLabel: getItemTypeDisplayName( treeVariables.inheritedProps[ propertyTypeName ].inheritedFrom.itemType, schema ),
                                                itemKey      : treeVariables.inheritedProps[ propertyTypeName ].inheritedFrom.key
                                            } );
                                        }
                                    }
                                }
                                else if ( dataAndMetaModel.type === 'referenceProperty' ) {
                                    const reference = dataUtils.getData(
                                        buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ObjectDataDescriptor,
                                        dataStructure,
                                        dataContext,
                                        rootItemId!
                                    )?.[ 0 ].object as Reference;

                                    if ( reference ) {
                                        dynamicFormFieldProps.inputStyle = { ...dynamicFormFieldProps.inputStyle, ...getStatusColorStyleObject( reference.statusId, schema ) };
                                    }
                                }
                            }
                            // value and originalValue
                            else if ( type === 'VARIABLE' ) {
                                dynamicFormFieldProps.value = undefined
                                    ?? variablesEditValues.repeated?.[ formFieldLayoutElementData.variableName ]?.[ parent.itemId ?? 0 ]
                                    ?? variablesEditValues[ formFieldLayoutElementData.variableName ]
                                    ?? variables.repeated?.[ formFieldLayoutElementData.variableName ]?.[ parent.itemId ?? 0 ]
                                    ?? variables[ formFieldLayoutElementData.variableName ];

                                dynamicFormFieldProps.originalValue = undefined
                                    ?? variables.repeated?.[ `${formFieldLayoutElementData.variableName}_originalValue` ]?.[ parent.itemId ?? 0 ]
                                    ?? variables[ `${formFieldLayoutElementData.variableName}_originalValue` ]
                                    ?? variables.repeated?.[ formFieldLayoutElementData.variableName ]?.[ parent.itemId ?? 0 ]
                                    ?? variables[ formFieldLayoutElementData.variableName ];

                                if ( dynamicFormFieldProps.value === undefined && variables[ `${formFieldLayoutElementData.variableName}_defaultValue` ] !== undefined ) {
                                    dynamicFormFieldProps.value = variables[ `${formFieldLayoutElementData.variableName}_defaultValue` ];
                                    dataAndMetaModel = {
                                        ...dataAndMetaModel,
                                        isDefault: true
                                    } as PropertyDataAndMetaModelMapping;
                                }

                                dynamicFormFieldProps.markDirty = !dynamicFormFieldProps.disabled && !dataAndMetaModel?.isDefault && ( undefined
                                    ?? variablesEditValues.repeated?.[ formFieldLayoutElementData.variableName ]?.[ parent.itemId ?? 0 ]
                                    ?? variablesEditValues[ formFieldLayoutElementData.variableName ]
                                    ?? variables.repeated?.[ `${formFieldLayoutElementData.variableName}_originalValue` ]?.[ parent.itemId ?? 0 ]
                                    ?? variables[ `${formFieldLayoutElementData.variableName}_originalValue` ]
                                ) !== undefined;
                            }

                            // resolve dynamic additional parameter values
                            if ( formFieldProps.additionalParameters ) {
                                dynamicFormFieldProps.additionalParameters = {};

                                for ( const parameter of Object.keys( formFieldProps.additionalParameters ) as string[] ) {
                                    if ( layoutElementData.additionalParametersDataDescriptors?.[ parameter ] ) {
                                        const stringData : { [ dataDescriptorAlias : string ] : string } = {};

                                        for ( const parameterDataDescriptor of layoutElementData.additionalParametersDataDescriptors[ parameter ] as ObjectDataDescriptor[] ) {
                                            const parameterValueType = dataUtils.getDataValueType(
                                                      parameterDataDescriptor,
                                                      schema,
                                                      dataStructure
                                                  ) as PropertyValueType,
                                                  parameterData      = dataUtils.getData(
                                                      parameterDataDescriptor,
                                                      dataStructure,
                                                      dataContext,
                                                      rootItemId
                                                  )?.[ 0 ],
                                                  parameterDataValue = parameterData
                                                      ? dataUtils.getValueFromData(
                                                          parameterDataDescriptor,
                                                          parameterData as Parameters<typeof dataUtils.getValueFromData>[ 1 ]
                                                      ) as AttributeValue
                                                      : undefined;

                                            if ( parameterDataValue && parameterValueType ) {
                                                stringData[ `${parameterDataDescriptor.dataObject.item.key}.${parameterDataDescriptor.attributePath}` ] = convertValueToString( parameterDataValue, parameterValueType );
                                            }
                                        }

                                        dynamicFormFieldProps.additionalParameters[ parameter ] = __( formFieldProps.additionalParameters[ parameter ], stringData );
                                    }
                                    else {
                                        dynamicFormFieldProps.additionalParameters[ parameter ] = this.messageTemplateStringResolver?.( formFieldProps.additionalParameters[ parameter ], true ) ?? formFieldProps.additionalParameters[ parameter ];
                                    }
                                }
                            }

                            // resolve dynamic filters values
                            if ( formFieldProps.filters ) {
                                dynamicFormFieldProps.filters = '';

                                if ( layoutElementData.additionalFiltersDataDescriptors ) {
                                    const stringData : { [ dataDescriptorAlias : string ] : string } = {};

                                    for ( const parameterDataDescriptor of layoutElementData.additionalFiltersDataDescriptors as ObjectDataDescriptor[] ) {
                                        const parameterValueType = dataUtils.getDataValueType(
                                                  parameterDataDescriptor,
                                                  schema,
                                                  dataStructure
                                              ) as PropertyValueType,
                                              parameterData      = dataUtils.getData(
                                                  parameterDataDescriptor,
                                                  dataStructure,
                                                  dataContext,
                                                  rootItemId
                                              )?.[ 0 ],
                                              parameterDataValue = parameterData
                                                  ? dataUtils.getValueFromData(
                                                      parameterDataDescriptor,
                                                      parameterData as Parameters<typeof dataUtils.getValueFromData>[ 1 ]
                                                  ) as AttributeValue
                                                  : undefined;

                                        if ( parameterDataValue && parameterValueType ) {
                                            stringData[ `${parameterDataDescriptor.dataObject.item.key}.${parameterDataDescriptor.attributePath}` ] = convertValueToString( parameterDataValue, parameterValueType );
                                        }
                                    }

                                    dynamicFormFieldProps.filters = __( formFieldProps.filters, stringData );
                                }
                                else {
                                    dynamicFormFieldProps.filters = this.messageTemplateStringResolver?.( formFieldProps.filters, true ) ?? formFieldProps.filters;
                                }
                            }

                            // resolve dynamic fixFilters values
                            if ( formFieldProps.fixFilters ) {
                                dynamicFormFieldProps.fixFilters = '';

                                if ( layoutElementData.additionalFixFiltersDataDescriptors ) {
                                    const stringData : { [ dataDescriptorAlias : string ] : string } = {};

                                    for ( const parameterDataDescriptor of layoutElementData.additionalFixFiltersDataDescriptors as ObjectDataDescriptor[] ) {
                                        const parameterValueType = dataUtils.getDataValueType(
                                                  parameterDataDescriptor,
                                                  schema,
                                                  dataStructure
                                              ) as PropertyValueType,
                                              parameterData      = dataUtils.getData(
                                                  parameterDataDescriptor,
                                                  dataStructure,
                                                  dataContext,
                                                  rootItemId
                                              )?.[ 0 ],
                                              parameterDataValue = parameterData
                                                  ? dataUtils.getValueFromData(
                                                      parameterDataDescriptor,
                                                      parameterData as Parameters<typeof dataUtils.getValueFromData>[ 1 ]
                                                  ) as AttributeValue
                                                  : undefined;

                                        if ( parameterDataValue && parameterValueType ) {
                                            stringData[ `${parameterDataDescriptor.dataObject.item.key}.${parameterDataDescriptor.attributePath}` ] = convertValueToString( parameterDataValue, parameterValueType );
                                        }
                                    }

                                    dynamicFormFieldProps.fixFilters = __( formFieldProps.fixFilters, stringData );
                                }
                                else {
                                    dynamicFormFieldProps.fixFilters = this.messageTemplateStringResolver?.( formFieldProps.fixFilters, true ) ?? formFieldProps.fixFilters;
                                }
                            }

                            // style
                            if ( formFieldLayoutElementData.styles?.style ) {
                                dynamicFormFieldProps.inputClassNames = dynamicFormFieldProps.inputClassNames.concat( resolveStyleToClassNames( formFieldLayoutElementData.styles.style, dataDescriptorToValueMapping, undefined, undefined, true ) );
                                /*dynamicFormFieldProps.style = {
                                    ...dynamicFormFieldProps.style,
                                    ...resolveStyle( formFieldLayoutElementData.styles.style, dataDescriptorToValueMapping )
                                };*/
                            }

                            // labelStyle
                            if ( formFieldLayoutElementData.styles?.labelStyle ) {
                                dynamicFormFieldProps.labelClassNames = dynamicFormFieldProps.labelClassNames.concat( resolveStyleToClassNames( formFieldLayoutElementData.styles.labelStyle, dataDescriptorToValueMapping ) );
                            }

                            // defaultValue and/or format
                            if (
                                type !== 'BUTTON'
                                && type !== 'CALENDAR'
                                && type !== 'CHART'
                                && type !== 'COLOR'
                                && type !== 'ENUM'
                                && type !== 'FILE'
                                && type !== 'GRAPH'
                                && type !== 'MAP'
                                && type !== 'OVERVIEW'
                                && type !== 'REFERENCE'
                                && (
                                    type !== 'VARIABLE'
                                    || formFieldLayoutElementData.variableType !== 'COLOR'
                                )
                                && dynamicFormFieldProps.value !== undefined
                                && (
                                    type === 'STATIC'
                                    || isInherited
                                    || !!dataAndMetaModel?.isDefault
                                    || formFieldLayoutElementData.format
                                )
                            ) {
                                if ( formFieldLayoutElementData.format && dynamicFormFieldProps.value !== '' ) {
                                    const valueType     = type === 'VARIABLE' ? formFieldLayoutElementData.variableType : type,
                                          valueToFormat = isPropertyValueType( valueType )
                                              ? convertValueToString( dynamicFormFieldProps.value as AttributeValue, valueType )
                                              : String( dynamicFormFieldProps.value );

                                    dynamicFormFieldProps.displayValue = formatRenderer( valueToFormat, formFieldLayoutElementData.format );
                                }
                                else if (
                                    type === 'STATIC'
                                    || (
                                        (
                                            isInherited
                                            || dataAndMetaModel?.isDefault
                                        )
                                        && type !== 'BOOLEAN'
                                    )
                                ) {
                                    const dataValueType           = type === 'VARIABLE'
                                              ? formFieldLayoutElementData.variableType as PropertyValueType
                                              : dataUtils.getDataValueType( dataDescriptor, schema, dataStructure ) ?? 'STRING',
                                          renderValueParams : any = {
                                              language : user?.language,
                                              valueUnit: layoutElementData.valueUnit
                                          };

                                    dynamicFormFieldProps.displayValue = renderValue( dataValueType, dynamicFormFieldProps.value, renderValueParams );
                                }
                                else {
                                    dynamicFormFieldProps.displayValue = String( dynamicFormFieldProps.value );
                                }

                                // clear value if default
                                if ( isInherited || dataAndMetaModel?.isDefault ) {
                                    dynamicFormFieldProps.value = undefined;
                                }

                                // clear displayValue if not default value and equal to value
                                else if ( type !== 'STATIC' && !isInherited && !dataAndMetaModel?.isDefault && dynamicFormFieldProps.displayValue === String( dynamicFormFieldProps.value ) ) {
                                    dynamicFormFieldProps.displayValue = undefined;
                                }

                                // mark inherited value
                                if ( isInherited ) {
                                    dynamicFormFieldProps.displayClassNames.push( 'property-inherited' );
                                }
                                // mark default value
                                else if ( dataAndMetaModel?.isDefault ) {
                                    dynamicFormFieldProps.displayClassNames.push( 'property-default' );
                                }
                            }

                            // copy styles from input into display
                            dynamicFormFieldProps.displayClassNames = dynamicFormFieldProps.inputClassNames.concat( dynamicFormFieldProps.displayClassNames );
                            dynamicFormFieldProps.displayStyle = { ...dynamicFormFieldProps.inputStyle, ...dynamicFormFieldProps.displayStyle };

                            // errors
                            if ( setValueErrors ) {
                                dynamicFormFieldProps.errorMessage += setValueErrors.join( ' ' );
                            }
                            if ( errors ) {
                                if ( dynamicFormFieldProps.errorMessage ) {
                                    dynamicFormFieldProps.errorMessage += ' ';
                                }
                                dynamicFormFieldProps.errorMessage += errors.join( ' ' );
                            }
                            if ( !dynamicFormFieldProps.errorMessage ) {
                                delete dynamicFormFieldProps.errorMessage;
                            }

                            // autofocus first (erroneous if) field
                            if (
                                (
                                    ( rootItemId ?? 0 ) < 1
                                    || !elusiveData?.layoutElementMappings?.errors
                                    || errors
                                )
                                && (
                                    dynamicFormFieldProps.tabIndex
                                    && dynamicFormFieldProps.tabIndex > 0
                                    && (
                                        !this.autoFocusField
                                        || dynamicFormFieldProps.tabIndex < this.autoFocusField.tabIndex
                                        || ( errors && !this.autoFocusField.hasError )
                                    )
                                )
                            ) {
                                this.autoFocusField = {
                                    tabIndex         : dynamicFormFieldProps.tabIndex,
                                    layoutElementName: formFieldProps.name,
                                    hasError         : !!errors
                                };
                            }

                            retVal.isDirty = retVal.isDirty || ( !dataAndMetaModel?.isDefault && JSON.stringify( dynamicFormFieldProps.value ?? '' ) !== JSON.stringify( dynamicFormFieldProps.originalValue ?? '' ) );
                            retVal.isInvalid = retVal.isInvalid || !!dynamicFormFieldProps.errorMessage;

                            if ( layoutElementData.hasValueSet ) {
                                retVal.content.push(
                                    <ValueSetField
                                        {...formFieldProps}
                                        {...dynamicFormFieldProps}
                                        allowNone={!required}
                                        valueStyle={formFieldLayoutElementData.styles?.style}
                                        valuesForValueStyle={dataDescriptorToValueMapping}
                                        dataDescriptorAlias={dataDescriptorAlias}
                                    />
                                );
                            }
                            else {
                                const fieldType = type === 'VARIABLE' ? formFieldLayoutElementData.variableType : type;

                                switch ( fieldType ) {
                                    case 'BOOLEAN':
                                        retVal.content.push(
                                            <BooleanField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'BUTTON':
                                        retVal.content.push(
                                            <ButtonField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'CALENDAR':
                                        retVal.content.push(
                                            <CalendarView
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                ref={( node ) => {
                                                    this.views[ formFieldProps.key ] = node;
                                                }}
                                                formUid={uid}
                                                dataContext={dataContext}
                                                additionalAddItemFormProps={{
                                                    formParentItemId: rootItemId,
                                                    treeRootItemId,
                                                    treeParentItemId
                                                }}
                                                additionalElusiveDataContexts={additionalElusiveDataContexts}
                                                tabIndexOffset={tabIndexOffset + ( this.maxTabIndex * this.tabIndexStep )}
                                            />
                                        );
                                        break;

                                    case 'CHART':
                                        retVal.content.push(
                                            <ChartView
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                dataContext={dataContext}
                                            />
                                        );
                                        break;

                                    case 'COLOR':
                                        retVal.content.push(
                                            <ColorField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                isDefault={dataAndMetaModel?.isDefault}
                                            />
                                        );
                                        break;

                                    case 'COORDINATE':
                                        retVal.content.push(
                                            <CoordinateField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'CURRENCY':
                                        retVal.content.push(
                                            <CurrencyField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'DATE':
                                        retVal.content.push(
                                            <DateField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'DOUBLE':
                                        retVal.content.push(
                                            <DoubleField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'DURATION':
                                        retVal.content.push(
                                            <DurationField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'EMAIL':
                                        retVal.content.push(
                                            <EmailField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'GRAPH':
                                        retVal.content.push(
                                            <GraphView
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                dataContext={dataContext}
                                                formUid={uid}
                                            />
                                        );
                                        break;

                                    case 'IBAN':
                                        retVal.content.push(
                                            <IbanField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'IMAGEDATA':
                                        retVal.content.push(
                                            <ImageDataField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'INTEGER':
                                        retVal.content.push(
                                            <IntegerField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'JSON':
                                        retVal.content.push(
                                            <JsonField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'LINK':
                                        retVal.content.push(
                                            <LinkField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'MAP':
                                        retVal.content.push(
                                            <MapView
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                dataContext={dataContext}
                                                formUid={uid}
                                            />
                                        );
                                        break;

                                    case 'OVERVIEW':
                                        retVal.content.push(
                                            <OverviewView
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                ref={( node ) => {
                                                    this.views[ formFieldProps.key ] = node;
                                                }}
                                                dataContext={dataContext}
                                            />
                                        );
                                        break;

                                    case 'PASSWORD':
                                        retVal.content.push(
                                            <PasswordField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'PHONE':
                                        retVal.content.push(
                                            <PhoneField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'STATIC':
                                        retVal.content.push(
                                            <StaticContentField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                content={formFieldProps.content ?? ( typeof dynamicFormFieldProps.value === 'string' ? dynamicFormFieldProps.value : dynamicFormFieldProps.displayValue )}
                                                rawContent={formFieldProps.content ?? dynamicFormFieldProps.value ? String( dynamicFormFieldProps.value ) : undefined}
                                                onGetData={( getDataConfig : StaticContentFieldGetDataConfig ) => {
                                                    return this.onGetDataAction( layoutElementId, getDataConfig ) as ReturnType<Required<StaticContentFieldComponentProps>[ 'onGetData' ]>;
                                                }}
                                            />
                                        );
                                        break;

                                    case 'STRING':
                                        retVal.content.push(
                                            <StringField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'TEXT':
                                        retVal.content.push(
                                            <TextField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'TIME':
                                        retVal.content.push(
                                            <TimeField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'TIMESTAMP':
                                        retVal.content.push(
                                            <TimestampField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'ENUM':
                                        retVal.content.push(
                                            <EnumField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                            />
                                        );
                                        break;

                                    case 'REFERENCE':
                                        retVal.content.push(
                                            <ReferenceField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                rootItemId={parent.parentItemId ?? formFieldProps.rootItemId}
                                                isRepeated={!!parent.dataDescriptorAlias}
                                                formUid={uid}
                                                onGetData={( getDataConfig : ReferenceFieldGetDataConfig ) => {
                                                    return this.onGetDataAction( layoutElementId, getDataConfig ) as ReturnType<Required<ReferenceFieldComponentProps>[ 'onGetData' ]>;
                                                }}
                                                onCreateNewItemForReference={( item, event ) => this.onReferenceAction( layoutElementId, 'newReferencedItem', item, undefined, event )}
                                                additionalAddItemFormProps={{
                                                    formParentItemId: rootItemId,
                                                    treeRootItemId,
                                                    treeParentItemId
                                                }}
                                                tabIndexOffset={tabIndexOffset + ( this.maxTabIndex * this.tabIndexStep )}
                                            />
                                        );
                                        break;

                                    case 'FILE':
                                        retVal.content.push(
                                            <FileField
                                                {...formFieldProps}
                                                {...dynamicFormFieldProps}
                                                rootItemId={parent.parentItemId ?? formFieldProps.rootItemId}
                                            />
                                        );
                                        break;

                                    default:
                                        console.error( `FieldType '${fieldType}' not implemented: %o`, layoutElementData );
                                        throw ( new Error( `'${formFieldLayoutElementData.key}' (${formFieldLayoutElementData.name}): type '${layoutElementStructure.type}' not implemented yet` ) );
                                }
                            }
                        }
                        catch ( error ) {
                            let errorMessage : string = 'unknown error';

                            console.error( error );

                            if ( typeof error === 'string' ) {
                                errorMessage = error;
                            }
                            else if ( error instanceof Error ) {
                                errorMessage = error.message;
                            }

                            // strip field
                            errorMessage = errorMessage.substring( errorMessage.indexOf( '): ' ) + 3 );

                            retVal.content.push(
                                <StaticContentField
                                    {...formFieldProps}
                                    alignment="left"
                                    content={errorMessage}
                                    style={elementStyle}
                                    inputClassNames={[ 'yn-dummy-field', 'yn-error' ]}
                                />
                            );
                        }
                    }
                }
            }
        }

        if ( parent.type !== 'tabContainer' ) {
            if ( !repeatingContainer ) {
                retVal.content = [
                    <div key={`container_${parent.id}${parent.idSuffix}`} className="yn-form-container-content">
                        {retVal.content}
                    </div>
                ];
            }
            else {
                retVal.content = [
                    <div key={`container_${parent.id}${parent.idSuffix}`}
                         className="yn-form-container-content yn-form-container-repeated-content">
                        {retVal.content}
                    </div>
                ];
            }
        }

        return retVal;
    }

    /**
     * Sorter function for layoutStructures.
     *
     * @param {ContainerLayoutStructure | FormFieldLayoutStructure} layoutStructure1
     * @param {ContainerLayoutStructure | FormFieldLayoutStructure} layoutStructure2
     *
     * @return {-1 | 0 | 1}
     *
     * @private
     */
    orderLayoutStructure( layoutStructure1 : ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding, layoutStructure2 : ContainerLayoutStructure | TabContainerLayoutStructure | FormFieldLayoutStructureAndDataBinding ) : -1 | 0 | 1 {
        const layoutElementsData    = this.props.elusiveData?.layoutElementMappings?.cache ?? {},
              layoutStructure1Order = ( layoutElementsData[ layoutStructure1.layoutElementId ] as ( ContainerProperties | FormFieldProperties ) )?.order,
              layoutStructure2Order = ( layoutElementsData[ layoutStructure2.layoutElementId ] as ( ContainerProperties | FormFieldProperties ) )?.order;

        if ( layoutStructure1Order > layoutStructure2Order ) {
            return 1;
        }
        else if ( layoutStructure1Order < layoutStructure2Order ) {
            return -1;
        }
        else {
            return 0;
        }
    }

    /**
     * Generates a CSS style object for form layout based on the provided element properties and grid size.
     *
     * @param {Object} elementProperties - The properties defining the layout constraints for the element.
     * @param {number} [elementProperties.minWidth] - The optional minimum width of the element.
     * @param {WidthUnit} elementProperties.minWidthUnit - The unit for the minimum width (e.g., pixels, percentage, or grid units).
     * @param {number} [elementProperties.maxWidth] - The optional maximum width of the element.
     * @param {WidthUnit} elementProperties.maxWidthUnit - The unit for the maximum width (e.g., pixels, percentage, or grid units).
     * @param {number} [elementProperties.width] - The optional fixed width of the element.
     * @param {WidthUnit} elementProperties.widthUnit - The unit for the fixed width (e.g., pixels, percentage, or grid units).
     * @param {number} gridSize - The size of the grid for determining grid unit-based dimensions.
     *
     * @return {React.CSSProperties} The computed CSS style object for the element's layout.
     */
    getFormLayoutStyle( elementProperties : {
        minWidth? : number;
        minWidthUnit : WidthUnit;
        maxWidth? : number;
        maxWidthUnit : WidthUnit;
        width? : number;
        widthUnit : WidthUnit;
    }, gridSize : number ) : React.CSSProperties {
        const retVal : React.CSSProperties = {},
              {
                  minWidth,
                  minWidthUnit,
                  maxWidth,
                  maxWidthUnit,
                  width,
                  widthUnit
              }                            = elementProperties;

        if ( width ) {
            if ( width < 0 ) {
                retVal.flex = `${-1 * width} ${-1 * width} auto`;
                retVal.width = '100px';
            }
            else {
                retVal.flex = 'none';
                if ( widthUnit === 'gridunit' ) {
                    retVal.width = `${100 * Math.min( 1, width / gridSize )}%`;
                }
                else {
                    retVal.width = `${width}${widthUnitMapping[ widthUnit ]}`;
                }
                retVal.minWidth = retVal.width;
                retVal.maxWidth = retVal.width;
            }
        }
        else {
            retVal.flex = 'none';
            retVal.width = '100%';
            retVal.minWidth = retVal.width;
            retVal.maxWidth = retVal.width;
        }

        if ( minWidth ) {
            if ( minWidthUnit === 'gridunit' ) {
                retVal.minWidth = `${100 * Math.min( 1, minWidth / gridSize )}%`;
            }
            else {
                retVal.minWidth = `${minWidth}${widthUnitMapping[ minWidthUnit ]}`;
            }
        }

        if ( maxWidth ) {
            if ( maxWidthUnit === 'gridunit' ) {
                retVal.maxWidth = `${100 * Math.min( 1, maxWidth / gridSize )}%`;
            }
            else {
                retVal.maxWidth = `${maxWidth}${widthUnitMapping[ maxWidthUnit ]}`;
            }
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const content = this.getForm();

        if ( this.debug ) {
            console.log( `%cForm - %crender%c - ${Math.round( 1000 * ( performance.now() - this.renderStartTime ) ) / 1000} ms%c`.asLogLine( 108 ), `font-weight: bold;`, `color: #${debugColorScheme.render}; font-weight: bold;`, 'color: unset; font-weight: bold;', 'font-weight: unset;' );
            this.renderStartTime = 0;
        }

        return content;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        xhrRequestsForComponent: undefined,
        elusiveData            : undefined,
        intercomMessages       : undefined,
        user                   : undefined,
        messageCatalogTimestamp: 0,
        metaModelTimestamp     : 0,
        debug                  : false
    };

    // get the running xhr request belonging to this component (uid)
    if ( state.xhr?.[ ownProps.uid ] ) {
        retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
    }

    // get elusiveData
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = state.elusiveData[ ownProps.uid ];
    }
    // merge data versions from dataContext state if different from uid
    if ( ownProps.dataContext !== ownProps.uid && state.elusiveData?.[ ownProps.dataContext ]?.data ) {
        const stateElusiveData = state.elusiveData?.[ ownProps.dataContext ]?.data;

        if ( retVal.elusiveData?.data?.items && stateElusiveData?.items ) {
            for ( const key of Object.keys( retVal.elusiveData.data.items ) ) {
                retVal.elusiveData.data.items[ key ] = stateElusiveData.items[ key ] ?? retVal.elusiveData.data.items[ key ];
            }
        }
        if ( retVal.elusiveData?.data?.references && stateElusiveData?.references ) {
            for ( const key of Object.keys( retVal.elusiveData.data.references ) ) {
                retVal.elusiveData.data.references[ key ] = stateElusiveData.references[ key ] ?? retVal.elusiveData.data.references[ key ];
            }
        }
    }

    // get intercom messages
    if ( state.intercom?.[ ownProps.uid ] ) {
        retVal.intercomMessages = state.intercom[ ownProps.uid ];
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    if ( state.messagecatalog?.timestamp ) {
        retVal.messageCatalogTimestamp = state.messagecatalog.timestamp;
    }

    if ( state.metamodel?.timestamp ) {
        retVal.metaModelTimestamp = state.metamodel.timestamp;
    }

    if ( !!state.debugConfig?.formDebug?.showAll || state.debugConfig?.formDebug?.dedicated?.[ ownProps.uid ] ) {
        retVal.debug = true;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : AppDispatch ) {
    return bindActionCreators( Actions, dispatch );
}

// apply mixin to Form component
Object.assign( Form.prototype, ActionMixin );

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true } )( Form );
